// Helper murni untuk NOTA TAGIHAN (invoice) — mig 069 + redesign 071/072.
// Nota adalah SATU-SATUNYA pintu pembayaran: pending_acc (buatan pembeli,
// menunggu ACC admin) → open → partial → paid; terminal void/rejected.
// Dibayar via saldo (pay_invoice_with_balance) dan/atau cicilan transfer
// (invoice_payments). Alokasi pembayaran → per-pcs item dilakukan server-side
// (apply_invoice_allocation); `allocateInvoice` di bawah adalah CERMIN TS
// dari compute_invoice_allocation untuk kebutuhan tampilan.

import { buildDpShareLines, type DpBreakdown } from "@/lib/dp";
import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
import {
  pickCheapestPromo,
  promoDiscountForSize,
  promoLevelPolicy,
  promosForLevel,
  type ProductPromo
} from "@/lib/promo";
import { cutsFromTrace, type DiscountTraceRow } from "@/lib/cartItemPrice";
// Peta slot → kolom diskon: SATU sumber untuk seluruh jalur uang (mig 5600).
import { DISKON_SLOT_KEY, isDiskonSlot } from "@/lib/reseller-groups";
import { isUsableBrandPersen } from "@/lib/groupBrandDiscount";
// Urutan FIFO nota (created_at, invoice_no) dipakai bersama halaman bayar &
// pratinjau alokasi batch di bawah. Diimpor, BUKAN disalin, supaya kunci urut
// tak pernah menyimpang dari yang dipakai server. Aman dari siklus impor:
// payPage.ts hanya mengambil TYPE dari modul ini (terhapus saat kompilasi).
import { sortInvoicesFifo } from "@/lib/payPage";
import {
  resolveShipmentWeight,
  type ResolvedShipmentWeight,
  type WeightSourceItem
} from "@/lib/shipmentWeight";

export type InvoiceStatus =
  | "pending_acc"
  | "open"
  | "partial"
  | "paid"
  | "void"
  | "rejected";

export type InvoiceLineKind =
  | "product_item"
  | "shipment_due"
  | "manual"
  | "dp_credit"
  | "discount";

export type InvoicePaymentStatus = "pending" | "confirmed" | "rejected";

export type InvoiceKind = "product" | "dp" | "ongkir";

export const INVOICE_STATUS_META: Record<
  InvoiceStatus,
  { label: string; color: string }
> = {
  pending_acc: { label: "Menunggu ACC", color: "bg-violet-100 text-violet-700" },
  open: { label: "Belum dibayar", color: "bg-marigold/15 text-marigold" },
  partial: { label: "Dibayar sebagian", color: "bg-sky-100 text-sky-700" },
  paid: { label: "Lunas", color: "bg-leaf/15 text-leaf" },
  void: { label: "Dibatalkan", color: "bg-zinc-100 text-zinc-500" },
  rejected: { label: "Ditolak", color: "bg-red-100 text-red-600" }
};

/**
 * Posisi pembayaran sebuah nota di DOKUMEN (struk thermal, PDF, surat jalan):
 *   `"due"`    → masih ada sisa (`outstanding > 0`);
 *   `"paid"`   → status `paid` — SATU-SATUNYA keadaan yang boleh tercetak LUNAS;
 *   `"unpaid"` → sisa 0 tapi statusnya BUKAN `paid`.
 *
 * Yang ketiga nyata: nota yang seluruh barisnya "harga menyusul", atau yang
 * totalnya jadi 0 karena baris potongan. Server sengaja membiarkannya `open`
 * (`apply_invoice_allocation` mig 4820 baru melunaskan bila bruto > 0) sampai
 * harga ditetapkan atau admin menekan Tandai Lunas. Dulu ketiga jenis dokumen
 * memutuskan dari `outstanding` saja, jadi nota "Belum dibayar" tercetak blok
 * besar "L U N A S" — Rekap Per Pelanggan ikut mencetaknya (cari-bug
 * 2026-10-15; 1 nota seperti ini di Galea).
 */
export type InvoiceSettlement = "due" | "paid" | "unpaid";

export function invoiceSettlement(input: {
  status: string;
  outstanding: number;
}): InvoiceSettlement {
  if (input.outstanding > 0) return "due";
  return input.status === "paid" ? "paid" : "unpaid";
}

/** Tagihan barang yang membuat nota DP hangus (mig 6590), siap tampil. */
export type InvoiceHangusRef = {
  invoiceId: string;
  /** "INV2609280014" / "#12". */
  displayNo: string;
};

/**
 * Tagihan gabungan yang menyerap tagihan ini (mig 6870), siap tampil — plus
 * NASIB-nya sekarang: tagihan gabungan sendiri bisa lunas, dibatalkan, atau
 * digabung LAGI (bughunt 2026-10-02). Absen/null = payload tanpa kolomnya
 * (mis. nota publik) → kalimat jatuh ke bentuk umum.
 */
export type InvoiceMergedRef = InvoiceHangusRef & {
  status?: InvoiceStatus | null;
  /** Tagihan gabungan itu sendiri sudah digabung lagi ke tagihan lain. */
  mergedAgain?: boolean;
};

/**
 * Tagihan ARSIP — dibatalkan / digabung (void) atau pengajuan ditolak. Ia tak
 * menagih apa pun: `outstanding` mentahnya (total − uang efektif) BUKAN utang,
 * jadi layar & dokumen tak boleh menyebutnya "Sisa tagihan" (bughunt
 * 2026-10-02: sumber gabungan yang uangnya sudah dipindah tampil "Sisa Rp
 * <total>" dan pembeli bisa mentransfernya lagi).
 */
export function invoiceIsArchived(status: InvoiceStatus): boolean {
  return status === "void" || status === "rejected";
}

/**
 * Chip status nota untuk daftar & detail tagihan. SATU sumber untuk layar
 * pembeli, layar admin, dan PDF: nota DP yang di-void KARENA tagihan barang
 * terbit sebelum DP dibayar (`invoices.hangus_by_invoice_id`, mig 6590) bukan
 * "Dibatalkan" — permintaan pemilik 2026-09-29, supaya admin tidak mengira
 * DP-nya dibatalkan manual atau pembelinya kabur. Sejak revisi tagihan DP
 * (2026-10-08, keputusan #5) labelnya "Ditagih di Tagihan Barang", bukan lagi
 * "Tagihan DP Hangus": nominal DP-nya ikut ditagih di tagihan barang itu dan
 * dibayar lewat opsi Bayar DP di sana. Void karena sebab lain (pesanan batal,
 * PO dibuka ulang, manual) tetap "Dibatalkan".
 */
export function invoiceStatusChip(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  hangusBy?: InvoiceHangusRef | null;
  mergedInto?: InvoiceMergedRef | null;
  dpExpiredAt?: string | null;
}): { label: string; color: string } {
  // mig 7130: nota DP yang KEDALUWARSA karena barangnya sudah ready semua —
  // bukan "Dibatalkan" (tak ada yang membatalkannya) dan bukan "Ditagih di
  // Tagihan Barang" (nominalnya tidak dipindah ke mana pun).
  if (isDpExpired(invoice)) return DP_EXPIRED_CHIP;
  if (invoice.kind === "dp" && invoice.status === "void" && invoice.hangusBy) {
    return { label: HANGUS_CHIP_LABEL, color: "bg-zinc-100 text-zinc-600" };
  }
  // Gabung Tagihan (mig 6870): sumber gabungan bukan "Dibatalkan" — isinya
  // pindah utuh ke tagihan lain, dan pembeli harus tahu harus membayar yang mana.
  if (invoice.status === "void" && invoice.mergedInto) {
    return { label: MERGED_CHIP_LABEL, color: "bg-sky-100 text-sky-700" };
  }
  return INVOICE_STATUS_META[invoice.status];
}

/**
 * Label chip/badge nota DP yang nominalnya ikut ditagih di tagihan barang (dulu
 * "Tagihan DP Hangus", mig 6590) — SATU sumber (chip tagihan, kartu DP, badge
 * keranjang, PDF). Kata "hangus" dibuang (keputusan pemilik #5, 2026-10-08):
 * DP-nya tidak hilang, ia dibayar lewat tagihan barang.
 */
export const HANGUS_CHIP_LABEL = "Ditagih di Tagihan Barang";

/** Label chip tagihan yang digabung ke tagihan lain (mig 6870). */
export const MERGED_CHIP_LABEL = "Digabung";

/**
 * Label chip nota DP yang KEDALUWARSA (mig 7130, putusan pemilik 2026-10-12):
 * semua barang pesanannya sudah ready sebelum DP dibayar, jadi DP tak lagi
 * berlaku — harga barangnya ditagih penuh lewat tagihan barang (pelunasan).
 * SATU sumber (chip tagihan, kartu DP, jejak DP, PDF, keranjang admin).
 */
export const DP_EXPIRED_CHIP_LABEL = "Kedaluwarsa";

/** Chip nota DP kedaluwarsa — abu netral: tak menagih, tak dibatalkan siapa pun. */
export const DP_EXPIRED_CHIP = { label: DP_EXPIRED_CHIP_LABEL, color: "bg-zinc-100 text-zinc-600" };

/** Keterangan nota DP kedaluwarsa (daftar, detail, jejak DP). */
export const DP_EXPIRED_NOTE =
  "Barang pesanannya sudah ready sebelum DP dibayar — DP tidak perlu dibayar lagi; lanjutkan pelunasan lewat tagihan barang";

/**
 * Nota DP kedaluwarsa = `kind='dp'`, `void`, `dp_expired_at` terisi (server
 * menegakkan bentuk itu lewat CHECK `invoices_dp_expired_shape`, mig 7130).
 */
export function isDpExpired(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  dpExpiredAt?: string | null;
}): boolean {
  return invoice.kind === "dp" && invoice.status === "void" && Boolean(invoice.dpExpiredAt);
}

/** Baris keterangan di bawah chip {@link DP_EXPIRED_CHIP_LABEL}; null untuk nota lain. */
export function invoiceDpExpiredNote(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  dpExpiredAt?: string | null;
}): string | null {
  return isDpExpired(invoice) ? DP_EXPIRED_NOTE : null;
}

/**
 * Pengganti pesan generik server ("Tagihan #… sudah lunas/batal") bila
 * pembayaran ditolak karena notanya nota DP yang barusan KEDALUWARSA —
 * halaman bayar dibuka sebelum barangnya ditandai ready (mig 7130). Pembeli
 * yang terlanjur transfer perlu tahu ke mana buktinya. `selectedCount` =
 * jumlah nota yang dikirim bersama: server menolak SELURUH kiriman, jadi
 * nota lain di dalamnya juga belum tercatat.
 */
export function dpExpiredPayRejectionMessage(
  expired: ReadonlyArray<{ invoiceNo: number | null; invoiceCode?: string | null }>,
  selectedCount: number = expired.length
): string {
  const nos = expired.map((i) => formatInvoiceNo(i.invoiceNo, i.invoiceCode)).join(", ");
  const others =
    selectedCount > expired.length
      ? " Tagihan lain yang kamu pilih belum tercatat — kirim ulang tanpa tagihan DP itu."
      : "";
  return (
    `Tagihan DP ${nos} sudah kedaluwarsa — barangnya sudah ready sebelum DP dibayar, ` +
    "jadi DP tidak perlu dibayar lagi. Lanjutkan pelunasan dari Keranjang; kalau sudah " +
    "terlanjur transfer, unggah buktinya di tagihan barangnya." +
    others
  );
}

/**
 * Ekor kalimat "ke mana sekarang" untuk tagihan yang digabung — mengikuti
 * nasib tagihan gabungannya. "Bayar di tagihan itu" hanya benar selama tagihan
 * gabungan masih menagih; yang sudah lunas, dibatalkan, atau digabung lagi
 * butuh kalimat lain (bughunt 2026-10-02).
 */
export function invoiceMergedTargetHint(ref: InvoiceMergedRef): string {
  if (ref.mergedAgain) return "tagihan itu sudah digabung lagi — buka untuk tagihan terbarunya";
  if (ref.status === "paid") return "tagihan itu sudah lunas";
  if (ref.status === "void" || ref.status === "rejected") {
    return "tagihan itu kemudian dibatalkan — tanyakan admin";
  }
  return "bayar di tagihan itu";
}

/**
 * Pemberitahuan pengganti "Sisa tagihan" untuk tagihan ARSIP di layar yang
 * dibuka tanpa konteks (nota publik `/lihat-tagihan/<token>`): tagihan yang sudah
 * digabung menyebut tagihan barunya, yang dibatalkan/ditolak menyebut bahwa
 * ia tak perlu dibayar. null = tagihan masih menagih — tampilkan sisanya.
 */
export function invoiceArchiveNotice(invoice: {
  status: InvoiceStatus;
  mergedInto?: InvoiceMergedRef | null;
  kind?: InvoiceKind;
  dpExpiredAt?: string | null;
}): string | null {
  // mig 7130: Tagihan DP kedaluwarsa (barang sudah ready) — kalimatnya bukan
  // "dibatalkan": tak ada yang membatalkan, DP-nya tak berlaku lagi.
  if (invoice.kind && isDpExpired({ kind: invoice.kind, status: invoice.status, dpExpiredAt: invoice.dpExpiredAt })) {
    return "Tagihan DP ini KEDALUWARSA — barang pesanannya sudah ready, jadi DP tidak perlu dibayar. Harga barangnya ditagih lewat tagihan barang.";
  }
  if (invoice.status === "void" && invoice.mergedInto) {
    return `Tagihan ini sudah DIGABUNG ke tagihan ${invoice.mergedInto.displayNo} — jangan dibayar di sini; ${invoiceMergedTargetHint(invoice.mergedInto)}.`;
  }
  if (invoice.status === "void") return "Tagihan ini DIBATALKAN — tidak perlu dibayar.";
  if (invoice.status === "rejected") return "Pengajuan tagihan ini DITOLAK — tidak perlu dibayar.";
  return null;
}

/** Baris keterangan di bawah chip "Digabung"; null untuk tagihan lain. */
export function invoiceMergedNote(invoice: {
  status: InvoiceStatus;
  mergedInto?: InvoiceMergedRef | null;
}): string | null {
  if (invoice.status !== "void" || !invoice.mergedInto) return null;
  return `Digabung ke tagihan ${invoice.mergedInto.displayNo} — ${invoiceMergedTargetHint(invoice.mergedInto)}`;
}

/**
 * Baris keterangan nota DP yang ikut ditagih di tagihan barang (chip
 * {@link HANGUS_CHIP_LABEL}); null untuk nota lain.
 */
export function invoiceHangusNote(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  hangusBy?: InvoiceHangusRef | null;
}): string | null {
  if (invoiceStatusChip(invoice).label !== HANGUS_CHIP_LABEL || !invoice.hangusBy) return null;
  return `Tagihan DP ini belum dibayar saat Tagihan Barang ${invoice.hangusBy.displayNo} terbit — nominal DP-nya ikut ditagih di sana`;
}

/**
 * Siapa yang membatalkan nota (`invoices.voided_via`, mig 8650/8670, bughunt
 * E10-4) — baris kecil di kartu Detail Tagihan admin. null bila nota tidak batal
 * atau pelakunya tak tercatat (batal sebelum mig 8670, gabung tagihan, nota DP
 * hangus/kedaluwarsa — yang terakhir itu sudah punya kalimatnya sendiri).
 */
export function invoiceVoidedViaNote(invoice: {
  status: InvoiceStatus;
  voidedVia?: "buyer" | "admin" | "system" | null;
}): string | null {
  if (invoice.status !== "void") return null;
  switch (invoice.voidedVia) {
    case "buyer":
      return "Dibatalkan sendiri oleh pembeli lewat tombol Batalkan Tagihan.";
    case "admin":
      return "Dibatalkan admin lewat tombol Batalkan tagihan.";
    case "system":
      return "Dibatalkan otomatis karena lewat tempo bayar.";
    default:
      return null;
  }
}

/** Keadaan tagihan yang dikirim ke pembeli bersama dokumennya. */
export type InvoiceShareStatus = {
  /** Baris rekap pesan WhatsApp (tanpa bullet). */
  line: string;
  /** Kotak ringkas di lembar kirim: label kiri + nilai kanan. */
  boxLabel: "Sisa tagihan" | "Status";
  boxValue: string;
  /**
   * owed = masih menagih · paid = lunas · pending = pengajuan menunggu ACC
   * (nominal belum final) · archived = tak menagih apa pun.
   */
  tone: "owed" | "paid" | "pending" | "archived";
};

/**
 * Bahan nota DP void yang SAMA dengan banner PDF terlampir (`InvoicePdfExtras`
 * dari jejak DP) — tanpa ini pesan & dokumen bisa saling menyangkal.
 */
export type InvoiceShareDpRefs = {
  /** DP "yatim": tagihan barang yang dulu menagihnya (`dpHangusOrphanNo`). */
  orphanNo?: string | null;
  /** Tagihan barang yang KINI menagihnya (`dpHangusBilledNo`). */
  billedNo?: string | null;
  /** Status tagihan itu (`dpHangusBilledStatus`). */
  billedStatus?: InvoiceStatus | null;
};

/**
 * Status rekap "Kirim ke WhatsApp" kartu Dokumen tagihan admin. Tagihan ARSIP
 * (`invoiceIsArchived`: kedaluwarsa, ikut ditagih di tagihan barang, digabung,
 * dibatalkan, ditolak) tak menagih apa pun — `outstanding` mentahnya bukan
 * utang, jadi barisnya kalimat status yang sama dengan nota publik, bukan
 * "Sisa yang harus dibayar" (bughunt 2026-10-12: pesan menulis sisa Rp 100.000
 * sementara PDF terlampir bertuliskan TAGIHAN DP KEDALUWARSA).
 *
 * Bughunt 2026-10-13, tiga sisa pertentangan dengan PDF terlampir:
 *   • pengajuan `pending_acc` ditulis "Sisa yang harus dibayar" padahal PDF-nya
 *     "Nominal belum final, jangan dijadikan dasar pembayaran";
 *   • nota DP "yatim" disuruh "bayar lewat tagihan barang itu" padahal PDF &
 *     layar menyebutnya tidak ditagih (`dpRefs.orphanNo`);
 *   • DP yang ikut ditagih menyebut sumber gabungan yang sudah diarsipkan
 *     (`dpRefs.billedNo`) dan tetap menyuruh membayar walau tagihan barangnya
 *     sudah lunas (`dpRefs.billedStatus`).
 */
export function invoiceShareStatus(
  invoice: {
    kind: InvoiceKind;
    status: InvoiceStatus;
    outstanding: number;
    hangusBy?: InvoiceHangusRef | null;
    mergedInto?: InvoiceMergedRef | null;
    dpExpiredAt?: string | null;
  },
  dpRefs?: InvoiceShareDpRefs | null
): InvoiceShareStatus {
  if (invoice.status === "pending_acc") {
    return {
      line: "Status: MENUNGGU ACC admin — nominal belum final, jangan ditransfer dulu.",
      boxLabel: "Status",
      boxValue: INVOICE_STATUS_META.pending_acc.label,
      tone: "pending"
    };
  }
  if (invoiceIsArchived(invoice.status)) {
    if (invoice.kind === "dp" && invoice.status === "void" && !isDpExpired(invoice)) {
      if (dpRefs?.orphanNo) {
        return {
          line: `Tagihan DP ini TIDAK DITAGIH — Tagihan Barang ${dpRefs.orphanNo} yang dulu ikut menagihnya sudah dibatalkan / tak lagi menagih pesanannya. Jangan dibayar lewat tagihan itu.`,
          boxLabel: "Status",
          boxValue: INVOICE_STATUS_META.void.label,
          tone: "archived"
        };
      }
      if (invoice.hangusBy) {
        const goodsNo = dpRefs?.billedNo || invoice.hangusBy.displayNo;
        if (dpRefs?.billedStatus === "paid") {
          return {
            line: `Nominal DP ini ikut ditagih di Tagihan Barang ${goodsNo} dan sudah lunas bersama tagihan itu — tidak ada yang perlu dibayar.`,
            boxLabel: "Status",
            boxValue: INVOICE_STATUS_META.paid.label,
            tone: "paid"
          };
        }
        return {
          line: `Tagihan DP ini belum dibayar saat tagihan barangnya terbit — nominal DP-nya ikut ditagih di Tagihan Barang ${goodsNo}; bayar lewat tagihan barang itu, bukan di sini.`,
          boxLabel: "Status",
          boxValue: HANGUS_CHIP_LABEL,
          tone: "archived"
        };
      }
    }
    return {
      line: invoiceArchiveNotice(invoice) ?? "Tagihan ini tidak perlu dibayar.",
      boxLabel: "Status",
      boxValue: invoiceStatusChip(invoice).label,
      tone: "archived"
    };
  }
  if (invoice.outstanding > 0) {
    return {
      line: `Sisa yang harus dibayar: ${formatLineAmount(invoice.outstanding)}`,
      boxLabel: "Sisa tagihan",
      boxValue: formatLineAmount(invoice.outstanding),
      tone: "owed"
    };
  }
  return { line: "Status: LUNAS ✅", boxLabel: "Status", boxValue: "Lunas", tone: "paid" };
}

export const INVOICE_KIND_META: Record<InvoiceKind, { label: string; color: string }> = {
  product: { label: "Produk", color: "bg-zinc-100 text-zinc-600" },
  dp: { label: "DP", color: "bg-amber-100 text-amber-700" },
  ongkir: { label: "Ongkir", color: "bg-sky-100 text-sky-700" }
};

// ─── Facet halaman /tagihan (sub-kolom + chip jenis) ─────────────────────────

/** Sub-kolom halaman /tagihan. */
export type InvoiceSubTab = "belum_lunas" | "sudah_lunas" | "ditolak";

/**
 * Klasifikasi status nota → sub-kolom /tagihan: void/rejected = "Ditolak",
 * paid = "Sudah Lunas", sisanya (menunggu ACC / belum dibayar / sebagian) =
 * "Belum Lunas".
 */
export function invoiceStatusSubTab(status: InvoiceStatus): InvoiceSubTab {
  if (status === "rejected" || status === "void") return "ditolak";
  return status === "paid" ? "sudah_lunas" : "belum_lunas";
}

/** Status DB per sub-kolom — sumbernya modul kecil (dipakai juga bottom nav). */
export { INVOICE_SUBTAB_STATUSES } from "@/lib/invoiceSubTabStatuses";

/**
 * Sub-kolom /tagihan yang dipilah per BULAN (`MonthFilter`: Bulan Ini · Bulan
 * Lalu · Kustom, bulan kalender WIB). Sejak 2026-09-30 "Semua" & rentang
 * harian dibuang — permintaan pemilik.
 *
 * Belum Lunas SENGAJA tidak (keputusan pemilik, hari yang sama): ia daftar
 * UTANG, bukan riwayat. Saat diputuskan, 163 dari 397 nota belum lunas Galea
 * (41%) terbit sebelum bulan berjalan, dan 19 pembeli tunggakannya seluruhnya
 * di bulan lampau. Dipilah per bulan, mereka membaca "Tidak ada tagihan yang
 * belum lunas" padahal berutang, badge Tagihan di bottom nav (menghitung semua
 * bulan) tak cocok dengan layar, "Bayar Semua" berhenti FIFO lintas bulan, dan
 * tiap tanggal 1 daftar itu kosong untuk SEMUA pembeli.
 */
export function invoiceSubTabByMonth(subTab: InvoiceSubTab): boolean {
  return subTab !== "belum_lunas";
}

/**
 * Urutan TAMPIL daftar /tagihan per sub-kolom.
 *
 * Belum Lunas: TERLAMA dulu — urutan FIFO yang sama dengan alokasi server
 * (`pay_invoices_with_balance`) dan dengan "Pilih Semua", yang memilih
 * `MAX_INVOICES_PER_PAYMENT` nota terlama. Audit 2026-09-02: daftar ini dulu
 * terbaru-dulu, sehingga saat nota siap-bayar > 20, ke-20 nota yang dipilih
 * "Pilih Semua" justru berada di DASAR daftar — di luar 20 kartu pertama yang
 * dirender paginasi tampilan — dan kartu yang terlihat semuanya tak tercentang
 * sementara tombol berbunyi "Bayar Sekaligus (20)". Terbaca seperti "tandai
 * semua tidak ikut". Dengan terlama-dulu, 20 nota terpilih = 20 kartu teratas.
 *
 * Sudah Lunas & Ditolak tetap terbaru dulu (riwayat). Tidak memutasi input.
 */
export function sortInvoicesForSubTab<
  T extends { createdAt: string; invoiceNo: number }
>(list: ReadonlyArray<T>, subTab: InvoiceSubTab): T[] {
  const fifo = (a: T, b: T) =>
    a.createdAt.localeCompare(b.createdAt) || a.invoiceNo - b.invoiceNo;
  return [...list].sort(
    subTab === "belum_lunas" ? fifo : (a, b) => fifo(b, a)
  );
}

export type InvoiceFacetRow = { status: InvoiceStatus; kind: InvoiceKind };

export type InvoiceFacets = {
  /** Jumlah seluruh nota (semua status). */
  total: number;
  /** Jumlah nota per sub-kolom. */
  counts: Record<InvoiceSubTab, number>;
  /**
   * Jenis nota yang dimiliki pembeli, urut Produk → DP → Ongkir. Sejak
   * 2026-09-30 chip jenis /tagihan TIDAK lagi bergantung padanya (selalu
   * empat, `INVOICE_KIND_FILTERS`); tetap dipulangkan RPC
   * `customer_invoice_facets` apa adanya.
   */
  kinds: InvoiceKind[];
};

/**
 * Rangkum baris facet ringan (status + kind seluruh nota) jadi badge jumlah
 * per sub-kolom + daftar jenis untuk chip filter — pengganti perhitungan dari
 * daftar nota penuh setelah /tagihan dimuat per sub-kolom (audit 2026-07).
 */
export function buildInvoiceFacets(rows: InvoiceFacetRow[]): InvoiceFacets {
  const counts: Record<InvoiceSubTab, number> = {
    belum_lunas: 0,
    sudah_lunas: 0,
    ditolak: 0
  };
  const kindSet = new Set<InvoiceKind>();
  for (const row of rows) {
    counts[invoiceStatusSubTab(row.status)] += 1;
    kindSet.add(row.kind);
  }
  const kindOrder: InvoiceKind[] = ["product", "dp", "ongkir"];
  return {
    total: rows.length,
    counts,
    kinds: kindOrder.filter((k) => kindSet.has(k))
  };
}

export const INVOICE_LINE_KIND_LABEL: Record<InvoiceLineKind, string> = {
  product_item: "Barang",
  shipment_due: "Pengiriman",
  manual: "Lainnya",
  dp_credit: "Kredit DP",
  discount: "Diskon"
};

/**
 * Baris bernominal NEGATIF yang mengurangi total tagihan — kredit DP (sistem)
 * & diskon manual/promo (input admin, mig 137). Ditampilkan hijau dengan tanda
 * minus dan TIDAK ikut menyerap uang pada alokasi pembayaran.
 */
export function isCreditLine(kind: InvoiceLineKind): boolean {
  return kind === "dp_credit" || kind === "discount";
}

/** Format nominal baris tagihan — baris negatif (kredit/diskon) jadi "−Rp …". */
export function formatLineAmount(amount: number): string {
  const abs = Math.abs(amount).toLocaleString("id-ID");
  return amount < 0 ? `−Rp ${abs}` : `Rp ${abs}`;
}

/**
 * Total tagihan = jumlah baris AKTIF (baris cancelled dikecualikan; baris
 * dp_credit bernilai negatif ikut mengurangi), di-clamp ≥ 0 — cermin SQL
 * invoice_total v2 (mig 072).
 */
export function computeInvoiceTotal(
  lines: ReadonlyArray<{ amount: number; cancelledAt?: string | null }>
): number {
  const sum = lines.reduce(
    (acc, l) =>
      l.cancelledAt ? acc : acc + (Number.isFinite(l.amount) ? l.amount : 0),
    0
  );
  return Math.max(0, sum);
}

/**
 * Uang riil yang menempel di nota = saldo terpakai + setoran terkonfirmasi −
 * refund (gagal produksi/cancel). balance_applied & amount_paid tidak pernah
 * ditulis-mundur; refund dicatat terpisah di refunded_amount.
 */
export function computeSettledEffective(
  balanceApplied: number,
  amountPaid: number,
  refundedAmount = 0
): number {
  return Math.max(0, balanceApplied + amountPaid - refundedAmount);
}

/**
 * Sisa tagihan = total − settled efektif. Overpayment dicatat apa adanya di
 * DB; tampilan di-clamp ke 0.
 */
export function computeInvoiceOutstanding(
  total: number,
  balanceApplied: number,
  amountPaid: number,
  refundedAmount = 0
): number {
  return Math.max(
    0,
    total - computeSettledEffective(balanceApplied, amountPaid, refundedAmount)
  );
}

/** Tagihan masih bisa dibayar (saldo/cicilan transfer)? */
export function isInvoicePayable(
  status: InvoiceStatus,
  outstanding: number
): boolean {
  return (status === "open" || status === "partial") && outstanding > 0;
}

/**
 * Bukti transfer TERAKHIR pembeli ditolak dan belum ada bukti baru — kartu
 * tagihan pembeli diberi tanda «Bukti ditolak» (uji nyata S09 T6: tab
 * «Ditolak» kosong karena isinya tagihan yang ditolak, bukan bukti, jadi
 * pembeli yang mencari "kenapa ditolak" tak menemukan apa pun).
 */
export function latestProofRejected(invoice: {
  status: InvoiceStatus;
  outstanding: number;
  hasPendingPayment?: boolean;
  payments?: ReadonlyArray<{ status: string; createdAt: string }> | null;
}): boolean {
  if (invoice.hasPendingPayment) return false;
  if (!isInvoicePayable(invoice.status, invoice.outstanding)) return false;
  let latest: { status: string; createdAt: string } | null = null;
  for (const p of invoice.payments ?? []) {
    if (!latest || p.createdAt > latest.createdAt) latest = p;
  }
  return latest?.status === "rejected";
}

/** Label tanda kartu tagihan untuk {@link latestProofRejected}. */
export const PROOF_REJECTED_CHIP_LABEL = "Bukti ditolak";

/**
 * Nota bisa ikut "Tandai Lunas Sekaligus" admin (mig 214) — cermin guard RPC
 * `admin_record_invoices_combo_payment`: terbuka + masih bersisa, dan TIDAK
 * sedang menunggu konfirmasi bukti pembeli (yang pending diproses lewat tab
 * Perlu Konfirmasi supaya uang tidak dobel tercatat).
 */
export function canBatchSettleInvoice(inv: {
  status: InvoiceStatus;
  outstanding: number;
  hasPendingPayment: boolean;
}): boolean {
  return isInvoicePayable(inv.status, inv.outstanding) && !inv.hasPendingPayment;
}

/**
 * Nota terbuka bisa DIBATALKAN (void) admin? Cermin guard RPC `admin_void_invoice`
 * (mig 072 + 220): TANPA uang riil tersisa — net settled efektif (balance +
 * amount_paid − refunded) = 0, artinya belum pernah dibayar ATAU sudah DIREFUND
 * PENUH (pelunasan dibatalkan lewat `admin_unmark_invoice_paid`) — dan tak ada
 * bukti pembayaran menunggu. Status open/partial ditegakkan pemanggil; nota
 * `pending_acc` juga voidable di server. Refund parsial (settled efektif > 0)
 * tetap memblokir.
 */
export function canVoidOpenInvoice(inv: {
  balanceApplied: number;
  amountPaid: number;
  refundedAmount: number;
  hasPendingPayment: boolean;
}): boolean {
  return (
    computeSettledEffective(
      inv.balanceApplied,
      inv.amountPaid,
      inv.refundedAmount
    ) === 0 && !inv.hasPendingPayment
  );
}

const INVOICE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Id nota di URL `/orders/tagihan/<id>` berbentuk UUID utuh? Bughunt ketiga
 * (2026-09-30): pembeli membuka tautan nota yang TERPOTONG
 * ("f2ec774c-2511-4e37-ae3") 10× dalam 42 menit — query-nya gagal 22P02 dan
 * halaman menampilkan galat mentah Postgres dengan tombol "Coba Lagi" yang tak
 * mungkin berhasil. Id yang bentuknya salah tak perlu ditanyakan ke server.
 */
export function isInvoiceIdShape(id: string | null | undefined): boolean {
  return INVOICE_ID_RE.test((id ?? "").trim());
}

/**
 * Pcs TERCATAT LUNAS di baris produk AKTIF sebuah nota — gerbang mig 4980 di
 * `admin_void_invoice`: nota tanpa uang bersih (lolos {@link canVoidOpenInvoice})
 * tetap DITOLAK di-void selama angka ini > 0 ("Nota ini masih mengunci N pcs
 * yang tercatat lunas"). Penyebabnya dua: Kredit DP (kartu `dpCreditLock`) dan
 * baris Rp 0 — item gratis dianggap otomatis lunas (`invoice_zero_line_covered`).
 * `zeroLinePcs` = bagian yang berasal dari baris Rp 0, supaya layar bisa
 * menyebut sebab yang benar (bughunt ketiga, natasyashop #5495: tombol
 * Batalkan tampil, server menolak dan menyuruh "batalkan pelunasan" yang tak ada).
 */
export function openInvoiceLockedPaidPcs(inv: {
  lines: ReadonlyArray<{
    kind?: string | null;
    amount?: number | null;
    cancelledAt?: string | null;
    paidQty?: number | null;
  }>;
}): { total: number; zeroLinePcs: number } {
  let total = 0;
  let zeroLinePcs = 0;
  for (const l of inv.lines) {
    if (l.kind !== "product_item" || l.cancelledAt) continue;
    const pcs = Number.isFinite(l.paidQty) ? Math.max(0, Number(l.paidQty)) : 0;
    if (pcs <= 0) continue;
    total += pcs;
    if (Number(l.amount ?? 0) === 0) zeroLinePcs += pcs;
  }
  return { total, zeroLinePcs };
}

/** Kalimat penjelas saat Batalkan nota disembunyikan karena pcs lunas. */
export function lockedPaidPcsVoidNote(locked: { total: number; zeroLinePcs: number }): string {
  const sebab =
    locked.zeroLinePcs >= locked.total
      ? "item gratis (Rp 0) dihitung otomatis lunas"
      : locked.zeroLinePcs > 0
        ? `${locked.zeroLinePcs} pcs di antaranya item gratis (Rp 0) yang dihitung otomatis lunas`
        : "pcs itu sudah dibayar";
  return (
    `Tagihan ini tidak bisa dibatalkan: ${locked.total} pcs di dalamnya tercatat lunas (${sebab}). ` +
    "Bila tagihan memang harus dibatalkan, batalkan dulu pcs itu dari Keranjang Pelanggan."
  );
}

/**
 * Nota terbuka bisa DIEDIT admin? Cermin guard RPC `admin_edit_open_invoice`
 * (mig 101/107 + 220): superset {@link canVoidOpenInvoice} (net settled efektif
 * = 0 → aman walau sudah direfund penuh) DAN tak ada baris aktif ber-`paidQty>0`
 * (kredit DP terpakai) — alokasi bersifat monoton-naik sehingga qty tak boleh
 * turun saat ada pcs tertutup.
 */
export function canEditOpenInvoice(inv: {
  balanceApplied: number;
  amountPaid: number;
  refundedAmount: number;
  hasPendingPayment: boolean;
  lines: ReadonlyArray<{ cancelledAt?: string | null; paidQty?: number }>;
}): boolean {
  if (!canVoidOpenInvoice(inv)) return false;
  return !inv.lines.some((l) => !l.cancelledAt && (l.paidQty ?? 0) > 0);
}

/**
 * Kandidat "Tambah Produk" di Edit Nota / editor ACC: item tagihan pelanggan
 * MINUS item yang sudah punya baris produk AKTIF di nota ini. Cermin guard
 * "baris kembar" RPC `admin_edit_open_invoice` & `admin_add_invoice_items`
 * (mig 4810/4860), yang hanya menolak baris dengan `cancelled_at IS NULL`.
 *
 * Baris yang SUDAH DIBATALKAN tidak ikut menyaring: dulu filter klien memakai
 * semua `orderItemId` nota, sehingga item yang barisnya dibatalkan lalu ingin
 * dimasukkan lagi hilang dari daftar ("Tidak ada produk lain yang bisa
 * ditambahkan") padahal masih di keranjang dan server menerimanya — Edit Nota
 * tak punya tombol Pulihkan untuk baris yang sudah tersimpan batal, jadi item
 * itu terkunci di luar nota (LL049 lealova, 2026-11-01).
 */
export function invoiceAddableItems<T extends { id: string }>(
  items: ReadonlyArray<T>,
  lines: ReadonlyArray<{
    kind: InvoiceLineKind;
    orderItemId?: string | null;
    cancelledAt?: string | null;
  }>
): T[] {
  const activeItemIds = new Set<string>();
  for (const l of lines) {
    if (l.cancelledAt || l.kind !== "product_item" || !l.orderItemId) continue;
    activeItemIds.add(l.orderItemId);
  }
  return items.filter((it) => !activeItemIds.has(it.id));
}

/**
 * KELEBIHAN DP yang bisa dikembalikan ke saldo (mig 174) — cermin TS rumus
 * RPC `admin_refund_dp_excess`: settled efektif nota DP dikurangi total DP
 * yang sudah terpakai sebagai baris kredit `dp_credit` AKTIF pada nota-nota
 * order yang sama, dikurangi CADANGAN DP varian hidup yang belum ditagih
 * (`dp_reserved_for_unbilled`, kredit DP per varian — uji nyata S27). Muncul
 * saat harga akhir produk < DP yang terlanjur dibayar (skenario A3/B5).
 * Negatif/0 = tidak ada kelebihan (kartu tidak tampil).
 */
export function computeDpExcess(settled: number, dpCreditUsed: number, reservedUnbilled = 0): number {
  return Math.max(0, settled - Math.max(0, dpCreditUsed) - Math.max(0, reservedUnbilled));
}

/**
 * Kunci ANTREAN konfirmasi sebuah setoran: satu transfer untuk beberapa nota
 * berbagi batch_id (mig 077) = satu antrean; setoran tanpa batch dihitung per
 * baris. Dipakai badge/indikator "Perlu Konfirmasi" agar 1 batch = 1 hitungan.
 */
export function confirmationBatchKey(payment: {
  batchId: string | null;
  id: string;
}): string {
  return payment.batchId ? `b:${payment.batchId}` : `s:${payment.id}`;
}

/**
 * Jumlah antrean konfirmasi dari sekumpulan setoran PENDING (sudah difilter
 * status='pending'): hitung kunci batch unik. Cermin badge "Perlu Konfirmasi"
 * di direktori tagihan admin (/admin/invoices).
 */
export function countConfirmationBatches(
  pendingPayments: ReadonlyArray<{ batchId: string | null; id: string }>
): number {
  const keys = new Set<string>();
  for (const p of pendingPayments) keys.add(confirmationBatchKey(p));
  return keys.size;
}

/**
 * Satu anggota setoran "Bayar Sekaligus" (mig 077): nota + porsi yang
 * dideklarasi pembeli untuknya. `declared` = invoice_payments.amount baris
 * PENDING milik batch (mig 178); setoran pra-178 mengisi 0 di semua anggota.
 */
export type BatchSubmissionMember = {
  id: string;
  invoiceNo: number;
  invoiceCode: string | null;
  kind: InvoiceKind;
  createdAt: string;
  outstanding: number;
  declared: number;
};

export type BatchSubmissionSummary = {
  memberCount: number;
  /** Angka batch mencakup lebih dari satu nota → label WAJIB menyebut cakupan. */
  spansMultipleInvoices: boolean;
  totalOutstanding: number;
  /** Σ deklarasi pembeli; 0 untuk setoran pra-mig-178. */
  declaredTotal: number;
  /** Anggota yang halamannya sedang dibuka admin; null bila tak ketemu. */
  current: BatchSubmissionMember | null;
  /** Isi awal kolom "nominal yang diterima". */
  defaultAmount: number;
};

/**
 * Ringkasan setoran satu batch DILIHAT DARI halaman salah satu anggotanya —
 * bahan panel konfirmasi admin (`PendingPaymentPanel`). Dipisah ke helper murni
 * karena di sinilah dua cakupan berbeda bertemu di satu layar: total se-batch
 * vs porsi nota yang sedang dibuka. Panel pernah hanya merender yang pertama,
 * sehingga nota Rp 100.000 tampil beserta pengajuan Rp 350.000 tanpa keterangan.
 *
 * `defaultAmount` mempertahankan urutan prioritas yang sudah hidup: total porsi
 * KOMBINASI (mig 166) → deklarasi pembeli (mig 178) → sisa tagihan (fallback
 * setoran lama ber-amount 0).
 */
export function summarizeBatchSubmission(
  members: ReadonlyArray<BatchSubmissionMember>,
  currentInvoiceId: string,
  splitsTotal: number | null
): BatchSubmissionSummary {
  let totalOutstanding = 0;
  let declaredTotal = 0;
  for (const m of members) {
    totalOutstanding += Math.max(0, m.outstanding);
    declaredTotal += Math.max(0, Math.round(m.declared));
  }
  const splits = splitsTotal != null ? Math.max(0, splitsTotal) : 0;
  return {
    memberCount: members.length,
    spansMultipleInvoices: members.length > 1,
    totalOutstanding,
    declaredTotal,
    current: members.find((m) => m.id === currentInvoiceId) ?? null,
    defaultAmount:
      splits > 0 ? splits : declaredTotal > 0 ? declaredTotal : totalOutstanding
  };
}

export type BatchAllocationRow = {
  member: BatchSubmissionMember;
  /** Nominal yang akan masuk ke nota ini bila admin konfirmasi sebesar `total`. */
  portion: number;
  fullyPaid: boolean;
};

/**
 * CERMIN TS dari loop alokasi `confirm_invoice_payment_batch` v4
 * (mig 4380, `order by i.created_at, i.invoice_no`) untuk PRATINJAU UI —
 * tidak menyentuh server. Admin mengetik SATU nominal untuk seluruh batch, dan
 * tanpa pratinjau ia tak punya cara tahu nota yang sedang dibuka kebagian
 * berapa — atau tidak kebagian sama sekali.
 *
 * Tiga aturan server yang ditiru PERSIS (menyederhanakannya = pratinjau
 * berbohong):
 *  1. SETIAP anggota — termasuk yang terakhir — berporsi
 *     `least(remaining, outstanding)`. Sampai mig 5180 anggota terakhir
 *     menyerap `remaining` utuh, dan kelebihannya mengendap di `amount_paid`
 *     tanpa pernah kembali ke pembeli (bughunt B6-1, live di nota #691);
 *  2. sisa yang tak tertampung satu pun nota = `excessToBalance`, yang di
 *     server dikreditkan ke SALDO pembeli (`credit_customer_balance`,
 *     `type='refund'`, `reference_id = batch_id`);
 *  3. anggota ber-outstanding 0 TETAP diproses (baris porsi 0), tidak dilewati
 *     seperti `previewBalanceAllocation` yang mencermin RPC lain.
 */
export function previewBatchConfirmAllocation(
  members: ReadonlyArray<BatchSubmissionMember>,
  total: number
): { rows: BatchAllocationRow[]; allocated: number; excessToBalance: number } {
  const ordered = sortInvoicesFifo(members);
  let remaining = Math.max(0, Math.round(total));
  let allocated = 0;
  const rows: BatchAllocationRow[] = [];

  ordered.forEach((member) => {
    const outstanding = Math.max(0, member.outstanding);
    const portion = Math.max(0, Math.min(remaining, outstanding));
    remaining -= portion;
    allocated += portion;
    rows.push({
      member,
      portion,
      fullyPaid: portion >= outstanding
    });
  });

  return { rows, allocated, excessToBalance: Math.max(0, remaining) };
}

/**
 * Label metode pembayaran untuk pelunasan "Tandai Lunas oleh admin" (mig 077/103)
 * — penanda baris invoice_payments yang dibuat saat admin menandai lunas tanpa
 * bukti transfer. Dipakai untuk mendeteksi & membatalkan penandaan (mig 104/111).
 */
export const ADMIN_MARK_PAID_METHOD = "Tandai Lunas oleh admin";

type UnmarkPaymentRow = {
  status: InvoicePaymentStatus;
  methodLabel: string | null;
  amount: number;
};

/**
 * Jenis nota yang pelunasannya bisa DIBATALKAN dari app (cermin gerbang RPC
 * `admin_unmark_invoice_paid`: `kind not in ('product','ongkir','dp')`).
 * Nota DP ikut sejak mig 6620 (permintaan pemilik 2026-09-30). Gerbang
 * hilirnya — Kredit DP di tagihan barang LUNAS yang memegang uang ditolak, di
 * tagihan barang belum lunas DITARIK, di tagihan LUNAS murni karena kredit
 * ditarik lalu tagihannya dibuka kembali (mig 6760), nota DP hangus bila
 * tagihan barangnya sudah terbit — dicermin `dpSettlementReversal`
 * (`dpInvoiceTrace.ts`), sebab datanya ada di jejak DP, bukan di nota ini.
 */
const REVERSIBLE_INVOICE_KINDS: ReadonlySet<InvoiceKind> = new Set<InvoiceKind>([
  "product",
  "ongkir",
  "dp"
]);

/** Nota ini dilunasi lewat "Tandai Lunas oleh admin"? (baris confirmed) */
export function isAdminMarkedPaid(invoice: {
  kind: InvoiceKind;
  payments: ReadonlyArray<UnmarkPaymentRow>;
}): boolean {
  if (!REVERSIBLE_INVOICE_KINDS.has(invoice.kind)) return false;
  return invoice.payments.some(
    (p) =>
      p.status === "confirmed" &&
      p.methodLabel === ADMIN_MARK_PAID_METHOD &&
      p.amount > 0
  );
}

/** Σ nominal baris "Tandai Lunas oleh admin" yang confirmed (cermin `v_mark_sum`). */
export function adminMarkedPaidSum(invoice: {
  payments: ReadonlyArray<UnmarkPaymentRow>;
}): number {
  return invoice.payments.reduce(
    (sum, p) =>
      p.status === "confirmed" &&
      p.methodLabel === ADMIN_MARK_PAID_METHOD &&
      Number.isFinite(p.amount) &&
      p.amount > 0
        ? sum + p.amount
        : sum,
    0
  );
}

/** Sebab pelunasan nota TIDAK bisa dibatalkan — urutan gerbang RPC. */
export type SettlementReversalBlock =
  | "kind" //          bukan nota barang / ongkir / DP
  | "status" //        void / rejected / menunggu ACC — tak ada pelunasan
  | "over_refunded" // refund yang pernah keluar > uang riil yang pernah dipegang
  | "pending" //       bukti pembayaran menunggu konfirmasi
  | "nothing"; //      tak ada penandaan lunas maupun uang bersih

export type SettlementReversal = {
  /** Σ "Tandai Lunas oleh admin" confirmed — dicabut TANPA uang ke saldo. */
  mark: number;
  /**
   * Uang BERSIH yang masih dipegang nota = saldo + setoran − penandaan − yang
   * sudah dikembalikan. Inilah (dan hanya ini) yang kembali ke saldo pembeli.
   */
  money: number;
  /** Yang sudah pernah dikembalikan ke saldo sebelumnya — tak diulang. */
  refundedBefore: number;
  block: SettlementReversalBlock | null;
};

/**
 * Apa yang terjadi bila pelunasan nota ini dibatalkan — cermin
 * `admin_unmark_invoice_paid` (mig 204/209/6100/6620/7670).
 *
 * mig 7670: refund = UANG BERSIH, bukan buku mentah. `balance_applied` dan
 * `amount_paid` tak pernah ditulis-mundur oleh refund (mig 074) — refund hanya
 * menaikkan `refunded_amount` — jadi nota yang pernah dibatalkan pelunasannya
 * lalu dilunasi lagi memegang `balance + amountPaid − mark − refunded`, bukan
 * `balance + transfer`. Dulu gerbang "pernah direfund" (`refundedAmount > 0`)
 * mengunci nota itu selamanya (batal → edit → Tandai Lunas → buntu); kini
 * yang ditolak hanya refund yang MELAMPAUI uang riil (`money < 0`: sisanya
 * diambil dari porsi Tandai Lunas, mis. refund pembatalan item nota tunai).
 *
 * Gerbang hilir tetap hanya di server: pcs diminta kirim / dikirim (barang),
 * paket shipped/done (ongkir, mig 6100), Kredit DP di tagihan barang lunas
 * ber-uang (DP, mig 6620 — dicermin `dpSettlementReversal` dari jejak DP).
 */
export function invoiceSettlementReversal(
  invoice: {
    kind: InvoiceKind;
    status: InvoiceStatus;
    balanceApplied: number;
    amountPaid: number;
    refundedAmount: number;
    payments: ReadonlyArray<UnmarkPaymentRow>;
  },
  /**
   * bughunt G9-1: ongkir combo nota produk yang SUDAH kembali ke saldo lewat
   * jalur paket (`admin_invoice_combo_ongkir_returned`). Server menahannya
   * dari uang yang dikembalikan, dijepit ke uang bersih nota.
   */
  comboOngkirReturned = 0
): SettlementReversal & { comboKept: number } {
  const mark = adminMarkedPaidSum(invoice);
  const gross = Math.round(
    invoice.balanceApplied + invoice.amountPaid - mark - invoice.refundedAmount
  );
  const comboKept =
    invoice.kind === "product" && gross > 0
      ? Math.min(Math.max(0, Math.round(comboOngkirReturned)), gross)
      : 0;
  const money = gross - comboKept;
  const refundedBefore = Math.max(0, invoice.refundedAmount);
  const block: SettlementReversalBlock | null = !REVERSIBLE_INVOICE_KINDS.has(
    invoice.kind
  )
    ? "kind"
    : invoice.status !== "open" &&
        invoice.status !== "partial" &&
        invoice.status !== "paid"
      ? "status"
      : money < 0
        ? "over_refunded"
        : mark <= 0 && money <= 0
          ? "nothing"
          : invoice.payments.some((p) => p.status === "pending")
            ? "pending"
            : null;
  return { mark, money: Math.max(0, money), refundedBefore, block, comboKept };
}

/**
 * Penandaan lunas admin pada nota ini masih bisa DIBATALKAN dari KERANJANG
 * (`admin_unmark_items_paid`)? Lembar keranjang hanya menjanjikan "penandaan
 * dicabut, nota kembali belum dibayar" — jadi yang ditawarkan hanya nota
 * ber-"Tandai Lunas oleh admin" TANPA uang riil yang masih dipegang
 * (`money === 0`): tak ada yang pindah ke saldo di luar yang tertulis. Nota
 * yang pernah direfund lalu ditandai lunas lagi ikut memenuhi syarat ini
 * (mig 7670). Nota ber-uang riil dibatalkan dari halaman notanya.
 */
export function canUnmarkInvoicePaid(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  balanceApplied: number;
  amountPaid: number;
  refundedAmount: number;
  payments: ReadonlyArray<UnmarkPaymentRow>;
}): boolean {
  const r = invoiceSettlementReversal(invoice);
  return r.block === null && r.mark > 0 && r.money === 0;
}

/**
 * Pelunasan nota ini masih bisa DIBATALKAN otomatis dari halaman detail nota?
 * Superset dari `canUnmarkInvoicePaid` — pelunasan METODE APA PUN: "Tandai
 * Lunas oleh admin" (baris → rejected), SALDO, DAN/ATAU transfer/QRIS
 * terkonfirmasi (uang bersihnya dikembalikan ke saldo pelanggan lewat mesin
 * refund standar). Rinciannya `invoiceSettlementReversal`.
 */
export function canReverseInvoiceSettlement(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  balanceApplied: number;
  amountPaid: number;
  refundedAmount: number;
  payments: ReadonlyArray<UnmarkPaymentRow>;
}): boolean {
  return invoiceSettlementReversal(invoice).block === null;
}

/** Nota ini settled (sebagian/penuh) memakai saldo pelanggan? */
export function isSettledWithBalance(invoice: {
  balanceApplied: number;
}): boolean {
  return invoice.balanceApplied > 0;
}

/**
 * Nota ini punya pembayaran TRANSFER/QRIS/kombinasi terkonfirmasi (uang riil,
 * di luar baris "Tandai Lunas oleh admin")? Dipakai UI Batalkan Pelunasan
 * (mig 209) untuk menjelaskan bahwa porsi uang riil dikembalikan ke SALDO
 * pelanggan (baris invoice_payments tetap confirmed — buku Saldo Bank tak
 * dibalik; pengembalian tunai/transfer di luar app diproses manual).
 */
export function hasConfirmedTransferPayment(invoice: {
  payments: ReadonlyArray<UnmarkPaymentRow>;
}): boolean {
  return invoice.payments.some(
    (p) =>
      p.status === "confirmed" &&
      p.methodLabel !== ADMIN_MARK_PAID_METHOD &&
      p.amount > 0
  );
}

// ─── Koreksi nominal pembayaran (mig 6400) ───────────────────────────────────

/**
 * Sebab sebuah pembayaran TIDAK bisa dikoreksi dari detail nota. Urutannya =
 * urutan gerbang RPC `admin_correct_invoice_payment` yang bisa dinilai dari
 * data layar; gerbang pcs-terkirim dan batas nominal hanya hidup di server
 * (batas nominal dipratinjau {@link previewPaymentCorrection}).
 */
export type PaymentCorrectionBlock =
  | "not_confirmed" // pending / ditolak / bernominal 0
  | "batch" //         setoran satu transfer beberapa nota → Batalkan Konfirmasi
  | "mark_paid" //     "Tandai Lunas oleh admin" → Batalkan Tandai Lunas
  | "combo" //         setoran kombinasi ber-splits
  | "dp_invoice" //    nominal nota DP sudah jadi kredit DP
  | "invoice_status" // nota bukan open/partial/paid
  | "ongkir_paid" //   nota ongkir lunas → Batalkan Pelunasan (efek paket)
  | "pending_proof"; // ada bukti transfer menunggu konfirmasi

/** `null` = pembayaran ini boleh dikoreksi (tunduk gerbang server lainnya). */
export function paymentCorrectionBlock(
  invoice: {
    kind: InvoiceKind;
    status: InvoiceStatus;
    hasPendingPayment: boolean;
  },
  payment: {
    status: InvoicePaymentStatus;
    amount: number;
    batchId: string | null;
    methodLabel: string | null;
    splits: ReadonlyArray<unknown> | null;
  }
): PaymentCorrectionBlock | null {
  if (payment.status !== "confirmed" || !(payment.amount > 0)) {
    return "not_confirmed";
  }
  if (payment.batchId) return "batch";
  if (payment.methodLabel === ADMIN_MARK_PAID_METHOD) return "mark_paid";
  if (payment.splits && payment.splits.length > 0) return "combo";
  if (invoice.kind === "dp") return "dp_invoice";
  if (
    invoice.status !== "open" &&
    invoice.status !== "partial" &&
    invoice.status !== "paid"
  ) {
    return "invoice_status";
  }
  if (invoice.status === "paid" && invoice.kind !== "product") {
    return "ongkir_paid";
  }
  if (invoice.hasPendingPayment) return "pending_proof";
  return null;
}

export type PaymentCorrectionError =
  | "invalid" //    kosong / negatif / pecahan
  | "same" //       sama dengan yang tercatat
  | "below_min" //  uang yang sudah direfund ke saldo > uang diterima
  | "above_max"; // melebihi total tagihan

export type PaymentCorrectionPreview = {
  /** Nominal baru − nominal tercatat (negatif = turun). */
  delta: number;
  /** Nominal terkecil yang diterima server (0 = boleh dibatalkan penuh). */
  min: number;
  /** Nominal terbesar yang diterima server (tanpa kelebihan bayar). */
  max: number;
  /** Uang efektif nota sesudah koreksi (balance + paid − refunded, ≥ 0). */
  settledAfter: number;
  outstandingAfter: number;
  statusAfter: InvoiceStatus;
  error: PaymentCorrectionError | null;
};

/**
 * PRATINJAU koreksi nominal — CERMIN aritmetika RPC
 * `admin_correct_invoice_payment` (mig 6400): settled = saldo terpakai +
 * transfer − refund (TIDAK dijepit dulu, sama dengan server), settled baru =
 * settled + selisih; ditolak bila < 0 atau, untuk kenaikan, > total tagihan.
 * Status sesudahnya: tertutup penuh → Lunas, ada uang → Dibayar sebagian,
 * nol → Belum dibayar. Hanya tampilan: server menghitung ulang semuanya.
 */
export function previewPaymentCorrection(
  invoice: {
    total: number;
    balanceApplied: number;
    amountPaid: number;
    refundedAmount: number;
  },
  oldAmount: number,
  newAmount: number
): PaymentCorrectionPreview {
  const old = Math.round(oldAmount);
  const total = invoice.total;
  const settled =
    invoice.balanceApplied + invoice.amountPaid - invoice.refundedAmount;
  const min = Math.max(0, old - settled);
  const max = old + Math.max(0, total - settled);

  const valid = Number.isInteger(newAmount) && newAmount >= 0;
  const delta = valid ? newAmount - old : 0;
  const newSettled = settled + delta;

  let error: PaymentCorrectionError | null = null;
  if (!valid) error = "invalid";
  else if (delta === 0) error = "same";
  else if (newSettled < 0) error = "below_min";
  else if (delta > 0 && newSettled > total) error = "above_max";

  const settledAfter = Math.max(0, newSettled);
  return {
    delta,
    min,
    max,
    settledAfter,
    outstandingAfter: Math.max(0, total - settledAfter),
    statusAfter:
      total > 0 && settledAfter >= total
        ? "paid"
        : settledAfter > 0
          ? "partial"
          : "open",
    error
  };
}

/**
 * Nomor nota untuk tampilan: kode kustom (Penomoran Dokumen, mig 198 — mis.
 * "INV2607100001") bila ada, selain itu fallback nomor urut bawaan "#12".
 */
export function formatInvoiceNo(
  no: number | null | undefined,
  code?: string | null
): string {
  const clean = (code ?? "").trim();
  if (clean) return clean;
  return no != null && no > 0 ? `#${no}` : "#—";
}

/** Kata jenis nota untuk judul: product → "Barang", dp → "DP", ongkir → "Ongkir". */
export const INVOICE_KIND_TITLE: Record<InvoiceKind, string> = {
  product: "Barang",
  dp: "DP",
  ongkir: "Ongkir"
};

/**
 * Kata jenis nota untuk judul, termasuk tagihan barang yang ikut menagih
 * ongkir: "Barang + Ongkir" (putusan pemilik 2026-10-30, uji nyata S05 T8).
 */
export function invoiceKindWord(kind: InvoiceKind, withOngkir = false): string {
  return withOngkir && kind === "product"
    ? `${INVOICE_KIND_TITLE.product} + ${INVOICE_KIND_TITLE.ongkir}`
    : INVOICE_KIND_TITLE[kind];
}

/**
 * Judul nota spesifik per jenis: "Tagihan Barang #12", "Tagihan Ongkir #12",
 * "Tagihan DP #12" — atau memakai kode kustom bila ada ("Tagihan Barang
 * INV2607100001"). Dipakai sebagai heading kartu/halaman nota. `withOngkir`
 * → "Tagihan Barang + Ongkir #12"; pemanggil yang memegang baris nota memakai
 * {@link formatInvoiceTitleFor} supaya bendera itu tak dihitung ulang sendiri.
 */
export function formatInvoiceTitle(
  kind: InvoiceKind,
  no: number | null | undefined,
  code?: string | null,
  opts?: { withOngkir?: boolean }
): string {
  return `Tagihan ${invoiceKindWord(kind, opts?.withOngkir)} ${formatInvoiceNo(no, code)}`;
}

/**
 * Tagihan barang yang ikut menagih ongkir di dalamnya sendiri — Buat Tagihan
 * + Kirim, atau Kirim Sebelum Lunas — yaitu punya baris ongkir yang belum
 * dibatalkan. Tanpa baris (daftar yang tak memuat baris) → false, judulnya
 * tetap "Tagihan Barang".
 */
export function invoiceCarriesOngkir(invoice: {
  kind: InvoiceKind;
  lines?: ReadonlyArray<{
    kind: InvoiceLineKind;
    description: string;
    cancelledAt?: string | null;
  }> | null;
}): boolean {
  if (invoice.kind !== "product" || !invoice.lines) return false;
  return invoice.lines.some((l) => !l.cancelledAt && isOngkirChargeLine(l));
}

/**
 * {@link formatInvoiceTitle} dari objek nota. Bila barisnya ikut dimuat,
 * tagihan barang berongkir berjudul "Tagihan Barang + Ongkir".
 */
export function formatInvoiceTitleFor(invoice: {
  kind: InvoiceKind;
  invoiceNo: number | null | undefined;
  invoiceCode?: string | null;
  lines?: ReadonlyArray<{
    kind: InvoiceLineKind;
    description: string;
    cancelledAt?: string | null;
  }> | null;
}): string {
  return formatInvoiceTitle(invoice.kind, invoice.invoiceNo, invoice.invoiceCode, {
    withOngkir: invoiceCarriesOngkir(invoice)
  });
}

/**
 * Teks konfirmasi tombol Batalkan nota admin. Nota ongkir yang terikat paket
 * diberi peringatan: `admin_void_invoice` (mig 7750) ikut melepas penunjuk
 * `shipment_requests.ongkir_invoice_id`, jadi paketnya tak lagi punya tagihan
 * ongkir sampai ditagih ulang lewat Set Ongkir.
 */
export function voidInvoiceConfirmText(inv: {
  kind: InvoiceKind;
  invoiceNo: number | null | undefined;
  invoiceCode?: string | null;
  customerName: string;
  /** Paket yang ditagih nota ongkir ini; null = nota tak terikat paket. */
  shipmentRequestId: string | null;
}): string {
  const head = `Batalkan ${formatInvoiceTitleFor(inv)} milik ${inv.customerName}?`;
  if (inv.kind !== "ongkir" || !inv.shipmentRequestId) return head;
  return (
    `${head}\n\n` +
    "Paket kiriman yang ditagih tagihan ini tak lagi punya tagihan ongkir. " +
    "Bila ongkirnya masih harus dibayar, tagih ulang lewat Set Ongkir di Detail Pengiriman " +
    "(selama paketnya belum dikirim)."
  );
}

/** Pilihan chip jenis /tagihan: satu jenis nota, atau "all" = semua jenis. */
export type InvoiceKindFilter = "all" | InvoiceKind;

/**
 * Label chip jenis /tagihan: "Semua", "Tagihan Barang", "Tagihan DP",
 * "Tagihan Ongkir" — kata jenisnya dari `INVOICE_KIND_TITLE`, sumber yang
 * sama dengan judul kartu ("Tagihan Barang #12"), jadi chip & kartu yang
 * disaringnya selalu sebahasa.
 */
export function invoiceKindFilterLabel(kind: InvoiceKindFilter): string {
  return kind === "all" ? "Semua" : `Tagihan ${INVOICE_KIND_TITLE[kind]}`;
}

/**
 * Chip jenis /tagihan — SELALU keempatnya (permintaan pemilik 2026-09-30).
 * Dulu hanya jenis yang dimiliki pembeli yang muncul, dan barisnya
 * disembunyikan bila jenisnya cuma satu; kini barisnya tetap, jenis yang
 * kosong cukup berbuah daftar kosong yang menyebut jenisnya
 * (`invoiceListEmptyText`).
 */
export const INVOICE_KIND_FILTERS: ReadonlyArray<{
  id: InvoiceKindFilter;
  label: string;
}> = (["all", "product", "dp", "ongkir"] as const).map((id) => ({
  id,
  label: invoiceKindFilterLabel(id)
}));

/**
 * Chip jenis awal /tagihan dari query `?jenis=` — tautan "Rincian DP lihat
 * di" pesan penagihan DP membuka `/tagihan?jenis=dp`. Nilai kosong/asing
 * (atau dobel dari `?jenis=a&jenis=b`) → "all", bukan daftar yang kosong.
 */
export function parseInvoiceKindFilterParam(
  raw: string | string[] | null | undefined
): InvoiceKindFilter {
  const value = (typeof raw === "string" ? raw : "").trim().toLowerCase();
  return INVOICE_KIND_FILTERS.find((f) => f.id === value)?.id ?? "all";
}

/**
 * Teks daftar /tagihan yang kosong. Menyebut jenis & bulan yang sedang
 * dipilah supaya "kosong di pilahan ini" tak terbaca "tak punya tagihan sama
 * sekali" — sejak chip jenis selalu tampil dan tab riwayat dipilah per bulan,
 * daftar kosong jauh lebih sering terjadi pada pembeli yang notanya ada.
 *
 * Bulan riwayat = bulan TERBIT, jadi kalimatnya "terbitan <bulan>", bukan
 * "lunas di <bulan>" (bughunt E6-3: ±11% nota lunas Galea dilunasi di bulan
 * lain). Saat mencari, bulan tak disebut — pencarian menembus bulan (E6-6).
 */
export function invoiceListEmptyText(input: {
  subTab: InvoiceSubTab;
  kind: InvoiceKindFilter;
  /** Label bulan terpilih ("Agustus 2026"); null untuk sub-kolom tanpa bulan. */
  monthLabel: string | null;
  /** Kotak cari sedang terisi. */
  searching: boolean;
}): string {
  const what = input.kind === "all" ? "tagihan" : invoiceKindFilterLabel(input.kind);
  if (input.searching) {
    return `Tidak ada ${what} yang cocok dengan pencarian.`;
  }
  const issued = input.monthLabel ? ` terbitan ${input.monthLabel}` : "";
  switch (input.subTab) {
    case "belum_lunas":
      return `Tidak ada ${what}${issued} yang belum lunas.`;
    case "sudah_lunas":
      return `Belum ada ${what}${issued} yang lunas.`;
    case "ditolak":
      return `Tidak ada ${what}${issued} yang ditolak.`;
  }
}

/**
 * Apakah filter bulan sedang memilah daftar? Hanya di tab yang dipilah per
 * bulan (`byMonth`), dan TIDAK saat kotak cari terisi: mencari nomor nota lama
 * (mis. kode dari chat WhatsApp) tak boleh menuntut orang menebak bulannya
 * (bughunt E6-6). Chip bulannya tetap tampil, redup, dengan
 * `searchAllMonthsNote` di bawahnya.
 */
export function monthFilterApplies(byMonth: boolean, search: string): boolean {
  return byMonth && search.trim() === "";
}

/** Catatan di bawah chip bulan saat pencarian menembus bulan (bughunt E6-6). */
export function searchAllMonthsNote(monthLabelText: string): string {
  return `Pencarian mencakup semua bulan — kosongkan kotak cari untuk kembali ke ${monthLabelText}.`;
}

/**
 * Keterangan badge "Anda telah hemat" (Sudah Lunas pembeli): menyebut nota mana
 * yang dijumlah — bulan TERBIT (bughunt E6-3), atau semua bulan saat kotak
 * cari terisi (bughunt E6-6).
 */
export function savingsScopeCaption(input: {
  count: number;
  monthLabel: string | null;
  searching: boolean;
}): string {
  const scope = input.searching
    ? " hasil pencarian (semua bulan)"
    : input.monthLabel
      ? ` terbitan ${input.monthLabel}`
      : "";
  return `Total diskon & potongan dari ${input.count} tagihan lunas${scope}.`;
}

/**
 * Judul kartu "Total belum lunas" (/tagihan pembeli & tab Terbuka admin).
 * Angkanya ikut chip jenis & pencarian, sedangkan chip jenisnya ada di BAWAH
 * kartu — jadi judulnya wajib menyebut pilahan yang sedang berlaku, supaya
 * "Rp 0" pada pilahan kosong tak terbaca "tak punya tunggakan".
 */
export function outstandingTotalLabel(input: {
  kind: InvoiceKindFilter;
  /** Kotak cari sedang terisi. */
  searching: boolean;
}): string {
  return [
    "Total belum lunas",
    input.kind !== "all" ? invoiceKindFilterLabel(input.kind) : null,
    input.searching ? "hasil pencarian" : null
  ]
    .filter(Boolean)
    .join(" · ");
}

// ─── Filter daftar nota per-reseller admin (/admin/invoices/reseller/[id]) ───

/**
 * Tab daftar nota per-reseller admin — susunan pemilik 2026-10-01:
 * Perlu Tindakan (gabungan Perlu ACC + Perlu Konfirmasi) | Terbuka | Riwayat.
 * (Tab Siap Kirim sempat ada lalu dibatalkan pemilik pada hari yang sama.)
 */
export type AdminInvoiceTab = "tindakan" | "terbuka" | "riwayat";

/**
 * Filter yang berlaku di tiap tab daftar nota per-reseller admin — cermin
 * /tagihan pembeli (permintaan pemilik 2026-09-30):
 *  - Terbuka = daftar UTANG → chip jenis, TANPA bulan (alasan yang sama dengan
 *    `invoiceSubTabByMonth`: tunggakan bulan lampau tak boleh lenyap dari
 *    layar orang yang menagihnya).
 *  - Riwayat = lunas/batal/ditolak → chip jenis + per bulan.
 *  - Perlu Tindakan (ACC + konfirmasi) = ANTREAN KERJA → tanpa filter apa
 *    pun: chip jenis yang tertinggal aktif dari tab lain akan menyembunyikan
 *    pekerjaan yang menunggu admin, sementara badge tab tetap menghitungnya.
 */
export function adminInvoiceTabFilters(tab: AdminInvoiceTab): {
  byKind: boolean;
  byMonth: boolean;
} {
  switch (tab) {
    case "terbuka":
      return { byKind: true, byMonth: false };
    case "riwayat":
      return { byKind: true, byMonth: true };
    case "tindakan":
      return { byKind: false, byMonth: false };
  }
}

/**
 * Teks daftar kosong per tab admin. Tab tanpa filter mengabaikan jenis/bulan
 * yang dioper (`adminInvoiceTabFilters`); teks tanpa pilahan sama persis
 * dengan sebelum filter ada.
 */
export function adminInvoiceEmptyText(input: {
  tab: AdminInvoiceTab;
  kind: InvoiceKindFilter;
  /** Label bulan terpilih ("Agustus 2026"). */
  monthLabel: string | null;
  /** Kotak cari sedang terisi. */
  searching: boolean;
}): string {
  const { byKind, byMonth } = adminInvoiceTabFilters(input.tab);
  const kindLabel =
    byKind && input.kind !== "all" ? invoiceKindFilterLabel(input.kind) : null;
  // Pencarian menembus bulan (bughunt E6-6) — bulan tak disebut.
  if (input.searching) {
    return `Tidak ada ${kindLabel ?? "tagihan"} yang cocok dengan pencarian.`;
  }
  const what = kindLabel ?? "tagihan";
  switch (input.tab) {
    case "tindakan":
      return "Tidak ada pengajuan tagihan yang menunggu ACC maupun bukti pembayaran yang menunggu konfirmasi.";
    case "terbuka":
      return `Tidak ada ${what} terbuka.`;
    case "riwayat":
      // Bulan riwayat = bulan TERBIT (bughunt E6-3).
      return byMonth && input.monthLabel
        ? `Belum ada ${what} terbitan ${input.monthLabel} yang lunas/batal.`
        : `Belum ada ${what} lunas/batal.`;
  }
}

/**
 * Baris penyeimbang kartu Total belum lunas tab Terbuka admin (bughunt E6-2).
 * Kartu itu menjumlah nota tab Terbuka saja (angka = daftar di bawahnya),
 * sedangkan pembeli & direktori admin ikut memasukkan nota Menunggu ACC —
 * baris ini menyebut selisihnya, jadi Terbuka + ACC = angka di layar pembeli.
 */
export function pendingAccNote(input: {
  count: number;
  /** Nominal yang sudah diformat pemanggil (formatter layar itu sendiri). */
  amountText: string;
}): string {
  return `+ ${input.amountText} menunggu ACC (${input.count} tagihan) — di tab Perlu Tindakan`;
}

/**
 * Nama produk dari nota DP. Baris `manual` nota DP berformat
 * "DP — <Nama Produk>" (lihat 0990_dp_invoice_on_po_close.sql); kembalikan
 * bagian nama produknya saja. null bila baris tak ditemukan / kosong.
 */
export function dpInvoiceProductName(
  lines: ReadonlyArray<{
    kind: InvoiceLineKind;
    description: string;
    cancelledAt?: string | null;
  }>
): string | null {
  const line = lines.find((l) => l.kind === "manual" && !l.cancelledAt);
  if (!line) return null;
  const name = (line.description ?? "").replace(DP_LINE_PREFIX, "").trim();
  return name || null;
}

/**
 * Awalan baris pokok nota DP. KELIMA penulisnya memakai bentuk yang sama —
 * `'DP — ' || products.name` (0990 · 1260 · backfill 0712/0720) — dengan em
 * dash U+2014; varian en dash/hyphen ditoleransi untuk data lama.
 */
const DP_LINE_PREFIX = /^\s*DP\s*[—–-]\s*/i;

/**
 * Nota ini layak menampilkan kartu "Rincian DP" (asal-usul nominal DP).
 *
 * Nota DP **void/rejected** sengaja dikecualikan. `void_unpaid_dp_for_order`
 * (mig 173) hanya men-set `invoices.status='void'` dan TIDAK menyentuh
 * `invoice_lines`, sedangkan `computeInvoiceTotal` hanya melewati baris
 * ber-`cancelled_at` — jadi nota DP yang sudah dibatalkan masih memulangkan
 * nominal penuh. Kartunya akan menyebut biaya yang sudah batal itu sebagai "DP
 * yang ditagihkan" lalu MENGURANGKANNYA dari perkiraan sisa pelunasan: pembeli
 * membaca sisa tagihan lebih kecil daripada yang sebenarnya, padahal DP yang
 * tak pernah dibayar tidak menghasilkan kredit apa pun saat pelunasan.
 */
export function canShowDpBreakdown(invoice: {
  kind: InvoiceKind;
  status: InvoiceStatus;
  sourceOrderId: string | null;
}): boolean {
  return (
    invoice.kind === "dp" &&
    invoice.sourceOrderId != null &&
    invoice.status !== "void" &&
    invoice.status !== "rejected"
  );
}

/** Bahan minimal penyusun judul nota (dipakai helper judul di bawah). */
export type InvoiceTitleSource = {
  kind: InvoiceKind;
  invoiceNo: number | null | undefined;
  invoiceCode?: string | null;
  lines: ReadonlyArray<{
    kind: InvoiceLineKind;
    description: string;
    cancelledAt?: string | null;
  }>;
};

/**
 * Judul kartu/halaman nota — sama dengan {@link formatInvoiceTitle}, tetapi
 * untuk nota DP memakai NAMA PRODUK ("Tagihan DP Kaos Polos") menggantikan
 * nomor nota bila nama produk bisa diturunkan dari barisnya.
 */
export function formatInvoiceTitleWithProduct(
  invoice: InvoiceTitleSource
): string {
  if (invoice.kind === "dp") {
    const name = dpInvoiceProductName(invoice.lines);
    if (name) return `Tagihan ${INVOICE_KIND_TITLE.dp} ${name}`;
  }
  return formatInvoiceTitleFor(invoice);
}

/**
 * Heading kartu nota dua baris untuk daftar nota admin
 * (`/admin/invoices/reseller/[customerId]`).
 *
 * Nota DP dinamai NAMA PRODUKNYA ("Tagihan DP Kaos Polos", cermin kartu
 * `/tagihan` pembeli) supaya admin tahu DP ini milik pesanan yang mana; nomor
 * notanya TIDAK hilang — turun ke `subtitle` (baris di bawah judul) agar
 * identitas dokumen tetap terbaca & bisa disalin/dicari.
 *
 * `subtitle` sengaja null untuk jenis nota lain — dan untuk nota DP yang nama
 * produknya tak bisa diturunkan (baris manualnya dibatalkan/kosong) — karena
 * nomornya sudah ada di judul; menampilkannya lagi hanya jadi baris dobel.
 */
export function invoiceCardHeading(invoice: InvoiceTitleSource): {
  title: string;
  subtitle: string | null;
} {
  const usesProductName =
    invoice.kind === "dp" && dpInvoiceProductName(invoice.lines) !== null;
  return {
    title: formatInvoiceTitleWithProduct(invoice),
    subtitle: usesProductName
      ? formatInvoiceNo(invoice.invoiceNo, invoice.invoiceCode)
      : null
  };
}

/**
 * Nama produk yang "diwakili" sebuah nota — bahan pencarian daftar nota admin.
 *
 * - Nota **Barang**: deskripsi tiap baris `product_item` AKTIF (snapshot
 *   "Nama Produk — Nama Varian", lihat 0720_invoice_acc_allocation_rpc.sql),
 *   sehingga mengetik nama produk MAUPUN nama varian sama-sama mengena.
 * - Nota **DP**: nama produk dari baris `manual` ("DP — <nama produk>") —
 *   yaitu nama yang kini jadi judul kartunya.
 * - Nota **Ongkir**: tak punya baris produk → kosong.
 *
 * SENGAJA hanya nama produk: label biaya (Ongkir / Biaya Dropship / Diskon)
 * tidak diikutkan supaya mengetik "diskon" tidak menyapu hampir semua nota.
 * Baris dibatalkan dilewati (cermin `dpInvoiceProductName` &
 * `summarizeInvoiceProducts`).
 */
function invoiceProductSearchTerms(
  inv: Pick<InvoiceTitleSource, "kind" | "lines">
): string[] {
  const terms = inv.lines
    .filter((l) => l.kind === "product_item" && !l.cancelledAt)
    .map((l) => l.description.trim())
    .filter(Boolean);
  if (inv.kind === "dp") {
    const dpName = dpInvoiceProductName(inv.lines);
    if (dpName) terms.push(dpName);
  }
  return terms;
}

/**
 * Cocokkan sebuah nota dengan kata kunci pencarian (daftar nota reseller —
 * `/admin/invoices/reseller/[id]`). Cocok bila query kosong, atau termuat di:
 * nomor nota (dengan/atu tanpa "#"), judul jenis ("barang/dp/ongkir"),
 * catatan, atau **nama produk/varian** di dalam nota
 * ({@link invoiceProductSearchTerms}). Case-insensitive + trim, pencocokan
 * substring. Pure → diuji `invoices.test.ts`.
 */
export function invoiceMatchesSearch(
  inv: {
    invoiceNo: number | null | undefined;
    invoiceCode?: string | null;
    kind: InvoiceKind;
    note: string | null | undefined;
    lines: InvoiceTitleSource["lines"];
  },
  query: string
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const needle = q.replace(/^#/, "");
  const haystacks = [
    String(inv.invoiceNo ?? ""),
    (inv.invoiceCode ?? "").toLowerCase(),
    // Judul per JENIS saja: "+ Ongkir" tak boleh jadi kunci (lihat tes
    // "label biaya/diskon TIDAK jadi kunci").
    formatInvoiceTitle(inv.kind, inv.invoiceNo, inv.invoiceCode).toLowerCase(),
    INVOICE_KIND_TITLE[inv.kind].toLowerCase(),
    (inv.note ?? "").toLowerCase(),
    ...invoiceProductSearchTerms(inv).map((t) => t.toLowerCase())
  ];
  return haystacks.some((h) => h.includes(needle));
}

// ─── Harga normal & diskon per baris (mig 095) ───────────────────────────────
// Baris product_item menyimpan `unitNormalPrice` (snapshot harga normal/pcs).
// Diskon DITURUNKAN dari (harga normal × qty − amount) — satu sumber melayani
// diskon nominal MAUPUN persen; tampilan ke reseller memuat keduanya.

export type DiscountMode = "nominal" | "persen";

export type LineDiscount = {
  /** Harga normal per-pcs (snapshot). */
  normalUnit: number;
  /** Harga normal total = normalUnit × qty. */
  normalTotal: number;
  /** Harga yang dibayar reseller (amount baris, clamp ≥ 0). */
  payTotal: number;
  /** Potongan Rupiah = max(0, normalTotal − payTotal). */
  discountRp: number;
  /** Potongan persen (0–100, belum dibulatkan). */
  discountPct: number;
  /**
   * Kelebihan tagih: nominal baris DI ATAS harga normal × qty. 0 pada baris
   * normal.
   *
   * Ada karena `discountRp` diklem `Math.max(0, …)` — selisih ke arah SEBALIKNYA
   * karena itu terbaca sebagai "tak ada diskon", tak terbedakan dari baris yang
   * memang dijual pada harga normal. Bughunt D1-2: nota lalu mencetak "–" di
   * kolom Harga Jual Umum DAN kolom Diskon, sehingga baris yang menagih
   * Rp 4.260.000 untuk barang berharga normal Rp 215.000/pcs tak punya satu pun
   * tanda di dokumen mana pun. Angka ini yang membuat keadaan itu bisa dilihat.
   */
  surchargeRp: number;
  /**
   * HARGA TAMBAHAN per pcs (mig 7900; snapshot `invoice_lines.unit_price_extra`)
   * yang TERMASUK di `payTotal` dan berada DI LUAR diskon: tambahan tak pernah
   * dipotong, jadi `discountRp`/`discountPct`/`surchargeRp` dinilai atas
   * `payBaseTotal` = `payTotal − extraTotal`. 0 = tanpa tambahan.
   */
  extraUnit: number;
  /** `extraUnit × qty`. */
  extraTotal: number;
  /** Bagian nominal baris yang lahir dari harga umum − potongan (tanpa tambahan). */
  payBaseTotal: number;
};

/**
 * Tambahan per pcs yang bisa dipercaya untuk sebuah baris: angka ≥ 0, dan tak
 * pernah melampaui nominal yang ditagih (lint DB `price_extra_lint` menjaganya
 * nol; kalau datanya janggal, diklem supaya diskon tak terbaca negatif).
 */
export function lineExtraUnit(
  raw: number | null | undefined,
  payTotal: number,
  qty: number
): number {
  const v = Number(raw ?? 0);
  if (!Number.isFinite(v) || v <= 0 || qty <= 0) return 0;
  return Math.min(v, Math.max(0, payTotal / qty));
}

/**
 * Lantai harga tambahan baris nota (bughunt D15-16, putusan pemilik 2026-10-22
 * opsi a): nominal baris produk WAJIB memuat tambahannya — 0 (harga menyusul /
 * belum dihargai, putusan turunan #11a) atau ≥ tambahan × qty; tambahan tak ikut
 * nego/diskon. Kembar SQL `invoice_line_below_extra` (mig 8100, kelonggaran ½
 * rupiah yang sama); penjaga tertunda `invoice_line_extra_floor` menolaknya di
 * SEMUA jalur nota — editor memanggil ini lebih dulu supaya pesannya tiba
 * sebelum kirim. `extraUnit` = tambahan per pcs yang AKAN tersimpan di baris.
 * `null` = lolos.
 */
export function invoiceLineExtraFloorError(line: {
  description: string;
  amount: number;
  qty: number | null | undefined;
  extraUnit: number | null | undefined;
}): string | null {
  const amount = Number.isFinite(line.amount) ? line.amount : 0;
  const qty = Math.max(0, Math.floor(Number(line.qty ?? 0) || 0));
  const extra = Math.max(0, Number(line.extraUnit ?? 0) || 0);
  const floor = extra * qty;
  if (!(amount > 0) || !(amount < floor - 0.5)) return null;
  return (
    `Nominal "${line.description.trim() || "produk"}" ${formatShareRp(amount)} di bawah harga tambahannya ` +
    `(${formatShareRp(extra)}/pcs × ${qty} pcs = ${formatShareRp(floor)}) — harga tambahan tak ikut ` +
    "nego/diskon; potongan yang lebih dalam pakai Diskon tagihan."
  );
}

/**
 * Tambahan BAWAAN untuk baris nota yang dihargai di ACC / Edit Nota (bughunt
 * D14-22, putusan pemilik 2026-10-21 "tambahan ikut ditempel"): potret pesanan
 * bila itemnya sudah berharga (`unit_price` terisi), selain itu tambahan varian
 * HARI INI — sumber yang sama dengan resnapshot (`unitPriceSnapshot.ts`).
 * Kembar SQL: `invoice_line_apply_price_extra` (server menolak angka lain).
 */
export function pricedLineExtraDefault(
  item: { unitPrice: number | string | null | undefined; unitPriceExtra: number | string | null | undefined },
  variantPriceExtra: number | string | null | undefined
): number {
  const raw = item.unitPrice != null ? item.unitPriceExtra : variantPriceExtra;
  const v = Number(raw ?? 0);
  return Number.isFinite(v) && v > 0 ? Math.round(v) : 0;
}

/**
 * Harga baris nota TERSIMPAN sudah disepakati? (D14-22; bughunt D15 R1) — berharga
 * normal DAN bernominal > 0. Nominal 0 = harga menyusul walau harga normalnya
 * sudah diisi trigger (`set_invoice_line_normal_price` mengisinya untuk item
 * yang belum berharga): gerbang yang SAMA dengan `lineHasSettledDiscount`, yang
 * membuat prefill diskon level memasang diskon di baris itu — tambahannya wajib
 * ikut ditempel, kalau tidak "Jadi Rp 180.000" alih-alih 220.000.
 */
export function pricedLineSeedAgreed(line: { unitNormalPrice?: number | null; amount: number }): boolean {
  return line.unitNormalPrice != null && line.unitNormalPrice > 0 && line.amount > 0;
}

/** Harga pesanan per pcs yang bisa dipakai membandingkan — > 0 dan berhingga. */
function knownOrderUnitPrice(v: number | null | undefined): number | null {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Diskon bawaan (setelan produk per level HARI INI, `fetchLevelDiscountDefaults`)
 * menempel saat admin MENGISI harga normal HANYA pada baris yang memang belum
 * berharga sejak awal (bughunt D16-9): baris TERSIMPAN belum disepakati
 * (`pricedLineSeedAgreed`) DAN pesanannya tak membawa harga checkout.
 *
 * "Belum berharga" dinilai dari baris tersimpan, bukan draf. Dulu mengosongkan
 * lalu mengetik ulang harga normal pada baris promo dianggap "harga menyusul"
 * dan ikut memasang diskon level di atas harga promo — 165.000 × 60% = 99.000
 * di urfimutiyaro, tanpa peringatan. Admin tetap bebas mengetik diskonnya sendiri.
 */
export function normalFillTakesDefault(args: {
  seedAgreed: boolean;
  orderUnitPrice: number | null | undefined;
  prevNormal: number | null;
  nextNormal: number | null;
  touched: boolean;
}): boolean {
  if (args.touched || args.seedAgreed) return false;
  if (knownOrderUnitPrice(args.orderUnitPrice) != null) return false;
  return args.prevNormal == null && args.nextNormal != null;
}

/**
 * Selisih nominal baris produk terhadap HARGA PESANAN (bughunt D16-9).
 *
 * `order_items.unit_price` = harga per pcs yang disetujui pembeli saat checkout
 * (promo, voucher, level, tambahan sudah terlipat; `request_invoice` menagih
 * qty × harga itu apa adanya). `diff` > 0 = nota lebih mahal dari pesanan.
 *
 * null = tak ada pembanding (harga menyusul, qty 0) atau selisihnya cuma
 * pembulatan — ≤ 1 rupiah per pcs, ambang yang SAMA dengan pencatat selisih di
 * server (mig 8370, `invoice_line_order_price_drift`). Bukan larangan: harga
 * nego admin sah, ia hanya wajib terlihat.
 */
export function orderPriceDrift(args: {
  orderUnitPrice: number | null | undefined;
  qty: number;
  amount: number;
}): { expected: number; diff: number } | null {
  const unit = knownOrderUnitPrice(args.orderUnitPrice);
  if (unit == null) return null;
  const qty = Number.isFinite(args.qty) ? Math.max(0, Math.round(args.qty)) : 0;
  if (qty <= 0) return null;
  const expected = Math.round(unit * qty);
  const diff = Math.round(Number.isFinite(args.amount) ? args.amount : 0) - expected;
  if (Math.abs(diff) <= Math.max(1, qty)) return null;
  return { expected, diff };
}

/**
 * Isian draf baris produk yang menagih tepat HARGA PESANAN — tombol "Pakai harga
 * pesanan" editor ACC & Edit Nota (bughunt D16-9). Harga normal dipertahankan
 * (harga coret tetap tampil) dan selisihnya jadi potongan nominal; bila harga
 * normal yang diketik justru di bawah harga pesanan, harga normalnya dikembalikan
 * ke harga pesanan tanpa potongan. Baris harga menyusul: harga akhir utuh
 * (termasuk tambahan, sama dengan kolom "Harga akhir").
 */
export function draftAtOrderPrice(args: {
  normalUnit: number | null;
  qty: number;
  extraUnit: number;
  orderUnitPrice: number;
}): { normalRaw?: string; mode: DiscountMode; value: string; amountRaw?: string } {
  const qty = Math.max(1, Math.round(args.qty));
  const expected = Math.round(args.orderUnitPrice * qty);
  if (args.normalUnit == null) {
    return { mode: "nominal", value: "0", amountRaw: String(expected) };
  }
  const extraTotal = Math.max(0, Math.round(args.extraUnit)) * qty;
  const base = expected - extraTotal;
  const normalTotal = args.normalUnit * qty;
  if (base >= 0 && normalTotal >= base) {
    return { mode: "nominal", value: String(Math.round(normalTotal - base)) };
  }
  return {
    normalRaw: String(Math.max(0, Math.round(args.orderUnitPrice - Math.max(0, args.extraUnit)))),
    mode: "nominal",
    value: "0"
  };
}

/**
 * Tambahan baris ini BERGANTUNG pada tambahan bawaan (D14-22): sedang dihargai
 * — berharga normal di draf DAN bagian dasarnya (potongan atas harga normal,
 * tanpa tambahan) > 0 — dari harga yang belum disepakati dan tanpa tambahan
 * tepercaya. Bagian dasar 0 = baris masih gratis / belum dihargai (pelanggan
 * tanpa prefill diskon level, baris gratis yang disengaja): tambahan TIDAK
 * ditempel, kalau tidak nominalnya jadi "tambahan saja" (2 × 20.000 = 40.000,
 * tinjauan PR D #1). Satu predikat untuk draf, op server, dan gerbang "bawaan
 * belum termuat" — supaya ketiganya tak mungkin menyimpang.
 */
export function pricedLineExtraPending(args: {
  hasNormal: boolean;
  /** Bagian dasar draf (potongan atas harga normal × qty, tanpa tambahan) > 0. */
  basePriced: boolean;
  seedAgreed: boolean;
  seedExtra: number;
}): boolean {
  return args.hasNormal && args.basePriced && !args.seedAgreed && !(args.seedExtra > 0);
}

/**
 * Banyak baris yang tambahannya BERGANTUNG pada bawaan (`pricedLineExtraPending`)
 * tetapi bawaannya belum diketahui — belum tiba / gagal dimuat (bughunt D15
 * A4). > 0 = simpan/ACC baris itu menunggu bawaannya (atau admin menyetujui
 * memakai tambahan tersimpan, tinjauan PR D #6). Baris yang belum dihargai
 * (bagian dasar 0) tak pernah menahan apa pun — emas-nol utuh.
 */
export function pricedLineExtrasUnknownCount(
  rows: ReadonlyArray<{
    hasNormal: boolean;
    basePriced: boolean;
    seedAgreed: boolean;
    seedExtra: number;
    defaultKnown: boolean;
  }>
): number {
  return rows.filter((r) => !r.defaultKnown && pricedLineExtraPending(r)).length;
}

/**
 * Tambahan per pcs draf baris editor ACC / Edit Nota (D14-22). Baris yang
 * TERSIMPAN tanpa harga yang disepakati & tanpa tambahan (harga menyusul /
 * harga akhir, termasuk bernominal 0 ber-harga normal trigger) mendapat
 * tambahan bawaan begitu bagian dasarnya > 0 (prefill diskon level, atau admin
 * mengisi harga normal / diskon); dikosongkan lagi atau digratiskan → kembali
 * ke potret semula. Baris yang harganya sudah disepakati atau sudah bertambahan
 * tak pernah diubah.
 */
export function pricedLineDraftExtra(args: {
  hasNormal: boolean;
  basePriced: boolean;
  seedAgreed: boolean;
  seedExtra: number;
  defaultExtra: number | undefined;
}): number {
  const { seedExtra, defaultExtra } = args;
  if (!pricedLineExtraPending(args)) return seedExtra;
  return defaultExtra !== undefined && defaultExtra > 0 ? defaultExtra : seedExtra;
}

/**
 * Tambahan yang WAJIB dikirim di op baris ACC / Edit Nota (D14-22), atau
 * `undefined` = kunci absen (potret baris tak disentuh, perilaku lama). Hanya
 * baris yang sedang dihargai dari harga menyusul (`pricedLineExtraPending`)
 * dan tambahannya beda dari potret TERSIMPAN — bukan potret terjepit editor:
 * baris bernominal 0 menyimpan potret pesanan (mis. 20.000) yang terjepit jadi
 * 0 di editor, sehingga nominal baru tanpa tambahan WAJIB menulis 0, bukan
 * meninggalkan 20.000. Pemanggil WAJIB menahan simpan selama bawaannya belum
 * termuat (bughunt D15 A4) — tanpa bawaan, `extraUnit` masih potret terjepit.
 */
export function pricedLineExtraToSend(args: {
  hasNormal: boolean;
  basePriced: boolean;
  seedAgreed: boolean;
  seedExtra: number;
  storedExtra: number;
  extraUnit: number;
}): number | undefined {
  const { storedExtra, extraUnit } = args;
  if (!pricedLineExtraPending(args)) return undefined;
  return extraUnit !== storedExtra ? extraUnit : undefined;
}

/**
 * Rincian diskon sebuah baris nota. Hanya relevan untuk baris product_item
 * yang punya harga normal (snapshot) & qty > 0; selain itu null (mis. baris
 * ongkir/manual/kredit DP, atau produk "harga menyusul" tanpa price_normal).
 */
export function lineDiscount(line: {
  kind: InvoiceLineKind;
  qty?: number | null;
  amount: number;
  unitNormalPrice?: number | null;
  /** Snapshot tambahan per pcs (mig 7900) — di luar diskon; absen/null = 0. */
  unitPriceExtra?: number | null;
}): LineDiscount | null {
  if (line.kind !== "product_item") return null;
  const qty = line.qty ?? 0;
  const normalUnit = line.unitNormalPrice ?? 0;
  if (qty <= 0 || normalUnit <= 0) return null;
  const normalTotal = normalUnit * qty;
  const payTotal = Math.max(0, Number.isFinite(line.amount) ? line.amount : 0);
  // Tambahan (mig 7900) dikeluarkan dulu: potongan dinilai atas bagian yang
  // lahir dari harga umum — XXL umum 100.000, bayar 110.000 = 90.000 + 20.000
  // tetap "diskon 10 %", bukan "bayar di atas harga umum".
  const extraUnit = lineExtraUnit(line.unitPriceExtra, payTotal, qty);
  const extraTotal = extraUnit * qty;
  const payBaseTotal = Math.max(0, payTotal - extraTotal);
  const discountRp = Math.max(0, normalTotal - payBaseTotal);
  const discountPct = normalTotal > 0 ? (discountRp / normalTotal) * 100 : 0;
  const surchargeRp = Math.max(0, payBaseTotal - normalTotal);
  return {
    normalUnit, normalTotal, payTotal, discountRp, discountPct, surchargeRp,
    extraUnit, extraTotal, payBaseTotal
  };
}

/**
 * Baris ini menagih DI ATAS harga normal yang tercatat?
 *
 * SATU definisi untuk tiga permukaan yang wajib sepakat — nota PDF, surat jalan,
 * dan layar nota admin. Sampai 2026-09-07 rumusnya disalin di dua tempat
 * (`invoicePdf.ts`, `shippingLabel.ts`) dan tak ada di tempat ketiga; dua
 * dokumen karena itu MENYEMBUNYIKAN kolom pembandingnya sementara layar admin
 * tak pernah tahu keadaan itu ada.
 *
 * Dibandingkan pada NOMINAL BARIS (`amount` vs `normalUnit × qty`), bukan
 * rata-rata per pcs yang sudah dibulatkan — pembulatan bisa menyembunyikan
 * selisih.
 *
 * Menyembunyikan kolom pembanding di dokumen PEMBELI adalah keputusan yang
 * disengaja (harga khusus admin di atas harga umum bukan kesalahan, dan
 * "Umum Rp 300.000 · Diskon – · Harga Reseller Rp 400.000" hanya menyulut
 * pertanyaan). Yang tidak disengaja adalah keadaan itu juga tak terlihat oleh
 * ADMIN. Predikat ini melayani keduanya: dokumen pembeli memakainya untuk
 * MENYEMBUNYIKAN, layar admin untuk MENANDAI.
 */
export function linePaidAboveNormal(line: {
  kind: InvoiceLineKind;
  qty?: number | null;
  amount: number;
  unitNormalPrice?: number | null;
  unitPriceExtra?: number | null;
}): boolean {
  // Dinilai atas bagian TANPA tambahan (mig 7900) — satu rumus dengan
  // `lineDiscount`, supaya "di atas harga umum" dan "diskon" tak pernah
  // sama-sama benar untuk baris yang sama.
  const d = lineDiscount(line);
  return !!d && d.surchargeRp > 0;
}

/**
 * Judul kolom harga yang dibayar pembeli: "Harga Reseller" / "Harga Agen" /
 * nama grup kustom — mengikuti level pembeli. Tanpa level terbaca → "Harga
 * Nett" (istilah grosir untuk harga setelah potongan). Dipakai bersama nota
 * tagihan & surat jalan, jadi tinggal di sini bukan di salah satu pencetak.
 */
export function levelPriceHeader(level: string | null | undefined): string {
  const l = (level ?? "").trim();
  return l ? `Harga ${l}` : "Harga Nett";
}

/**
 * Potongan baris dipecah jadi VOUCHER dan DISKON LEVEL — per pcs, Rupiah.
 *
 * Kenapa ada: `unitDiscountRp` memulangkan SELURUH jarak harga umum → harga
 * bayar, dan potongan voucher sudah terlipat ke harga bayar (`unit_price`,
 * mig 5610). Begitu dokumen memajang nominal voucher DI SAMPING kolom Diskon,
 * rupiah yang sama terbaca dua kali — audit 2026-09-11 menemukannya di kolom
 * Voucher PDF nota yang baru (mig 5690 / permintaan pemilik). Yang benar adalah
 * rantai TIGA angka sesuai keputusan pemilik Q1:
 *
 *     harga umum − potongan voucher − diskon level = harga bayar
 *
 * Contoh hidup: umum 100.000, voucher 12.500/pcs, diskon level 10 % →
 * bayar `round((100.000 − 12.500) × 0,9)` = 78.750; voucher 12.500; level
 * 8.750. Persen level dihitung atas basis PASCA-voucher (87.500), karena itu
 * basis yang benar-benar dipotong level — bukan atas harga umum.
 *
 * `voucherRp` diklem ke potongan yang sungguh ada: baris yang harganya
 * dinego di bawah snapshot voucher tak boleh memulangkan level negatif.
 */
export type LineDiscountSplit = {
  /** Potongan total per pcs = `unitDiscountRp` (voucher + promo + level + bundel). */
  totalRp: number;
  /** Bagian voucher produk per pcs (0 bila baris tak ber-voucher). */
  voucherRp: number;
  /**
   * Sisanya sesudah voucher: promo + diskon level/nego (+ BUNDEL, mig 8040)
   * per pcs. Sengaja tetap "semua sesudah voucher" — kolom Diskon PDF & surat
   * jalan memajangnya di samping kolom Voucher dan keduanya wajib berjumlah
   * persis potongan baris; suku-sukunya dipisah `promoRp` / `levelOnlyRp` /
   * `bundleRp`.
   */
  levelRp: number;
  /** Persen `levelRp` atas basis pasca-voucher (0–100). */
  levelPct: number;
  /**
   * Bagian PROMO KAMPANYE dari `levelRp` — dari jejak potongan (mig 5920),
   * potongan pertama atas basis pasca-voucher (cermin rantai mesin voucher dua
   * tier `tier2(tier1(N − v))`). 0 bila jejak tak tercatat.
   */
  promoRp: number;
  /** Persen promo atas basis pasca-voucher (0–100). */
  promoPct: number;
  /** `levelRp − promoRp − bundleRp`: diskon level/nego murni per pcs. */
  levelOnlyRp: number;
  /** Persen `levelOnlyRp` atas basis pasca-voucher & pasca-promo (0–100). */
  levelOnlyPct: number;
  /**
   * Bagian BUNDEL per pcs (mig 8040) — langkah TERAKHIR rantai, jadi bagian
   * dari `levelRp`. 0 untuk baris tanpa jejak bundel. Lihat
   * `orderItemBundleDiscountPerPcs`.
   */
  bundleRp: number;
};

// Bagian potongan BUNDEL per pcs (mig 8040) — rumusnya tinggal di
// bundleOrders.ts (bebas impor) supaya cartItemPrice.ts bisa memakainya tanpa
// siklus impor; diteruskan dari sini untuk pembaca nota.
export { orderItemBundleDiscountPerPcs } from "@/lib/bundleOrders";

/** Satu potongan jejak atas sebuah basis — rumus & pembulatan = `applyPromo`. */
function applyTraceCut(base: number, cut: { mode: "persen" | "nominal"; value: number }): number {
  const v = Number.isFinite(cut.value) ? Math.max(0, cut.value) : 0;
  return Math.max(0, Math.round(cut.mode === "nominal" ? base - v : base * (1 - v / 100)));
}

export function lineDiscountSplit(line: {
  kind: InvoiceLineKind;
  qty: number | null;
  amount: number;
  unitNormalPrice: number | null;
  unitPriceExtra?: number | null;
  voucherDiscountPerPcs?: number | null;
  /** Jejak potongan (mig 5920) — memisahkan bagian promo dari diskon level. */
  discountTrace?: DiscountTraceRow | null;
  /**
   * Bagian BUNDEL per pcs (mig 8040, `orderItemBundleDiscountPerPcs`). Absen /
   * null / 0 = baris tanpa jejak bundel → hasil persis seperti sebelumnya.
   */
  bundleDiscountPerPcs?: number | null;
}): LineDiscountSplit | null {
  const totalRp = unitDiscountRp(line);
  if (totalRp === null) return null;
  // BUNDEL dihitung LEBIH DULU dan diklem ke potongan yang terlihat di baris
  // nota (nominal nota boleh disunting admin, jadi tak selalu = qty ×
  // unit_price) — barulah sisanya dipecah jadi voucher / promo / level.
  const rawBundle = Number(line.bundleDiscountPerPcs ?? 0);
  const bundleRp =
    Number.isFinite(rawBundle) && rawBundle > 0 ? Math.min(Math.round(rawBundle), totalRp) : 0;
  const raw = Number(line.voucherDiscountPerPcs ?? 0);
  const voucherRp =
    Number.isFinite(raw) && raw > 0 ? Math.min(Math.round(raw), totalRp - bundleRp) : 0;
  const levelRp = Math.max(0, totalRp - voucherRp);
  const base = Math.max(0, (line.unitNormalPrice ?? 0) - voucherRp);
  // Diklem ke `levelRp` (tanpa bagian bundel): baris yang harganya dinego di
  // bawah rantai tak boleh memulangkan diskon level negatif.
  const promoCut = cutsFromTrace(line.discountTrace).find((c) => c.source === "promo") ?? null;
  const promoRp = promoCut
    ? Math.min(levelRp - bundleRp, Math.max(0, base - applyTraceCut(base, promoCut)))
    : 0;
  const levelOnlyRp = Math.max(0, levelRp - bundleRp - promoRp);
  const base2 = Math.max(0, base - promoRp);
  return {
    totalRp,
    voucherRp,
    levelRp,
    levelPct: base > 0 ? (levelRp / base) * 100 : 0,
    promoRp,
    promoPct: base > 0 ? (promoRp / base) * 100 : 0,
    levelOnlyRp,
    levelOnlyPct: base2 > 0 ? (levelOnlyRp / base2) * 100 : 0,
    bundleRp
  };
}

/**
 * Rupiah voucher per pcs yang MASIH perlu dicetak sebagai angka tersendiri —
 * `null` berarti JANGAN cetak.
 *
 * Detail Tagihan sudah memasang pil bernama (`🎟 <nama voucher> −Rp x/pcs`)
 * di judul baris sejak mig 5610. Fase 11 lalu menambahkan angka voucher KEDUA
 * untuk memisahkannya dari diskon level, supaya "hemat" tak terbaca dua kali.
 * Pada nota yang TIDAK punya diskon level — mayoritas — pemisahan itu tidak
 * memisahkan apa pun: ia mencetak rupiah yang sama dua kali, berdampingan.
 * Laporan pemilik 2026-09-11 atas nota #3137 Urfi Group ("menampilkan 2
 * voucher … buat agar menampilkan hanya 1 voucher saja").
 *
 * Aturannya satu kalimat: **pil bernama adalah pemegang angka voucher.** Yang
 * lain hanya mencetaknya kalau pil itu tidak ada — baris warisan tanpa
 * `voucher_name` tetap memperlihatkan potongannya alih-alih menghilang. Di PROD
 * hari ini nol baris seperti itu (6 baris ber-voucher, semuanya bernama), jadi
 * cabang ini murni jaring pengaman.
 */
export function lineVoucherAmountToPrint(
  split: LineDiscountSplit | null,
  opts: { hasVoucherName: boolean }
): number | null {
  if (!split || split.voucherRp <= 0) return null;
  return opts.hasVoucherName ? null : split.voucherRp;
}

export type LineDiscountPart = {
  kind: "voucher" | "promo" | "level" | "bundle";
  label: string;
  perPcsRp: number;
  /** Persen atas basisnya; null untuk voucher (nominal per pcs). */
  pct: number | null;
};

/**
 * Suku potongan PER PCS sebuah baris, urut rantai umum − voucher − promo −
 * level − bundel = bayar — label sama dengan pil Detail Tagihan pembeli. Dipakai baris
 * "Normal Rp … · …" di Rincian Detail Nota admin, yang sejak permintaan pemilik
 * 2026-10-19 tak lagi mencetak total "hemat Rp … (x%)": tanpa suku-suku ini,
 * baris berdiskon level saja tak menyisakan satu angka potongan pun.
 * Voucher hanya bila `voucherToPrint` tak null (pil bernama pemegang angkanya,
 * `lineVoucherAmountToPrint`). "diskon level" bila promo ikut tampil.
 */
export function lineDiscountParts(
  split: LineDiscountSplit | null,
  voucherToPrint: number | null
): LineDiscountPart[] {
  if (!split) return [];
  const parts: LineDiscountPart[] = [];
  if (voucherToPrint !== null && voucherToPrint > 0) {
    parts.push({ kind: "voucher", label: "voucher", perPcsRp: voucherToPrint, pct: null });
  }
  if (split.promoRp > 0) {
    parts.push({ kind: "promo", label: "promo", perPcsRp: split.promoRp, pct: split.promoPct });
  }
  if (split.levelOnlyRp > 0) {
    parts.push({
      kind: "level",
      label: split.promoRp > 0 ? "diskon level" : "diskon",
      perPcsRp: split.levelOnlyRp,
      pct: split.levelOnlyPct
    });
  }
  // Bundel (mig 8040): nominal per pcs — harga bundel dialokasikan ke baris,
  // bukan persen setelan, jadi tak ada persen yang jujur untuk dicetak.
  if (split.bundleRp > 0) {
    parts.push({ kind: "bundle", label: "bundling", perPcsRp: split.bundleRp, pct: null });
  }
  return parts;
}

/**
 * Baris ini punya harga normal (snapshot) yang layak dicetak sebagai pembanding?
 * Tinggal di sini bersama `lineDiscount` sejak surat jalan ikut mencetak kolom
 * "Harga Jual Umum" & "Diskon" (2026-09) — `invoicePdf` meneruskan ekspornya.
 */
export function hasNormalPrice(line: {
  unitNormalPrice: number | null | undefined;
}): boolean {
  return line.unitNormalPrice != null && line.unitNormalPrice > 0;
}

/**
 * Potongan per PCS (Rp) = harga normal satuan − harga satuan yang dibayar.
 * Dibulatkan per pcs, jadi `unitDiscountRp × qty` bisa meleset beberapa rupiah
 * dari `discountRp` baris; yang otoritatif tetap Subtotal (nominal ditagih),
 * dan dokumen yang mencetak kolom ini wajib menyebutkan pembulatannya.
 */
export function unitDiscountRp(line: {
  kind: InvoiceLineKind;
  qty: number | null;
  amount: number;
  unitNormalPrice: number | null;
  unitPriceExtra?: number | null;
}): number | null {
  const disc = lineDiscount(line);
  if (!disc) return null;
  const qty = line.qty ?? 0;
  // Per pcs yang dibayar TANPA tambahan (mig 7900) — potongan atas harga umum.
  const unitPaid = qty > 0 ? Math.round(disc.payBaseTotal / qty) : 0;
  return Math.max(0, disc.normalUnit - unitPaid);
}

/**
 * True bila baris nota SUDAH membawa potongan harga yang nyata — nominalnya
 * lebih kecil dari harga normal × qty, dan bukan nol.
 *
 * Dipakai editor ACC & Edit Nota untuk memutuskan boleh-tidaknya menimpa diskon
 * sebuah baris dengan setelan HARI INI (`fetchLevelDiscountDefaults`). Potongan
 * yang sudah menempel berasal dari harga yang benar-benar dilihat & disetujui
 * pembeli saat memesan: ia di-snapshot ke `order_items.unit_price` lalu disalin
 * ke `invoice_lines.amount`. Menimpanya berarti menaikkan tagihan diam-diam
 * ketika keadaan berubah setelah pesanan dibuat —
 *   · kampanye PROMO (mig 3470) berakhir → potongan kampanye hilang, padahal
 *     keputusan pemilik adalah "harga terkunci saat memesan";
 *   · level reseller pembeli naik/turun sejak ia memesan;
 *   · admin sudah menegosiasikan harga khusus di baris itu.
 * Tak satu pun dari itu memunculkan peringatan di layar, jadi satu-satunya
 * penjaganya adalah tidak menyentuh baris yang sudah berharga.
 *
 * `amount = 0` SENGAJA dianggap "belum berharga" (harga menyusul), bukan
 * "diskon 100%" — justru baris itulah yang memang menunggu diisi admin, dan
 * prefill diciptakan untuknya.
 */
export function lineHasSettledDiscount(line: {
  kind: InvoiceLineKind;
  qty?: number | null;
  amount: number;
  unitNormalPrice?: number | null;
  unitPriceExtra?: number | null;
}): boolean {
  const d = lineDiscount(line);
  if (!d) return false;
  return d.payTotal > 0 && d.discountRp > 0;
}

/**
 * Total HEMAT sebuah nota di mata pembeli = potongan harga pada baris produk
 * (harga normal − yang dibayar, `lineDiscount`) + baris diskon/promo eksplisit
 * (`kind = "discount"`, magnitudonya). Baris dibatalkan tak ikut; baris produk
 * bernominal 0 (harga menyusul) BUKAN hemat 100% (gerbang yang sama dengan
 * `lineHasSettledDiscount`); kredit DP bukan hemat (itu pembayaran yang sudah
 * dilakukan). Dipakai badge "Anda telah hemat" di daftar tagihan pembeli.
 */
export function computeInvoiceSavings(
  lines: ReadonlyArray<{
    kind: InvoiceLineKind;
    qty?: number | null;
    amount: number;
    unitNormalPrice?: number | null;
    unitPriceExtra?: number | null;
    cancelledAt?: string | null;
  }>
): number {
  let total = 0;
  for (const l of lines) {
    if (l.cancelledAt) continue;
    if (l.kind === "product_item") {
      const d = lineDiscount(l);
      if (d && d.payTotal > 0) total += d.discountRp;
    } else if (l.kind === "discount") {
      total += Math.abs(Number.isFinite(l.amount) ? l.amount : 0);
    }
  }
  return total;
}

/** Σ `computeInvoiceSavings` beberapa nota (mis. nota lunas yang tampil pada satu periode). */
export function sumInvoicesSavings(
  invoices: ReadonlyArray<{ lines: Parameters<typeof computeInvoiceSavings>[0] }>
): number {
  return invoices.reduce((sum, inv) => sum + computeInvoiceSavings(inv.lines), 0);
}

/**
 * Harga akhir baris dari potongan yang diketik admin.
 *   nominal → amount = normalTotal − value (Rp)
 *   persen  → amount = normalTotal × (1 − value/100)
 * Clamp ≥ 0, dibulatkan; persen di-clamp 0–100. `extraTotal` (mig 7900) =
 * tambahan baris × qty yang ditempel SESUDAH potongan: amount = cut(normal) + extra.
 */
export function discountToAmount(
  normalTotal: number,
  mode: DiscountMode,
  value: number,
  extraTotal = 0
): number {
  const base = Math.max(0, Number.isFinite(normalTotal) ? normalTotal : 0);
  const v = Number.isFinite(value) ? Math.max(0, value) : 0;
  const extra = Number.isFinite(extraTotal) ? Math.max(0, Math.round(extraTotal)) : 0;
  if (mode === "persen") {
    const pct = Math.min(100, v);
    return Math.max(0, Math.round(base * (1 - pct / 100))) + extra;
  }
  return Math.max(0, Math.round(base - v)) + extra;
}

/**
 * Diskon (Rp & %) dari harga normal total & harga akhir — prefill editor admin.
 * `extraTotal` (mig 7900) dikeluarkan dari `amount` dulu: potongan dinilai atas
 * bagian yang lahir dari harga umum.
 */
export function amountToDiscount(
  normalTotal: number,
  amount: number,
  extraTotal = 0
): { rp: number; pct: number } {
  const nt = Math.max(0, Number.isFinite(normalTotal) ? normalTotal : 0);
  const extra = Number.isFinite(extraTotal) ? Math.max(0, extraTotal) : 0;
  const amt = Math.max(0, (Number.isFinite(amount) ? amount : 0) - extra);
  const rp = Math.max(0, nt - amt);
  const pct = nt > 0 ? (rp / nt) * 100 : 0;
  return { rp, pct };
}

/** Format persen diskon: bilangan bulat tanpa desimal, selain itu 1 desimal. */
export function formatDiscountPct(pct: number): string {
  const rounded = Math.round((Number.isFinite(pct) ? pct : 0) * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
}

/**
 * Bersihkan deskripsi baris nota product_item dari sufiks varian default
 * "Standar" (produk tanpa variasi / produk manual mig 121): snapshot baris
 * berformat "Nama Produk — Nama Varian", jadi "Produk Manual — Standar" →
 * "Produk Manual". Hanya sufiks varian default (didahului tanda pisah) yang
 * dibuang; deskripsi lain (varian nyata, ongkir, manual) dibiarkan apa adanya.
 */
export function cleanInvoiceLineDescription(description: string): string {
  const cleaned = (description ?? "").replace(/\s*[—–-]\s*standar\s*$/i, "").trim();
  return cleaned || description;
}

// ─── Gabungkan baris ber-SKU sama (tampilan tagihan) ─────────────────────────
// Tiap baris product_item lahir dari satu order_item; satu reseller bisa punya
// beberapa order_item dengan VARIAN (SKU) yang sama dalam satu nota — mis. 1 pcs
// + 2 pcs untuk varian yang sama. Untuk tampilan tagihan kita gabungkan baris
// ber-SKU sama menjadi SATU baris (qty/nominal/pcs-lunas dijumlah), mengikuti
// "Produk A Merah 1pcs + Produk A Merah 2pcs = Produk A Merah 3pcs".
//
// `description` baris product_item adalah snapshot "Nama Produk — Nama Varian"
// (lihat 0720_invoice_acc_allocation_rpc.sql) sehingga unik per varian = per SKU;
// memakainya sebagai kunci aman walau produk sudah non-aktif (SKU tak terbaca
// reseller via RLS). Baris dibatalkan & baris non-produk (ongkir/manual/kredit
// DP) TIDAK digabung. Urutan kemunculan pertama dipertahankan.

export type ConsolidatableLine = {
  // string (bukan InvoiceLineKind) agar kompatibel dgn ReceiptLine.kind; cek
  // internal hanya membandingkan literal "product_item".
  kind: string;
  description: string;
  qty?: number | null;
  amount: number;
  paidQty?: number;
  unitNormalPrice?: number | null;
  /**
   * Snapshot tambahan per pcs (mig 7900). Ikut KUNCI penggabungan (bughunt
   * D14-33): baris ber-tambahan berbeda (1 pcs pra-7900 + 2 pcs @ 20.000) tak
   * dilebur jadi "tambahan 13.333/pcs" — tiap baris tampil dengan rinciannya.
   */
  unitPriceExtra?: number | null;
  /**
   * Bagian BUNDEL per pcs (mig 8040) + nama bundelnya. Ikut KUNCI
   * penggabungan: baris bundel tak dilebur dengan baris biasa varian yang sama
   * (satu pesanan bisa memuat keduanya — checkout bundel mengelompokkan per
   * produk), dan dua bundel berpotongan beda tak dirata-rata.
   */
  bundleDiscountPerPcs?: number | null;
  bundleName?: string | null;
  cancelledAt?: string | null;
  /** Pcs yang barangnya sudah ready (`invoiceLineReadyQty`) — opsional. */
  readyQty?: number | null;
};

export function consolidateInvoiceLines<L extends ConsolidatableLine>(
  lines: ReadonlyArray<L>
): Array<L & { mergedCount: number }> {
  const out: Array<L & { mergedCount: number }> = [];
  const idxByKey = new Map<string, number>();
  // Akumulator harga normal per entri tergabung: jumlah normalTotal tiap baris
  // sumber agar diskon tetap benar walau harga normal/pcs antar baris berbeda.
  const normalAcc = new Map<number, { sum: number; anyNull: boolean }>();
  // Tambahan (mig 7900) berbobot qty — null/absen = 0. Sejak D14-33 baris yang
  // dilebur selalu ber-tambahan sama, jadi rata-ratanya = tambahan itu sendiri.
  const extraAcc = new Map<number, number>();

  for (const line of lines) {
    const mergeable = line.kind === "product_item" && !line.cancelledAt;
    if (!mergeable) {
      out.push({ ...line, mergedCount: 1 });
      continue;
    }

    // Tambahan 0/absen → kunci lama persis (deskripsi saja).
    const extraKey = Math.max(0, Number(line.unitPriceExtra ?? 0) || 0);
    const baseKey = extraKey > 0 ? `${line.description}\n+tambahan ${extraKey}` : line.description;
    // Baris tanpa jejak bundel → kunci lama persis.
    const key =
      line.bundleDiscountPerPcs != null
        ? `${baseKey}\n+bundling ${(line.bundleName ?? "").trim()}:${Math.round(Number(line.bundleDiscountPerPcs) || 0)}`
        : baseKey;
    const existingIdx = idxByKey.get(key);
    const un = line.unitNormalPrice ?? null;

    if (existingIdx === undefined) {
      const idx = out.length;
      idxByKey.set(key, idx);
      out.push({ ...line, mergedCount: 1 });
      normalAcc.set(idx, {
        sum: un == null ? 0 : un * (line.qty ?? 0),
        anyNull: un == null
      });
      extraAcc.set(idx, Math.max(0, Number(line.unitPriceExtra ?? 0) || 0) * (line.qty ?? 0));
      continue;
    }

    const cur = out[existingIdx];
    cur.mergedCount += 1;
    cur.qty = (cur.qty ?? 0) + (line.qty ?? 0);
    cur.amount += Number.isFinite(line.amount) ? line.amount : 0;
    if (cur.paidQty != null || line.paidQty != null) {
      cur.paidQty = (cur.paidQty ?? 0) + (line.paidQty ?? 0);
    }
    // Pcs ready dijumlah (pecahan satu varian bisa sebagian ready). Satu
    // sumber tak diketahui (null) → entri gabungan tak diketahui: badge
    // status barang tidak dikarang dari separuh data.
    if (cur.readyQty !== undefined || line.readyQty !== undefined) {
      cur.readyQty =
        cur.readyQty == null || line.readyQty == null
          ? null
          : cur.readyQty + line.readyQty;
    }

    const acc = normalAcc.get(existingIdx);
    if (acc) {
      if (un == null) acc.anyNull = true;
      else acc.sum += un * (line.qty ?? 0);
    }
    extraAcc.set(
      existingIdx,
      (extraAcc.get(existingIdx) ?? 0) + Math.max(0, Number(line.unitPriceExtra ?? 0) || 0) * (line.qty ?? 0)
    );
  }

  // Finalisasi harga normal/pcs entri tergabung: turunkan dari Σ(normalTotal)/qty
  // (berbobot qty). Bila ada baris tanpa harga normal → kosongkan (harga menyusul).
  for (const [idx, acc] of normalAcc) {
    const entry = out[idx];
    if (entry.mergedCount <= 1) continue;
    const qty = entry.qty ?? 0;
    entry.unitNormalPrice =
      acc.anyNull || qty <= 0 ? null : Math.round(acc.sum / qty);
    if (entry.unitPriceExtra !== undefined) {
      entry.unitPriceExtra = qty > 0 ? Math.round((extraAcc.get(idx) ?? 0) / qty) : null;
    }
  }

  return out;
}

// ─── Status barang per baris produk (Ready / Belum Ready) ────────────────────
// Badge di layar nota pembeli (`DetailLineRow`, InvoiceDetailClient) —
// permintaan pemilik 2026-10-17: pembeli melihat baris mana yang barangnya
// sudah ada tanpa membuka keranjang.

/**
 * `keep_status` baris pesanan yang berarti BARANGNYA SUDAH ADA — cermin
 * `order_goods_all_ready` (mig 7130) & `orderGoodsAllReady` (dp.ts): kolom
 * Sudah Ready (`unpaid_keep`/`paid_keep`/`hold`) plus yang sudah diajukan
 * kirim / dikirim. `keep_status` kosong = Belum Ready (PO masih jalan).
 */
const GOODS_READY_KEEP_STATUSES: ReadonlySet<string> = new Set([
  "unpaid_keep",
  "paid_keep",
  "hold",
  "requested_shipment",
  "shipped"
]);

/**
 * Pcs baris nota yang barangnya sudah ready: `qty` bila ready, 0 bila Belum
 * Ready (`keep_status` kosong), `null` bila tak berlaku — bukan baris produk,
 * baris dibatalkan, tanpa order_item, atau keep `cancelled` (barangnya tak
 * akan datang; menyebutnya "Belum Ready" berarti menjanjikannya).
 *
 * Paket yang MENUNGGU BARANG (`requested_shipment` ber-`ready_at` NULL, mig
 * 7760 — cermin `isWaitingPackageRow`) = Belum Ready. `readyAt` undefined =
 * payload tak membawa kolomnya → dibaca seperti dulu (ready), pola
 * `orderGoodsAllReady` (dp.ts).
 */
export function invoiceLineReadyQty(line: {
  kind: string;
  qty?: number | null;
  orderItemId: string | null;
  keepStatus: string | null;
  readyAt?: string | null;
  cancelledAt?: string | null;
}): number | null {
  if (line.kind !== "product_item" || line.cancelledAt || !line.orderItemId) {
    return null;
  }
  if (line.keepStatus == null) return 0;
  if (line.keepStatus === "requested_shipment" && line.readyAt === null) return 0;
  return GOODS_READY_KEEP_STATUSES.has(line.keepStatus)
    ? Math.max(0, line.qty ?? 0)
    : null;
}

export type InvoiceReadinessSummary = {
  /** Pcs yang barangnya sudah ada tapi BELUM berangkat. */
  readyPcs: number;
  /** Pcs Belum Ready (PO masih jalan / paket menunggu barang). */
  notReadyPcs: number;
  /** Pcs yang paketnya sudah berangkat (`keep_status = shipped`). */
  shippedPcs: number;
  /** Penyebut ketiga badge = readyPcs + notReadyPcs + shippedPcs. */
  totalPcs: number;
};

/**
 * Ringkasan status barang SATU nota untuk kartu daftar tagihan admin
 * (`/admin/invoices/reseller/[customerId]`, permintaan pemilik 2026-10-22):
 * badge "Ready r/n", "Belum Ready b/n" & "Dikirim t/n" dalam pcs — tiga
 * kelompok yang tak tumpang-tindih. "Dikirim" = paket sudah berangkat
 * (`mark_shipment_shipped` satu-satunya penulis `keep_status = shipped`),
 * BUKAN "diterima" — keadaan itu milik paket, tak tercatat per item. Hanya
 * baris yang `invoiceLineReadyQty`-nya berlaku yang dihitung — baris batal,
 * non-produk, dan keep `cancelled` tak masuk penyebut. null = tak ada baris
 * berlaku (nota DP / ongkir / manual saja) → kartu tanpa badge.
 */
export function invoiceReadinessSummary(
  lines: ReadonlyArray<Parameters<typeof invoiceLineReadyQty>[0]>
): InvoiceReadinessSummary | null {
  let readyPcs = 0;
  let shippedPcs = 0;
  let totalPcs = 0;
  for (const line of lines) {
    const ready = invoiceLineReadyQty(line);
    if (ready == null) continue;
    const qty = Math.max(0, line.qty ?? 0);
    totalPcs += qty;
    if (line.keepStatus === "shipped") shippedPcs += Math.min(ready, qty);
    else readyPcs += Math.min(ready, qty);
  }
  if (totalPcs <= 0) return null;
  return {
    readyPcs,
    notReadyPcs: totalPcs - readyPcs - shippedPcs,
    shippedPcs,
    totalPcs
  };
}

/**
 * Ringkasan status barang SATU KARTU, yang bisa mewakili beberapa nota (kartu
 * batch "Bayar Sekaligus · N nota" di tab Perlu Tindakan): badge wajib
 * menghitung SEMUA anggota, bukan nota perwakilan saja — kalau tidak, "Ready
 * 2/2" milik nota pertama terbaca sebagai status seluruh batch. Hanya nota
 * barang yang tak diarsip yang dihitung; nota yang muncul dua kali (dua setoran
 * tertunda dalam satu batch) dihitung sekali.
 */
export function invoicesReadinessSummary(
  invoices: ReadonlyArray<{
    id: string;
    kind: InvoiceKind;
    status: InvoiceStatus;
    lines: ReadonlyArray<Parameters<typeof invoiceLineReadyQty>[0]>;
  }>
): InvoiceReadinessSummary | null {
  const seen = new Set<string>();
  const lines: Parameters<typeof invoiceLineReadyQty>[0][] = [];
  for (const inv of invoices) {
    if (seen.has(inv.id)) continue;
    seen.add(inv.id);
    if (inv.kind !== "product" || invoiceIsArchived(inv.status)) continue;
    lines.push(...inv.lines);
  }
  return invoiceReadinessSummary(lines);
}

export type InvoiceLineReadiness = {
  tone: "ready" | "belum_ready" | "partial";
  label: string;
};

/**
 * Badge status barang dari pcs ready (`invoiceLineReadyQty`, dijumlah
 * `consolidateInvoiceLines` saat pecahan satu varian digabung) atas qty baris:
 * semua → "Ready", nol → "Belum Ready", sebagian → "x/y pcs ready" (pola badge
 * "x/y pcs lunas"). null = tak berlaku / tak diketahui → tanpa badge.
 */
export function invoiceLineReadinessBadge(
  readyQty: number | null | undefined,
  qty: number | null | undefined
): InvoiceLineReadiness | null {
  const total = Math.max(0, qty ?? 0);
  if (readyQty == null || total <= 0) return null;
  const ready = Math.min(Math.max(0, readyQty), total);
  if (ready >= total) return { tone: "ready", label: "Ready" };
  if (ready <= 0) return { tone: "belum_ready", label: "Belum Ready" };
  return { tone: "partial", label: `${ready}/${total} pcs ready` };
}

// ─── Ringkasan komponen biaya nota ────────────────────────────────────────────
// Kelompokkan baris AKTIF sebuah nota menjadi komponen tampilan: Subtotal
// Produk → Ongkir → Biaya Dropship/Packing → Biaya Lainnya → Kredit DP →
// Diskon → Total. Baris ongkir/biaya DS berjenis 'manual' (combo mig 162/205)
// atau 'shipment_due' (nota ongkir) dikenali dari deskripsi konvensional
// ('Ongkir …' / 'Biaya Dropship/Packing') supaya rincian nota mudah dibaca.

/** Deskripsi baku baris biaya dropship otomatis (mig 105/164/205). */
export const DROPSHIP_FEE_LABEL = "Biaya Dropship/Packing";

type ChargeLike = {
  kind: InvoiceLineKind;
  description: string;
};

/** Baris ongkir: kind `shipment_due`, atau baris manual berdeskripsi "Ongkir…". */
/**
 * Pola nama kurir yang berdiri SENDIRI sebagai deskripsi baris ongkir manual
 * (bughunt J4-7). CERMIN PERSIS `public.is_ongkir_charge_line` (mig 5330) —
 * kalau salah satu sisi bergeser, satu jenis uang kembali punya dua perlakuan.
 *
 * `\b` di ujung WAJIB: tanpanya `^pos` menelan "Posting IG" dan `^ongkir`
 * menelan "Ongkirim". Diuji lebih dulu terhadap SELURUH baris manual di PROD —
 * tepat 25 baris baru tertangkap, semuanya nama kurir.
 */
const ONGKIR_DESC_RE =
  /^(ongkir|ongkos|jnt|j&t|tiki|ncs|pos|jne|sicepat|id ?truck|id ?express|indah ?cargo|wahana|lion|anteraja)\b/i;

export function isOngkirChargeLine(line: ChargeLike): boolean {
  if (line.kind === "shipment_due") return true;
  return line.kind === "manual" && ONGKIR_DESC_RE.test(line.description.trim());
}

/** Baris Biaya Dropship/Packing ("biaya DS") — hanya berjenis manual. */
export function isDropshipFeeLine(line: ChargeLike): boolean {
  if (line.kind !== "manual") return false;
  const desc = line.description.trim().toLowerCase();
  return desc.includes("dropship") || /^biaya ds\b/.test(desc);
}

/**
 * Baris POKOK nota DP: manual berdeskripsi "DP — <nama produk>" (mig 099/126).
 *
 * Dipisahkan dari "biaya lainnya" karena baris inilah SELURUH isi nota DP —
 * menyebutnya "Biaya Lainnya" di ringkasan, PDF bukti lunas, dan rekap WA
 * membuat satu-satunya angka pada nota itu terbaca seperti biaya tambahan
 * misterius. Diperiksa PALING DULU di antara pemeriksa baris manual: nota DP
 * produk yang namanya memuat kata "dropship" akan tertangkap
 * {@link isDropshipFeeLine} lebih dulu dan terhitung sebagai biaya packing.
 */
export function isDpChargeLine(line: ChargeLike): boolean {
  return line.kind === "manual" && DP_LINE_PREFIX.test(line.description ?? "");
}

/** Label badge baris nota — memecah 'manual' jadi DP / Ongkir / Biaya DS / Lainnya. */
export function invoiceLineBadgeLabel(line: ChargeLike): string {
  if (line.kind === "manual") {
    if (isDpChargeLine(line)) return "DP";
    if (isOngkirChargeLine(line)) return "Ongkir";
    if (isDropshipFeeLine(line)) return "Biaya DS";
  }
  return INVOICE_LINE_KIND_LABEL[line.kind];
}

export type InvoiceChargesSummary = {
  /** Σ nominal baris product_item aktif. */
  productTotal: number;
  /** Σ pcs baris product_item aktif (qty null dihitung 0). */
  productQty: number;
  ongkirTotal: number;
  dropshipFeeTotal: number;
  /** Σ baris pokok nota DP ("DP — <produk>") — bukan "biaya lainnya". */
  dpTotal: number;
  /** Σ baris manual lain (penyesuaian, biaya tambahan, dll). */
  otherFeeTotal: number;
  /** Magnitudo kredit DP aktif (≥ 0; ditampilkan minus hijau). */
  dpCreditTotal: number;
  /**
   * Magnitudo diskon manual/promo aktif (≥ 0; ditampilkan minus hijau) —
   * TANPA baris voucher ongkir (itu `voucherOngkirTotal`).
   */
  discountTotal: number;
  /**
   * VOUCHER (mig 5610), dua baris ringkasan TERPISAH:
   * · produk = Σ potongan per pcs × qty baris produk aktif (sudah terlipat ke
   *   nominal baris, jadi TIDAK ikut mengurangi total lagi — informasi);
   * · ongkir = Σ |baris discount ber-usage| aktif (memang mengurangi total).
   */
  voucherProductTotal: number;
  /**
   * SEMUA nama voucher produk yang menyumbang potongan di nota ini, urut
   * kemunculan baris. Biasanya satu; bisa DUA atau lebih karena satu pesanan
   * bisa memegang lebih dari satu usage — pembeli checkout produk yang sama dua
   * kali, masing-masing dengan voucher berbeda, dan checkout kedua MELEBUR ke
   * pesanan yang sama (`checkout_orders_with_voucher`). Versi lama menyimpan
   * satu nama (`??=`) sementara nominalnya dijumlah semua, jadi nota bisa
   * berbunyi "Potongan Voucher Produk (V1)" untuk angka V1 + V2 (audit
   * 2026-09-10, dibuktikan probe DB offline).
   */
  voucherProductNames: string[];
  voucherOngkirTotal: number;
  /** Idem untuk voucher ongkir (satu nota paket normalnya satu usage). */
  voucherOngkirNames: string[];
  /** Total tagihan (cermin computeInvoiceTotal — clamp ≥ 0). */
  total: number;
  /** Ada komponen selain produk → tampilkan rincian bertingkat. */
  hasBreakdown: boolean;
};

/**
 * Rupiah voucher yang BENAR-BENAR terlipat ke nominal satu baris produk —
 * bahan "Termasuk potongan voucher produk".
 *
 * Biasanya = per pcs × qty (pecahan, dibulatkan sekali oleh pemanggil). Tapi
 * nominal baris boleh disunting admin (ACC / Edit Nota, kasus D16-9): baris
 * yang ditagih DI ATAS `umum − voucher` tak lagi memuat voucher penuh. Maka
 * voucher dibatasi ke potongan nyata baris (harga umum × qty − nominal tanpa
 * tambahan, dikurangi bagian bundel) — angka yang SAMA dengan pil voucher di
 * Rincian & kolom Voucher PDF (`lineDiscountSplit`). Selisih ≤ Rp 1 dibiarkan:
 * itu sisa pembulatan alokasi, bukan suntingan. Baris tanpa harga umum tak bisa
 * diperiksa → apa adanya.
 */
function productVoucherTotal(
  line: {
    kind: InvoiceLineKind;
    qty?: number | null;
    amount: number;
    unitNormalPrice?: number | null;
    unitPriceExtra?: number | null;
    bundleDiscountPerPcs?: number | null;
  },
  perPcs: number
): number {
  const qty = line.qty ?? 0;
  const recorded = perPcs * qty;
  const d = lineDiscount(line);
  if (!d) return recorded;
  const rawBundle = Number(line.bundleDiscountPerPcs ?? 0);
  const bundleTotal =
    Number.isFinite(rawBundle) && rawBundle > 0
      ? Math.min(Math.round(rawBundle) * qty, d.discountRp)
      : 0;
  const cap = Math.max(0, d.discountRp - bundleTotal);
  return recorded > cap + 1 ? cap : recorded;
}

export function summarizeInvoiceCharges(
  lines: ReadonlyArray<{
    kind: InvoiceLineKind;
    description: string;
    qty?: number | null;
    amount: number;
    cancelledAt?: string | null;
    /** Baris `discount` milik voucher ongkir (mig 5610). */
    voucherUsageId?: string | null;
    /** Jejak voucher produk pada baris produk (mig 5610). */
    voucherName?: string | null;
    voucherDiscountPerPcs?: number | null;
    /** Harga umum + tambahan + bagian bundel baris — pembatas voucher (`productVoucherTotal`). */
    unitNormalPrice?: number | null;
    unitPriceExtra?: number | null;
    bundleDiscountPerPcs?: number | null;
  }>
): InvoiceChargesSummary {
  let productTotal = 0;
  let productQty = 0;
  let ongkirTotal = 0;
  let dropshipFeeTotal = 0;
  let dpTotal = 0;
  let otherFeeTotal = 0;
  let dpCreditTotal = 0;
  let discountTotal = 0;
  let voucherProductRaw = 0;
  const voucherProductNames: string[] = [];
  let voucherOngkirTotal = 0;
  const voucherOngkirNames: string[] = [];
  const pushName = (into: string[], name: string | null) => {
    const n = (name ?? "").trim();
    if (n && !into.includes(n)) into.push(n);
  };

  for (const l of lines) {
    if (l.cancelledAt) continue;
    const amount = Number.isFinite(l.amount) ? l.amount : 0;
    if (l.kind === "product_item") {
      productTotal += amount;
      productQty += l.qty ?? 0;
      // Nama didaftar untuk SETIAP baris ber-jejak voucher — cermin predikat
      // `invoiceLinesHaveVoucher` (invoicePdf) yang menyalakan kolom Voucher.
      // Dulu nama hanya masuk bila per-pcs > 0, jadi nota yang alokasinya
      // membulat ke nol pada satu baris memajang kolom tanpa satu pun nama.
      const perPcs = Number(l.voucherDiscountPerPcs ?? 0);
      const hasPerPcs = Number.isFinite(perPcs) && perPcs > 0 && (l.qty ?? 0) > 0;
      if (hasPerPcs || (l.voucherName ?? "").trim()) {
        pushName(voucherProductNames, l.voucherName ?? null);
      }
      // Dijumlah sebagai pecahan lalu DIBULATKAN SEKALI: per-pcs boleh pecahan
      // (alokasi ÷ qty), dan membulatkan tiap baris lebih dulu membuat satu
      // baris yang dipecah tiga meleset 1 rupiah dari `amount_granted`
      // (10.000 ÷ 3 → 3.333 × 3 = 9.999).
      if (hasPerPcs) voucherProductRaw += productVoucherTotal(l, perPcs);
    } else if (l.kind === "dp_credit") {
      dpCreditTotal += Math.abs(amount);
    } else if (l.kind === "discount" && l.voucherUsageId) {
      voucherOngkirTotal += Math.abs(amount);
      pushName(voucherOngkirNames, voucherNameFromLineDescription(l.description));
    } else if (l.kind === "discount") {
      discountTotal += Math.abs(amount);
    } else if (isDpChargeLine(l)) {
      dpTotal += amount;
    } else if (isOngkirChargeLine(l)) {
      ongkirTotal += amount;
    } else if (isDropshipFeeLine(l)) {
      dropshipFeeTotal += amount;
    } else {
      otherFeeTotal += amount;
    }
  }

  const voucherProductTotal = Math.round(voucherProductRaw);

  return {
    productTotal,
    productQty,
    ongkirTotal,
    dropshipFeeTotal,
    dpTotal,
    otherFeeTotal,
    dpCreditTotal,
    discountTotal,
    voucherProductTotal,
    voucherProductNames,
    voucherOngkirTotal,
    voucherOngkirNames,
    total: computeInvoiceTotal(lines),
    // Nota DP MURNI (hanya baris DP, tanpa komponen lain) sengaja TIDAK
    // ber-rincian: barisnya sama dengan totalnya, jadi menampilkan "DP: Rp X"
    // tepat di atas "Total: Rp X" hanya mengulang angka yang sama. Asal-usul
    // angkanya dijelaskan kartu Rincian DP, bukan di sini.
    hasBreakdown:
      ongkirTotal > 0 ||
      dropshipFeeTotal > 0 ||
      otherFeeTotal > 0 ||
      dpCreditTotal > 0 ||
      discountTotal > 0 ||
      voucherOngkirTotal > 0 ||
      voucherProductTotal > 0 ||
      (dpTotal > 0 && productTotal > 0)
  };
}

/**
 * Nama voucher untuk label ringkasan: "V1", "V1, V2", atau null bila tak ada.
 * Satu sumber supaya layar pembeli, layar admin, PDF, dan bukti lunas tak
 * pernah menyebut jumlah voucher yang berbeda dari nominalnya.
 */
export function voucherNamesLabel(names: readonly string[]): string | null {
  const list = names.map((n) => n.trim()).filter(Boolean);
  return list.length ? list.join(", ") : null;
}

/** Awalan deskripsi baris potongan voucher ongkir (mig 5620) — SATU sumber utk TS. */
export const VOUCHER_ONGKIR_LINE_PREFIX = "Voucher ongkir: ";

/** "Voucher ongkir: Gratis JNE" → "Gratis JNE"; deskripsi lain → null. */
export function voucherNameFromLineDescription(description: string | null | undefined): string | null {
  const d = (description ?? "").trim();
  if (!d.toLowerCase().startsWith(VOUCHER_ONGKIR_LINE_PREFIX.toLowerCase())) return null;
  return d.slice(VOUCHER_ONGKIR_LINE_PREFIX.length).trim() || null;
}

// ─── Urutan tampilan baris nota ───────────────────────────────────────────────
// Rincian nota ditampilkan produk dulu, baru komponen biaya (Ongkir → Biaya
// Dropship/Packing → Biaya Lainnya), lalu potongan (Kredit DP → Diskon) —
// cermin urutan ringkasan `summarizeInvoiceCharges`. Tanpa ini baris ongkir
// yang lahir di tengah nota (Nota + Kirim mig 205) menyela daftar produk.

function invoiceLineDisplayRank(line: {
  kind: string;
  description: string;
}): number {
  if (line.kind === "product_item") return 0;
  if (line.kind === "dp_credit") return 4;
  if (line.kind === "discount") return 5;
  const charge = {
    kind: line.kind as InvoiceLineKind,
    description: line.description
  };
  if (isOngkirChargeLine(charge)) return 1;
  if (isDropshipFeeLine(charge)) return 2;
  return 3;
}

/**
 * Urutkan baris nota untuk TAMPILAN rincian: produk → ongkir → biaya DS →
 * biaya lainnya → kredit DP → diskon. Urutan asli dipertahankan di dalam
 * tiap kelompok (baris produk dibatalkan tetap menempel kelompok produk).
 */
export function sortInvoiceLinesForDisplay<
  L extends { kind: string; description: string }
>(lines: ReadonlyArray<L>): L[] {
  return lines
    .map((line, index) => ({ line, index }))
    .sort(
      (a, b) =>
        invoiceLineDisplayRank(a.line) - invoiceLineDisplayRank(b.line) ||
        a.index - b.index
    )
    .map((entry) => entry.line);
}

// ─── Bagikan nota ke WhatsApp reseller ────────────────────────────────────────
// Rekap teks-polos siap kirim lewat WhatsApp: rincian baris + total/bayar/sisa +
// tautan nota (halaman pembeli /orders/tagihan/[id]). Pure agar mudah diuji.

/** Format Rupiah untuk rekap WhatsApp; baris negatif (kredit DP) jadi "−Rp …". */
function formatShareRp(amount: number): string {
  const abs = Math.abs(Math.round(Number.isFinite(amount) ? amount : 0));
  return `${amount < 0 ? "−" : ""}Rp ${abs.toLocaleString("id-ID")}`;
}

export type InvoiceShareLine = {
  kind: InvoiceLineKind;
  description: string;
  qty?: number | null;
  amount: number;
  cancelledAt?: string | null;
  /**
   * Snapshot harga normal/pcs (mig 095) — bahan baris pembanding "harga umum"
   * yang dicoret. Null/absen ("harga menyusul") → baris pembandingnya memang
   * tak dicetak, bukan dicetak nol.
   */
  unitNormalPrice?: number | null;
  /** Snapshot tambahan per pcs (mig 7900) — harga umum yang dicoret = dasar + tambahan. */
  unitPriceExtra?: number | null;
  /**
   * Nama voucher produk (mig 5610) — dicetak sebagai NAMA saja. Rupiahnya
   * sudah terlipat ke harga bayar dan karena itu sudah terhitung di "hemat";
   * mencetaknya lagi di sebelahnya = rupiah yang sama terbaca dua kali, persis
   * temuan nota #3137 yang melahirkan `lineVoucherAmountToPrint`.
   */
  voucherName?: string | null;
};

/**
 * Satu rekening tujuan transfer di rekap WhatsApp. Bentuknya cermin
 * `InvoicePdfAccount` (blok "Cara membayar" nota PDF) — dua permukaan itu
 * dibaca berdampingan, jadi nomor & nama pemiliknya wajib berasal dari sumber
 * yang sama. Pemanggil yang menyaring QRIS (tak punya nomor untuk disalin) dan
 * rekening nonaktif, sama seperti pemasok bahan PDF.
 */
export type InvoiceShareAccount = {
  /** Label jenis pendek: "Bank" / "E-Wallet" (`paymentAccountTypeShort`). */
  typeLabel: string;
  /** Nama bank / e-wallet, mis. "BCA". */
  name: string;
  accountNumber: string | null;
  accountHolder: string | null;
};

/**
 * Kelompokkan baris AKTIF mengikuti urutan rincian layar (TotalsCard):
 * produk → ongkir → biaya DS → biaya lainnya → kredit DP → diskon. Dipakai
 * bersama rekap satu nota (`buildInvoiceShareMessage`) & pesan penagihan
 * multi-nota (`buildInvoiceReminderMessage`).
 */
function groupShareLines<L extends InvoiceShareLine>(
  lines: ReadonlyArray<L>
): { productLines: L[]; feeLines: L[]; deductionLines: L[] } {
  const productLines: L[] = [];
  const ongkirLines: L[] = [];
  const dsLines: L[] = [];
  const otherLines: L[] = [];
  const dpCreditLines: L[] = [];
  const discountLines: L[] = [];
  for (const l of lines) {
    if (l.cancelledAt) continue;
    if (l.kind === "product_item") productLines.push(l);
    else if (l.kind === "dp_credit") dpCreditLines.push(l);
    else if (l.kind === "discount") discountLines.push(l);
    else if (isOngkirChargeLine(l)) ongkirLines.push(l);
    else if (isDropshipFeeLine(l)) dsLines.push(l);
    else otherLines.push(l);
  }
  return {
    productLines,
    feeLines: [...ongkirLines, ...dsLines, ...otherLines],
    deductionLines: [...dpCreditLines, ...discountLines]
  };
}

/** Satu baris rincian dalam rekap WhatsApp: "• Kaos Polos ×2 — Rp 100.000". */
function formatShareRow(line: InvoiceShareLine): string {
  const qtyLabel = line.qty && line.qty > 0 ? ` ×${line.qty}` : "";
  const desc = line.description.trim() || INVOICE_LINE_KIND_LABEL[line.kind];
  return `• ${desc}${qtyLabel} — ${formatShareRp(line.amount)}`;
}

/**
 * Baris rincian + baris PEMBANDING HARGA di bawahnya — versi teks dari rincian
 * per baris di layar nota pembeli (`DetailLineRow`, InvoiceDetailClient).
 * Aturannya sengaja dijaga sama persis, karena pembeli membaca keduanya
 * berdampingan saat mempersoalkan angka:
 *
 * Bentuknya RINGKAS, dua baris (permintaan pemilik 2026-09-21): nama + qty di
 * atas, aritmetikanya di bawah — `@harga umum` dicoret berikut persen
 * potongannya, harga yang benar-benar dibayar, × qty, lalu `=` nominal
 * barisnya. Nominal pindah ke baris kedua
 * supaya barisnya berakhir pada hasil hitungan, bukan mengulanginya dua kali.
 *
 *   · ada potongan nyata   → `~@harga umum~ (N%) harga bayar ×qty = nominal`
 *   · tanpa potongan, qty>1 → `@harga satuan ×qty = nominal`
 *   · tanpa potongan, qty 1 → SATU baris biasa (tak ada yang diterangkan)
 *   · tanpa snapshot harga normal ("harga menyusul") → tanpa pembanding
 *   · MENAGIH DI ATAS harga umum → pembandingnya SENGAJA tak dicetak
 *
 * Butir terakhir bukan kelalaian: `lineDiscount` mengklem `discountRp` ke nol,
 * jadi baris yang menagih di atas snapshot jatuh ke cabang "tanpa potongan".
 * Itu keputusan yang sama dengan nota PDF & surat jalan (`linePaidAboveNormal`)
 * — harga khusus admin di atas harga umum bukan kesalahan, tapi "Umum
 * Rp 300.000 · Diskon – · bayar Rp 400.000" di dokumen PEMBELI hanya menyulut
 * pertanyaan. Yang memberi tahu ADMIN tetap layar nota, bukan pesan ini.
 *
 * ⛔ NOMINAL HEMAT-nya SENGAJA TAK DICETAK (permintaan pemilik 2026-09-21).
 * Selisihnya sudah terbaca sendiri dari harga umum yang dicoret di sebelah
 * harga bayar; mengejanya lagi jadi "hemat Rp X (Y%)" membuat tiap baris
 * berbunyi seperti iklan, dan pada nota grosir ia memajang struktur margin ke
 * siapa pun yang tautannya diteruskan. Yang tetap ada: rekap sekali di bawah
 * (`buildShareSavingsLines`), karena satu angka di kaki nota bukan iklan.
 *
 * Indentasi dua spasi bertahan sampai WhatsApp: `renderWaTemplate` melewatkan
 * perapian spasi pada baris yang memuat variabel BLOK, dan rincian ini selalu
 * masuk lewat `[rincian tagihan]` yang bernilai multi-baris.
 */
function formatShareRowsDetailed(line: InvoiceShareLine): string[] {
  const qty = line.qty ?? 0;
  // Harga satuan yang BENAR-BENAR dibayar = nominal baris ÷ qty (baris nota
  // menyimpan total, bukan harga satuan) — rumus yang sama dengan layar.
  const unitPaid =
    line.kind === "product_item" && qty > 0
      ? Math.round(Math.max(0, line.amount) / qty)
      : null;
  const d = line.cancelledAt ? null : lineDiscount(line);
  const berdiskon = !!d && d.discountRp > 0;

  // Tak ada yang perlu diterangkan → tetap SATU baris berikut nominalnya:
  // baris dibatalkan, baris non-produk (ongkir/diskon/kredit DP), dan produk
  // qty 1 tanpa potongan — di situ "@Rp 98.000 ×1 = Rp 98.000" cuma mengeja
  // ulang angka yang sudah tertulis di sebelahnya.
  if (line.cancelledAt || unitPaid == null || (!berdiskon && qty <= 1)) {
    return [formatShareRow(line)];
  }

  const desc = line.description.trim() || INVOICE_LINE_KIND_LABEL[line.kind];
  // `@` IKUT MASUK ke dalam tanda coret (`~@Rp 250.000~`, bukan `@~Rp …~`).
  // WhatsApp hanya merender coret bila tanda `~` pembukanya berada di batas
  // kata; menempelkannya di belakang `@` membuat tandanya tercetak apa adanya
  // dan harga umumnya justru tampil TIDAK tercoret — kebalikan dari maksudnya.
  // Persen potongan menempel di belakang harga umum yang dicoret — label
  // pendek, bukan kalimat: ia menerangkan JARAK antara dua harga yang berdiri
  // bersebelahan, dan itu yang membedakannya dari "hemat Rp … (…%)" yang
  // dicabut sehari sebelumnya. Basisnya harga umum baris ini (`discountPct`),
  // jadi angkanya konsisten dengan rekap di kaki rincian.
  // Harga umum yang dicoret = dasar + tambahan (mig 7900): angka yang akan
  // dibayar pembeli tanpa level; persennya tetap atas dasar.
  const umum = berdiskon
    ? `~@${formatShareRp(d.normalUnit + d.extraUnit)}~ (${formatDiscountPct(d.discountPct)}) `
    : "@";
  const voucher = line.voucherName?.trim()
    ? ` · \u{1F39F} ${line.voucherName.trim()}`
    : "";
  const rows = [
    `• ${desc}${qty > 0 ? ` ×${qty}` : ""}`,
    `  ${umum}${formatShareRp(unitPaid)} ×${qty} = ${formatShareRp(line.amount)}${voucher}`
  ];
  // Tambahan (mig 7900) ditempel UTUH sesudah potongan: tanpa rinciannya
  // "~@Rp 120.000~ (10%) Rp 110.000" terbaca salah hitung (120.000 − 10 % =
  // 108.000) — persis keluhan yang dicegah R1 (bughunt D14-15). Cermin sub-baris
  // "dasar + tambahan" nota PDF.
  if (berdiskon && d.extraUnit > 0) {
    rows.push(
      `  (dasar ${formatShareRp(d.normalUnit)} + tambahan ${formatShareRp(d.extraUnit)}; diskon dari dasar)`
    );
  }
  return rows;
}
/**
 * Rekap "harga umum vs potongan" — versi teks kartu **Potongan Harga** di layar
 * nota pembeli, dengan agregat yang dihitung persis sama: Σ harga normal & Σ
 * potongan baris produk yang PUNYA snapshot. Baris tanpa snapshot tak
 * menyumbang ke keduanya, jadi "Harga umum" di sini adalah harga umum dari
 * barang yang memang punya pembanding — bukan nilai nota.
 *
 * `[]` bila tak ada potongan sama sekali (gerbang `hasDiscount` di layar):
 * "Potongan kamu: −Rp 0" pada nota tanpa diskon cuma menambah baris.
 */
function buildShareSavingsLines(
  lines: ReadonlyArray<InvoiceShareLine>
): string[] {
  let normalSum = 0;
  let baseSum = 0;
  let extraSum = 0;
  let discountSum = 0;
  for (const l of lines) {
    if (l.cancelledAt) continue;
    const d = lineDiscount(l);
    if (!d) continue;
    // "Harga umum" yang dicoret memuat tambahan (mig 7900); persennya atas dasar.
    normalSum += d.normalTotal + d.extraTotal;
    baseSum += d.normalTotal;
    extraSum += d.extraTotal;
    discountSum += d.discountRp;
  }
  if (discountSum <= 0) return [];
  // Bertambahan → kedua angka menyebut basisnya, kalau tidak 20.000/240.000 =
  // 8,3 % tampak bertentangan dengan "(10%)" (bughunt D14-15). Tambahan 0 →
  // teks lama persis.
  const pct =
    baseSum > 0
      ? ` (${formatDiscountPct((discountSum / baseSum) * 100)}${extraSum > 0 ? " dari harga dasar" : ""})`
      : "";
  return [
    `Harga umum: ~${formatShareRp(normalSum)}~${
      extraSum > 0 ? ` (termasuk tambahan ${formatShareRp(extraSum)})` : ""
    }`,
    `Potongan kamu: −${formatShareRp(discountSum)}${pct}`
  ];
}

/**
 * Blok "Transfer ke" — rekening tujuan yang bisa disalin langsung dari chat.
 *
 * Pemanggil yang menggerbang MUNCUL/TIDAK-nya lewat `isInvoicePayable`, persis
 * seperti seksi "Cara membayar" nota PDF: nota lunas, batal, ditolak, dan
 * pengajuan yang nominalnya belum final tak pernah mengirim ajakan transfer.
 * Rekening tanpa nomor (QRIS) dilewati di sini juga — tak ada yang bisa
 * disalin pembeli dari sebuah gambar.
 */
function buildShareAccountLines(
  accounts: ReadonlyArray<InvoiceShareAccount>
): string[] {
  const rows: string[] = [];
  for (const a of accounts) {
    const number = (a.accountNumber ?? "").trim();
    if (!number) continue;
    const holder = (a.accountHolder ?? "").trim();
    const label = [a.typeLabel.trim(), a.name.trim()].filter(Boolean).join(" ");
    rows.push(`• *${label}* ${number}${holder ? ` a.n. ${holder}` : ""}`);
  }
  return rows.length ? ["*Transfer ke:*", ...rows] : [];
}

/**
 * Susun rekap nota untuk dibagikan ke WhatsApp reseller. Memuat nomor nota,
 * rincian baris aktif (baris dibatalkan dilewati) berikut PEMBANDING HARGA
 * per baris — `@harga umum` dicoret + persen + harga bayar ×qty = nominal — dan rekap
 * potongannya, lalu total/sudah dibayar/sisa, rekening tujuan transfer, tautan
 * nota, dan tautan unduh PDF publik. Semua URL di-pass pemanggil (origin
 * runtime); rekeningnya dari sumber yang sama dengan nota PDF.
 */
export function buildInvoiceShareMessage(input: {
  customerName: string;
  invoiceNo: number;
  invoiceCode?: string | null;
  kind: InvoiceKind;
  status: InvoiceStatus;
  lines: ReadonlyArray<InvoiceShareLine>;
  total: number;
  balanceApplied: number;
  amountPaid: number;
  refundedAmount?: number;
  note?: string | null;
  invoiceUrl: string;
  /** Nama toko (branding dari Pengaturan Website); kosong → baris dilewati. */
  storeName?: string | null;
  /**
   * Rincian nota DP (harga produk asli → setelan → aritmetika → sisa
   * pelunasan). Nota DP hanya berisi SATU baris bernominal, jadi tanpa blok ini
   * pesan WhatsApp-nya memuat angka tanpa jejak. Bahannya `buildDpBreakdown`
   * yang SAMA dengan kartu "Rincian DP" di layar. Null/kosong → blok dilewati.
   */
  dpBreakdown?: DpBreakdown | null;
  /**
   * Tautan halaman nota PUBLIK (`/lihat-tagihan/<token>`, mig 6080) — pembeli membuka &
   * mengunduh PDF-nya TANPA login. Kosong/null → blok ajakan + tautannya
   * hilang seutuhnya, bukan menyisakan judul tanpa tautan (idiom variabel BLOK
   * yang sama dengan `[blok bayar sekaligus]` pesan penagihan).
   */
  publicPdfUrl?: string | null;
  /**
   * Rekening tujuan transfer (bank/e-wallet AKTIF toko, tanpa QRIS) — bahan
   * blok "Transfer ke". Pemanggil mengambilnya dari sumber yang SAMA dengan
   * nota PDF supaya nomor di chat & di dokumen tak bisa berbeda. Kosong/absen
   * → blok dilewati; nota yang tak bisa dibayar lagi juga melewatinya sendiri.
   */
  accounts?: ReadonlyArray<InvoiceShareAccount>;
  /**
   * Kalimat peringatan tempo bayar (mig 4260), hasil `formatAutoVoidWarning`.
   * Pemanggil yang menentukan MUNCUL atau TIDAK — helper itulah yang tahu
   * apakah notanya memang akan disapu. Kosong/null → blok dilewati.
   */
  autoVoidWarning?: string | null;
}, overrides?: WaTemplateOverrides | null): string {
  const name = input.customerName.trim() || "Kak";
  const kindLabel =
    input.kind !== "product"
      ? ` (${INVOICE_KIND_META[input.kind].label})`
      : invoiceCarriesOngkir(input)
        ? ` (${invoiceKindWord("product", true)})`
        : "";
  const settled = computeSettledEffective(
    input.balanceApplied,
    input.amountPaid,
    input.refundedAmount ?? 0
  );
  const outstanding = computeInvoiceOutstanding(
    input.total,
    input.balanceApplied,
    input.amountPaid,
    input.refundedAmount ?? 0
  );

  // Kelompokkan baris aktif mengikuti urutan rincian layar (TotalsCard):
  // Produk → Ongkir → Biaya DS → Biaya Lainnya → Kredit DP → Diskon. Baris
  // produk tetap dirinci satu-satu, lalu disisipkan "Subtotal Produk" sebelum
  // baris ongkir/biaya DS bila nota punya komponen biaya selain produk.
  const { productLines, feeLines, deductionLines } = groupShareLines(
    input.lines
  );

  const charges = summarizeInvoiceCharges(input.lines);

  // Rincian dibagi jadi blok visual yang dipisah baris kosong biar rapi:
  //   Blok 1 (Produk)   : tiap produk + "Subtotal Produk"
  //   Blok 2 (Biaya)    : ongkir, biaya dropship/packing, biaya lainnya
  //   Blok 3 (Potongan) : kredit DP, diskon/promo (nominal minus)
  const productBlock = productLines.flatMap(formatShareRowsDetailed);
  // Subtotal Produk hanya bila ada baris produk + komponen biaya lain (kalau
  // nota berisi produk saja, subtotal = Total → dilewati agar tak berulang).
  if (charges.hasBreakdown && productLines.length) {
    const qtyLabel =
      charges.productQty > 0 ? ` (${charges.productQty} pcs)` : "";
    productBlock.push(
      `Subtotal Produk${qtyLabel}: ${formatShareRp(charges.productTotal)}`
    );
  }
  const feeBlock = feeLines.map(formatShareRow);
  const deductionBlock = deductionLines.map(formatShareRow);

  // Blok 4 (Rekap potongan): "Harga umum" yang dicoret + total potongan.
  // Ikut MASUK ke `[rincian tagihan]`, bukan jadi variabel sendiri — template
  // yang sudah disunting toko karena itu tetap mendapatkannya tanpa menyimpan
  // ulang (prinsip modul waTemplates: struktur angka milik kode).
  const savingsBlock = buildShareSavingsLines(input.lines);

  const detailBlocks = [
    productBlock,
    feeBlock,
    deductionBlock,
    savingsBlock
  ].filter((b) => b.length);
  const detailLines: string[] = detailBlocks.length
    ? detailBlocks.flatMap((block, i) => (i > 0 ? ["", ...block] : block))
    : ["• (belum ada rincian)"];

  // Blok "Rincian DP" hanya untuk nota DP — pada nota barang, rincian yang
  // sama sudah terbaca dari baris-baris produknya sendiri.
  const dpBlock =
    input.kind === "dp" && input.dpBreakdown
      ? buildDpShareLines(input.dpBreakdown, { audience: "buyer" })
      : [];

  const dueWarning = (input.autoVoidWarning ?? "").trim();
  const publicPdfUrl = (input.publicPdfUrl ?? "").trim();

  // Rincian + blok DP masuk sebagai SATU variabel: susunan angkanya milik kode,
  // yang boleh diubah toko adalah bingkai kalimatnya.
  return renderWaTemplate(resolveWaTemplate("invoice_share", overrides), {
    "[nomor nota]": `${formatInvoiceNo(input.invoiceNo, input.invoiceCode)}${kindLabel}`,
    "[nama toko]": (input.storeName ?? "").trim(),
    "[nama pelanggan]": name,
    "[rincian tagihan]": [
      ...detailLines,
      ...(dpBlock.length ? ["", ...dpBlock] : [])
    ].join("\n"),
    "[total tagihan]": formatShareRp(input.total),
    "[baris sudah dibayar]":
      settled > 0 ? `Sudah dibayar: ${formatShareRp(settled)}` : "",
    // Pengajuan yang belum di-ACC: nominalnya belum final \u2014 sama dengan nota
    // PDF-nya, yang sengaja tanpa strip sisa (bughunt 2026-10-13).
    "[status tagihan]":
      input.status === "pending_acc"
        ? "*Status: MENUNGGU ACC admin \u2014 nominal belum final, jangan ditransfer dulu*"
        : input.status === "paid" || outstanding <= 0
          ? "*Status: LUNAS \u2705*"
          : `*Sisa tagihan: ${formatShareRp(outstanding)}*`,
    "[blok rekening]": isInvoicePayable(input.status, outstanding)
      ? buildShareAccountLines(input.accounts ?? []).join("\n")
      : "",
    "[baris catatan]": input.note?.trim() ? `Catatan: ${input.note.trim()}` : "",
    "[baris peringatan tempo]": dueWarning ? `\u23F3 ${dueWarning}` : "",
    "[link nota]": input.invoiceUrl,
    "[blok unduh pdf]": publicPdfUrl
      ? `📄 Unduh tagihan PDF (tanpa perlu login):\n${publicPdfUrl}`
      : ""
  });
}

// ─── Tagih nota terbuka ke WhatsApp (penagihan multi-nota) ────────────────────
// Satu pesan WhatsApp siap kirim untuk MENAGIH beberapa nota terbuka sekaligus
// (tombol "Tagih via WhatsApp" di tab Terbuka halaman tagihan per-reseller).
// Isi: rincian tiap nota (item, total, sudah dibayar, posisi pcs lunas, sisa,
// umur tagihan) + REKAP total yang harus dibayar + tautan (nota per baris,
// bayar sekaligus, daftar tagihan). Nota DP diringkas SATU baris; bila semua
// nota DP, pesannya memakai template ringkas `invoice_reminder_dp`. Pure — URL
// dibentuk pemanggil (origin runtime), "sekarang" bisa di-inject supaya
// deterministik saat diuji.

/** Maks baris item yang dirinci per nota; sisanya diringkas satu baris. */
export const REMINDER_MAX_LINES_PER_INVOICE = 8;
/**
 * Di atas jumlah nota ini pesan beralih ke mode RINGKAS (tanpa rincian item)
 * supaya pesan WhatsApp tetap terbaca & URL wa.me tidak kepanjangan.
 */
export const REMINDER_DETAIL_INVOICE_LIMIT = 8;

const REMINDER_TIME_ZONE = "Asia/Jakarta";

export type InvoiceReminderLine = InvoiceShareLine & {
  /** Pcs baris yang sudah tertutup pembayaran — bahan "posisi barang". */
  paidQty?: number | null;
};

export type InvoiceReminderEntry = {
  invoiceNo: number;
  invoiceCode?: string | null;
  kind: InvoiceKind;
  createdAt?: string | null;
  lines: ReadonlyArray<InvoiceReminderLine>;
  total: number;
  balanceApplied: number;
  amountPaid: number;
  refundedAmount?: number;
  note?: string | null;
  /** Tautan nota di sisi pembeli (/orders/tagihan/[id]). */
  invoiceUrl: string;
};

/**
 * Nota yang boleh ditagih lewat pesan penagihan: masih TERBUKA (open/partial)
 * dan masih BERSISA — "posisi barangnya belum lunas". Nota lunas/void/ditolak
 * serta pengajuan yang belum di-ACC tidak ikut. Cermin gate tombol di UI.
 */
export function canRemindInvoice(inv: {
  status: InvoiceStatus;
  outstanding: number;
}): boolean {
  return isInvoicePayable(inv.status, inv.outstanding);
}

/**
 * Posisi barang sebuah nota: Σ pcs baris produk aktif dan pcs yang sudah
 * tertutup pembayaran (alokasi per-pcs mig 072). Nota tanpa baris produk
 * (ongkir/DP) → qty 0 sehingga baris "Barang lunas" dilewati.
 */
export function reminderItemProgress(
  lines: ReadonlyArray<InvoiceReminderLine>
): { qty: number; paidQty: number } {
  let qty = 0;
  let paidQty = 0;
  for (const l of lines) {
    if (l.cancelledAt || l.kind !== "product_item") continue;
    const q = Math.max(0, Math.round(l.qty ?? 0));
    qty += q;
    paidQty += Math.min(q, Math.max(0, Math.round(l.paidQty ?? 0)));
  }
  return { qty, paidQty };
}

/** Tanggal singkat WIB ("25 Jul 2026"); "" bila tanggal kosong/tak valid. */
function formatReminderDate(iso: string | null | undefined): string {
  const raw = (iso ?? "").trim();
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: REMINDER_TIME_ZONE
  }).format(date);
}

/** Umur nota dalam hari penuh (≥ 0); null bila tanggal kosong/tak valid. */
function reminderAgeDays(
  iso: string | null | undefined,
  now: number
): number | null {
  const raw = (iso ?? "").trim();
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const days = Math.floor((now - date.getTime()) / 86_400_000);
  return days > 0 ? days : 0;
}

/** Urutan tampil penagihan = FIFO (nota tertua dulu, cermin alokasi server). */
function sortRemindersFifo(
  invoices: ReadonlyArray<InvoiceReminderEntry>
): InvoiceReminderEntry[] {
  return [...invoices].sort(
    (a, b) =>
      (a.createdAt ?? "").localeCompare(b.createdAt ?? "") ||
      a.invoiceNo - b.invoiceNo
  );
}

/**
 * Susun pesan penagihan WhatsApp untuk beberapa nota terbuka sekaligus.
 * Nota diurut tertua dulu (cermin FIFO alokasi pembayaran). Nota yang sudah
 * tidak bersisa sebaiknya sudah disaring pemanggil lewat `canRemindInvoice`.
 */
export function buildInvoiceReminderMessage(input: {
  customerName: string;
  /** Nama toko (branding Pengaturan Website); kosong → baris dilewati. */
  storeName?: string | null;
  invoices: ReadonlyArray<InvoiceReminderEntry>;
  /** Tautan bayar semua sekaligus (/orders/pay?inv=…) — opsional. */
  payAllUrl?: string | null;
  /** Tautan daftar tagihan pembeli (/tagihan) — opsional. */
  invoiceListUrl?: string | null;
  /**
   * Tautan daftar Tagihan DP pembeli (/tagihan?jenis=dp) untuk pesan khusus
   * DP — opsional; kosong → jatuh ke `invoiceListUrl`.
   */
  dpListUrl?: string | null;
  /** Catatan penutup dari admin (mis. tenggat bayar) — opsional. */
  adminNote?: string | null;
  /** Referensi "sekarang" untuk umur tagihan (default: waktu panggil). */
  now?: Date | number | string;
}, overrides?: WaTemplateOverrides | null): string {
  const name = input.customerName.trim() || "Kak";
  const list = sortRemindersFifo(input.invoices);
  // Tak seharusnya terjadi (tombol digate `canRemindInvoice`), tapi jangan
  // sampai menghasilkan pesan aneh berisi "Rp 0".
  if (!list.length) {
    return `Halo ${name} 👋\n(tidak ada tagihan yang perlu ditagih)`;
  }
  const nowMs = (() => {
    const raw = input.now;
    if (raw == null) return Date.now();
    const date = raw instanceof Date ? raw : new Date(raw);
    const ms = date.getTime();
    return Number.isNaN(ms) ? Date.now() : ms;
  })();
  // Banyak nota → mode ringkas: rincian item dilewati, tautan tiap nota tetap
  // ada supaya pembeli masih bisa menelusuri detailnya.
  const detailed = list.length <= REMINDER_DETAIL_INVOICE_LIMIT;

  let grandTotal = 0;
  let grandSettled = 0;
  let grandOutstanding = 0;
  // `compact` = nota DP satu baris; baris ringkas yang berurutan dirapatkan
  // tanpa baris kosong di antaranya (lihat penyambung di bawah).
  const blocks: { rows: string[]; compact: boolean }[] = [];

  list.forEach((inv, index) => {
    const refunded = inv.refundedAmount ?? 0;
    const settled = computeSettledEffective(
      inv.balanceApplied,
      inv.amountPaid,
      refunded
    );
    const outstanding = computeInvoiceOutstanding(
      inv.total,
      inv.balanceApplied,
      inv.amountPaid,
      refunded
    );
    grandTotal += inv.total;
    grandSettled += settled;
    grandOutstanding += outstanding;

    if (inv.kind === "dp") {
      blocks.push({
        rows: formatDpReminderRows(index + 1, inv, settled, outstanding),
        compact: true
      });
      return;
    }

    const rows: string[] = [
      `*${index + 1}. ${formatInvoiceTitleFor(inv)}*`
    ];

    const dateLabel = formatReminderDate(inv.createdAt);
    if (dateLabel) {
      const age = reminderAgeDays(inv.createdAt, nowMs);
      rows.push(
        `Terbit: ${dateLabel}${
          age != null && age > 0 ? ` (${age} hari lalu)` : ""
        }`
      );
    }

    if (detailed) {
      const { productLines, feeLines, deductionLines } = groupShareLines(
        inv.lines
      );
      const charges = summarizeInvoiceCharges(inv.lines);
      const shown = productLines.slice(0, REMINDER_MAX_LINES_PER_INVOICE);
      const hidden = productLines.length - shown.length;
      rows.push(...shown.map(formatShareRow));
      if (hidden > 0) rows.push(`• …dan ${hidden} item lainnya`);
      // Subtotal produk hanya berguna bila nota punya komponen selain produk
      // (kalau produk saja, subtotal = Total → mubazir). Cermin rekap 1 nota.
      if (charges.hasBreakdown && productLines.length) {
        const qtyLabel =
          charges.productQty > 0 ? ` (${charges.productQty} pcs)` : "";
        rows.push(
          `Subtotal Produk${qtyLabel}: ${formatShareRp(charges.productTotal)}`
        );
      }
      rows.push(...feeLines.map(formatShareRow));
      rows.push(...deductionLines.map(formatShareRow));
    }

    rows.push(`Total tagihan: ${formatShareRp(inv.total)}`);
    if (settled > 0) rows.push(`Sudah dibayar: ${formatShareRp(settled)}`);

    const progress = reminderItemProgress(inv.lines);
    if (progress.qty > 0) {
      rows.push(
        `Barang lunas: ${progress.paidQty}/${progress.qty} pcs` +
          (progress.paidQty < progress.qty
            ? ` (belum lunas ${progress.qty - progress.paidQty} pcs)`
            : "")
      );
    }

    rows.push(`*Sisa yang harus dibayar: ${formatShareRp(outstanding)}*`);
    if (inv.note?.trim()) rows.push(`Catatan: ${inv.note.trim()}`);
    if (inv.invoiceUrl.trim()) rows.push(`Lihat tagihan: ${inv.invoiceUrl.trim()}`);

    blocks.push({ rows, compact: false });
  });

  // Dua baris DP ringkas berurutan → satu baris baru; selain itu (blok nota
  // lengkap di salah satu sisi) → baris kosong pemisah seperti biasa.
  const details = blocks
    .map((block, i) => {
      const text = block.rows.join("\n");
      if (i === 0) return text;
      const glue = block.compact && blocks[i - 1].compact ? "\n" : "\n\n";
      return glue + text;
    })
    .join("");

  const payAll = (input.payAllUrl ?? "").trim();
  const listUrl = (input.invoiceListUrl ?? "").trim();
  const shared = {
    "[jumlah nota]": list.length > 1 ? ` (${list.length} TAGIHAN)` : "",
    "[nama toko]": (input.storeName ?? "").trim(),
    "[nama pelanggan]": name,
    "[kalimat pembuka]":
      list.length === 1
        ? "Ada 1 tagihan yang masih belum lunas. Berikut rinciannya:"
        : `Ada ${list.length} tagihan yang masih belum lunas. Berikut rinciannya:`,
    "[total harus dibayar]": formatShareRp(grandOutstanding),
    "[catatan admin]": input.adminNote?.trim() ?? ""
  };

  // Semua nota DP → pesan ringkas khusus DP (template sendiri, bisa disunting
  // terpisah) TANPA tautan bayar sekaligus — rinciannya & pembayarannya lewat
  // daftar Tagihan DP. Campuran DP + Barang/Ongkir tetap memakai template
  // umum; nota DP-nya saja yang diringkas satu baris.
  if (list.every((inv) => inv.kind === "dp")) {
    const dpList = (input.dpListUrl ?? "").trim() || listUrl;
    return renderWaTemplate(
      resolveWaTemplate("invoice_reminder_dp", overrides),
      {
        ...shared,
        "[rincian dp]": details,
        "[blok rincian dp]": dpList ? `Rincian DP lihat di:\n${dpList}` : ""
      }
    );
  }

  return renderWaTemplate(resolveWaTemplate("invoice_reminder", overrides), {
    ...shared,
    "[rincian nota]": details,
    "[blok bayar sekaligus]": payAll
      ? `${list.length === 1 ? "Bayar sekarang di sini:" : "Bayar semua sekaligus:"}\n${payAll}`
      : "",
    "[ringkasan jumlah]": `Dari ${list.length} tagihan \u00B7 nilai ${formatShareRp(grandTotal)}`,
    "[baris sudah dibayar]":
      grandSettled > 0 ? `Sudah dibayar: ${formatShareRp(grandSettled)}` : "",
    "[blok daftar tagihan]": listUrl
      ? `Cek semua tagihan kamu:\n${listUrl}`
      : ""
  });
}

/**
 * Nota DP di pesan penagihan = SATU baris tebal "N. Tagihan DP — <produk> —
 * Rp <sisa>" (pemilik 2026-10-05). Nota DP hanya berisi satu baris "DP —
 * <produk>", jadi Terbit/Total nota/Lihat nota per nota cuma memanjangkan
 * pesan. Nominalnya SISA; DP yang sudah dicicil diberi "(sisa dari Rp total)"
 * supaya pembeli tak mengira nominal DP-nya berubah. Nama produk tak
 * terbaca → judul bernomor biasa ("Tagihan DP TGH…"). Catatan nota tetap ikut.
 */
function formatDpReminderRows(
  no: number,
  inv: InvoiceReminderEntry,
  settled: number,
  outstanding: number
): string[] {
  const product = dpInvoiceProductName(inv.lines);
  const title = product
    ? `Tagihan ${INVOICE_KIND_TITLE.dp} \u2014 ${product}`
    : formatInvoiceTitleFor(inv);
  const amount =
    settled > 0
      ? `${formatShareRp(outstanding)} (sisa dari ${formatShareRp(inv.total)})`
      : formatShareRp(outstanding);
  const rows = [`*${no}. ${title} \u2014 ${amount}*`];
  if (inv.note?.trim()) rows.push(`Catatan: ${inv.note.trim()}`);
  return rows;
}

/** Setelan diskon produk untuk satu level reseller — mode + nilai mentah. */
export type LevelDiscountConfig = { mode: DiscountMode; value: number };

/** Kolom override diskon per-varian (cermin product_variants, mapping slot).
 *  15 slot sejak mig 7300 (8 sejak mig 5600) — nama fieldnya dipetakan `DISKON_SLOT_KEY`. */
export type VariantDiscountConfig = {
  diskonMode?: DiscountMode | null;
  diskonReseller?: number | null;
  diskonAgen?: number | null;
  diskonSlot3?: number | null;
  diskonSlot4?: number | null;
  diskonSlot5?: number | null;
  diskonSlot6?: number | null;
  diskonSlot7?: number | null;
  diskonSlot8?: number | null;
  diskonSlot9?: number | null;
  diskonSlot10?: number | null;
  diskonSlot11?: number | null;
  diskonSlot12?: number | null;
  diskonSlot13?: number | null;
  diskonSlot14?: number | null;
  diskonSlot15?: number | null;
};

/**
 * Diskon default sebuah baris produk dari SETELAN PRODUK sesuai level reseller —
 * dipakai untuk prefill editor "Atur Diskon" nota. Cermin logika `pricing.ts`:
 *   1. override diskon per-varian utk slot grup MENANG (mode persen/nominal
 *      apa adanya dari `diskon_mode`),
 *   2. tanpa override → diskon grup (selalu persen, `discount_persen`).
 * Null bila tak ada grup / tak ada diskon (Customer biasa atau nilai 0).
 */
/**
 * Harga normal per-pcs yang dipakai MENILAI promo pada prefill diskon.
 *
 * Cermin `resolve_order_item_normal_price` (mig 0952 → 7730) yang mengisi
 * `invoice_lines.unit_normal_price`: harga varian dulu, lalu warisan slot
 * (produk ber-sumbu) / perwakilan hanya bila seragam, lalu `products
 * .price_normal` hanya bila tak ada varian berharga — NULL = harga menyusul.
 * Ada karena `resolveLevelDiscountConfig` MELEWATI cabang promo
 * saat basisnya null — dan varian yang belum diberi harga sendiri membuatnya
 * null. Akibatnya, membuka Edit Nota / ACC pada produk yang sedang promo
 * memasang ulang diskon LEVEL menimpa harga promo, diam-diam (bug live
 * 2026-08-14, produk "Lili Hijab New" varian "Navy / S").
 *
 * `null` hanya bila BENAR-BENAR tak ada sumber harga — di situ promo memang tak
 * bisa dinilai dan diskon level adalah jawaban yang benar.
 */
export function promoBaseUnitPrice(
  variantPrice: number | null | undefined,
  productPrice: number | null | undefined
): number | null {
  for (const raw of [variantPrice, productPrice]) {
    if (raw === null || raw === undefined) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
}

export function resolveLevelDiscountConfig(
  variant: VariantDiscountConfig | null,
  /**
   * `brandPersen` = diskon merek per level (mig 7070) yang SUDAH diselesaikan
   * untuk produk baris ini (`levelForProduct`) — menggantikan override slot dan
   * diskon rata, cermin `applyLevelLadder` di pricing.ts.
   */
  group: { slot: number; discountPersen: number; brandPersen?: number | null } | null,
  /**
   * Promo kampanye yang berlaku untuk produk baris ini (mig 3470) + harga normal
   * per-pcs untuk menilai mana yang termurah. PROMO MENANG MUTLAK: bila ada,
   * ia dipakai apa adanya dan konfigurasi level di bawah TIDAK dilihat.
   *
   * Tanpa cabang ini, admin yang meng-ACC nota produk yang sedang promo akan
   * melihat diskon LEVEL dipasang ulang menimpa harga promo — diam-diam, tanpa
   * pesan. Rantai presedensi di sini WAJIB sama dengan pricing.ts; kalau dua
   * tempat ini berbeda urutan, angka di layar dan angka di nota berpisah.
   */
  promos?: readonly ProductPromo[] | null,
  unitNormalPrice?: number | null,
  /**
   * Label ukuran varian baris ini (mig 3670) — diturunkan pemanggil dari NAMA
   * varian lewat `variantSizeLabel`, cermin `resolveVariantPromo`. Tanpa ini,
   * kampanye yang mengecualikan ukuran terpilih tetap ikut bersaing dan bisa
   * memasang potongan yang tak pernah berlaku di storefront.
   */
  sizeLabel?: string | null
): LevelDiscountConfig | null {
  // Tangga level (override varian slot grup → diskon grup) — dipakai cabang
  // tanpa promo DAN cabang promo harga coret (basis = harga coret).
  const levelConfig = (): LevelDiscountConfig | null => {
    if (!group) return null;
    // §1a pricing.ts (mig 7070): aturan merek level menang atas override & rata.
    const brandPersen = group.brandPersen;
    if (brandPersen != null && isUsableBrandPersen(brandPersen)) {
      return { mode: "persen", value: brandPersen };
    }
    // Peta slot → kolom dibaca dari SATU sumber (`DISKON_SLOT_KEY`, mig 5600).
    // Sebelumnya `switch` ini salinan kembar dari `pricing.ts`; salinan yang
    // tertinggal satu slot membuat nota menagih angka berbeda dari yang dilihat
    // pembeli, tanpa satu pun error.
    const override: number | null | undefined = isDiskonSlot(group.slot)
      ? variant?.[DISKON_SLOT_KEY[group.slot]]
      : null;
    if (override != null && override > 0) {
      return { mode: variant?.diskonMode ?? "persen", value: override };
    }
    if (group.discountPersen > 0) {
      return { mode: "persen", value: group.discountPersen };
    }
    return null;
  };
  const applyConfig = (base: number, cfg: LevelDiscountConfig | null): number => {
    if (!cfg) return base;
    return cfg.mode === "nominal"
      ? Math.max(0, Math.round(base - cfg.value))
      : Math.max(0, Math.round(base * (1 - cfg.value / 100)));
  };

  if (promos && promos.length && unitNormalPrice != null && unitNormalPrice > 0) {
    // KELAYAKAN LEVEL DULU (mig 3680) — cermin `pricing.ts:resolveVariantPromo`.
    // Ini JALUR UANG: cabang promo dievaluasi SEBELUM `if (!group)` di bawah,
    // jadi tanpa gerbang ini pembeli yang tak berhak (tanpa level sama sekali,
    // atau ber-level di luar `levelSlots`) mendapat potongan kampanye ber-level
    // ter-prefill ke baris notanya saat admin menekan ACC / Edit Nota — dan itu
    // angka yang benar-benar ditagihkan, bukan sekadar tampilan.
    const eligible = promosForLevel(promos, group?.slot ?? null);
    // "Termurah" dinilai pada harga AKHIR (kampanye harga coret masih dipotong
    // level) — cermin `finalPriceOf` di pricing.ts.
    const promo = pickCheapestPromo(eligible, unitNormalPrice, sizeLabel ?? null, (p, cand) =>
      promoLevelPolicy(cand) === "stack"
        ? applyConfig(p, levelConfig())
        : cand.neverWorse
          ? Math.min(p, applyConfig(unitNormalPrice, levelConfig()))
          : p
    );
    if (promo) {
      const rule = promoDiscountForSize(promo, sizeLabel ?? null) ?? {
        mode: promo.mode,
        value: promo.value
      };
      // never_worse (mig 6010) — cermin `resolveVariantPromo`: harga level yang
      // lebih murah MENANG, kampanye tak diklaim; prefill nota memakai level.
      if (promoLevelPolicy(promo) !== "stack" && promo.neverWorse) {
        const level = levelConfig();
        if (applyConfig(unitNormalPrice, level) < applyConfig(unitNormalPrice, rule)) {
          return level;
        }
      }
      if (promoLevelPolicy(promo) === "stack") {
        // Draf editor hanya memegang SATU potongan, sedangkan harga coret
        // memotong dua kali (promo lalu level). Dikembalikan sebagai NOMINAL
        // persis (harga umum − harga akhir) supaya angka notanya tepat sama
        // dengan yang dilihat pembeli — persen gabungan bisa meleset 1 rupiah.
        const coret = applyConfig(unitNormalPrice, rule);
        const final = applyConfig(coret, levelConfig());
        return { mode: "nominal", value: Math.max(0, unitNormalPrice - final) };
      }
      return { mode: rule.mode, value: rule.value };
    }
  }
  return levelConfig();
}

// ─── Diskon manual / promo (mig 137) ─────────────────────────────────────────
// Admin bisa menambah baris potongan harga bernominal (Rp) ke nota produk saat
// membuat / mengedit / meng-ACC. Magnitudo SELALU positif di UI & op; server
// (RPC) menyimpannya negatif sebagai baris `kind='discount'`.

/** Satu baris draft editor diskon manual. */
export type DiscountDraftRow = {
  /** id baris nota bila sudah ada di nota; null untuk baris baru. */
  lineId: string | null;
  description: string;
  /** Magnitudo potongan (Rp, string input) — positif. */
  amountRaw: string;
  /** Magnitudo tersimpan awal (baris existing) untuk deteksi perubahan; 0 utk baru. */
  initialAmount: number;
  cancelled: boolean;
};

export const DEFAULT_DISCOUNT_LABEL = "Diskon Promo";

/** Magnitudo satu baris draft (clamp ≥ 0, bulat). */
export function discountRowAmount(row: {
  amountRaw: string;
  cancelled?: boolean;
}): number {
  if (row.cancelled) return 0;
  return Math.max(0, Math.round(Number(row.amountRaw) || 0));
}

/**
 * Plafon diskon se-nota — CERMIN SQL `invoice_discountable_base`
 * (mig 4820, dilebarkan mig 5060). Dipakai editor diskon supaya admin tahu
 * batasnya SEBELUM menyimpan: di server plafon ini ditegakkan CONSTRAINT
 * TRIGGER `invoice_discount_ceiling` yang DEFERRABLE INITIALLY DEFERRED, jadi
 * pelanggarannya baru meledak di COMMIT dan seluruh simpanan ikut batal.
 *
 * Dua cabang, persis SQL-nya:
 *   • nota BER-baris produk → Σ per-order dari (Σ product_item − kredit DP
 *     order itu), tak pernah negatif per order;
 *   • nota TANPA baris produk (nota ongkir, nota "Ongkir — selisih", nota DP)
 *     → nilai tagihannya sendiri, yaitu Σ baris aktif non-diskon. Sebelum
 *     mig 5060 cabang ini bernilai 0 dan setiap koreksi ongkir ditolak.
 *
 * Baris dibatalkan tidak ikut, di kedua cabang.
 */
export function invoiceDiscountableBase(
  lines: ReadonlyArray<{
    kind: InvoiceLineKind;
    orderId?: string | null;
    amount: number;
    cancelledAt?: string | null;
  }>
): number {
  const active = lines.filter((l) => !l.cancelledAt);
  const amt = (l: { amount: number }) =>
    Number.isFinite(l.amount) ? l.amount : 0;

  if (!active.some((l) => l.kind === "product_item")) {
    return Math.max(
      0,
      active.reduce((s, l) => (l.kind === "discount" ? s : s + amt(l)), 0)
    );
  }

  const prod = new Map<string, number>();
  const pool = new Map<string, number>();
  for (const l of active) {
    const key = l.orderId ?? "";
    if (l.kind === "product_item") prod.set(key, (prod.get(key) ?? 0) + amt(l));
    else if (l.kind === "dp_credit")
      pool.set(key, (pool.get(key) ?? 0) + Math.abs(amt(l)));
  }
  let base = 0;
  for (const [key, sum] of prod) base += Math.max(0, sum - (pool.get(key) ?? 0));
  return base;
}

/** Total magnitudo diskon AKTIF (baris existing tak dibatalkan + baris baru). */
export function discountDraftTotal(
  rows: ReadonlyArray<DiscountDraftRow>
): number {
  return rows.reduce((s, r) => s + discountRowAmount(r), 0);
}

/**
 * Op baris diskon untuk RPC (cermin sub-set ApproveLineOp / EditOpenLineOp):
 *   • baris existing dibatalkan → {lineId, cancel}
 *   • baris existing nominal berubah → {lineId, amount} (magnitudo)
 *   • baris baru bernilai > 0 → {kind:'discount', description, amount}
 * Baris baru kosong/0 dilewati; existing tanpa perubahan dilewati.
 */
export type DiscountOp =
  | { lineId: string; cancel: true }
  | { lineId: string; amount: number }
  | { kind: "discount"; description: string; amount: number };

export function buildDiscountOps(
  rows: ReadonlyArray<DiscountDraftRow>
): DiscountOp[] {
  const ops: DiscountOp[] = [];
  for (const r of rows) {
    const mag = discountRowAmount({ amountRaw: r.amountRaw });
    if (r.lineId) {
      // Mengosongkan nominal = MEMBATALKAN barisnya. Dulu ini mengirim
      // {lineId, amount: 0}; server menyimpannya sebagai baris `discount`
      // bernilai 0 yang tetap AKTIF (CHECK-nya mengizinkan `amount <= 0`),
      // jadi nota pembeli tetap mencetak potongan "Rp 0" yang dikira admin
      // sudah dihapus. (bughunt B3-8)
      if (r.cancelled || mag === 0) ops.push({ lineId: r.lineId, cancel: true });
      else if (mag !== r.initialAmount) ops.push({ lineId: r.lineId, amount: mag });
    } else if (!r.cancelled && mag > 0) {
      ops.push({
        kind: "discount",
        description: r.description.trim() || DEFAULT_DISCOUNT_LABEL,
        amount: mag
      });
    }
  }
  return ops;
}

// ─── Alokasi pembayaran → per-pcs (CERMIN compute_invoice_allocation) ────────

export type AllocLineInput = {
  id: string;
  kind: InvoiceLineKind;
  /** Scope kredit DP + pengelompokan baris produk per order. */
  orderId?: string | null;
  orderItemId?: string | null;
  qty?: number | null;
  amount: number;
  createdAt: string;
  cancelledAt?: string | null;
};

export type LineAllocation = {
  creditAbsorbed: number;
  /** Porsi diskon se-nota (mig 4820) yang menempel baris ini; 0 selain produk. */
  discountAbsorbed: number;
  moneyAbsorbed: number;
  /** Pcs tertutup pembayaran — hanya untuk baris product_item; null lainnya. */
  coveredPcs: number | null;
};

/**
 * Peringkat urutan alokasi — SAMA dengan urutan tampilan rincian nota
 * (`sortInvoiceLinesForDisplay`): produk → ongkir → biaya lainnya. Sebelum
 * mig 4820 urutannya cuma `createdAt, id`, dan karena `invoice_lines.created_at`
 * default `now()` (waktu TRANSAKSI) semua baris satu nota lahir ber-nilai
 * identik — tie-break jatuh ke `gen_random_uuid()`, jadi nota yang sama dengan
 * pembayaran yang sama bisa menutup 2 pcs atau 1 pcs tergantung undian.
 */
function allocKindRank(kind: InvoiceLineKind): number {
  if (kind === "product_item") return 0;
  if (kind === "shipment_due") return 1;
  return 2;
}

/**
 * Alokasi deterministik 3-pass (urut peringkat kind → createdAt → id):
 *   PASS 1  — kredit DP (baris dp_credit) menutup baris product_item ORDER
 *             miliknya saja (tidak bocor ke order lain).
 *   PASS 1b — diskon se-nota (baris discount, mig 137) disebar PRO-RATA ke
 *             baris product_item saja — ongkir & biaya tetap ditagih penuh —
 *             proporsional terhadap SISA baris setelah kredit DP (mig 4820).
 *   PASS 2  — uang riil (settled efektif) FIFO lintas semua baris positif.
 *
 * coveredPcs = floor((credit+diskon+money) × qty / amount); baris amount 0
 * dianggap tertutup penuh; saat nota lunas penuh semua baris dipaksa penuh
 * (force-cover, menghapus residu pembulatan). Dua pengecualian:
 *   • mig 110 — nota tanpa tagihan riil (gross = Σ baris non-kredit ≤ 0, mis.
 *     semua "harga menyusul") tidak menutup baris apa pun;
 *   • mig 4820 — nota yang totalnya nol KARENA diskon juga tidak menutup apa
 *     pun: ia sengaja TIDAK auto-LUNAS, dan menunggu Tandai Lunas manual
 *     supaya ada jejak siapa yang menggratiskan.
 *
 * Pembagi PASS 1b sengaja "sisa setelah kredit DP", bukan `amount` mentah:
 * pembagi mentah butuh clamp per baris, dan clamp itu MEMBOCORKAN diskon saat
 * kredit DP tidak merata (produk Rp 100.000 + DP Rp 60.000 + ongkir Rp 50.000
 * + diskon Rp 50.000 → terserap cuma Rp 40.000, Σ kebutuhan ≠ total). Plafon
 * diskon ditegakkan server (`invoice_discountable_base` + constraint trigger).
 */
export function allocateInvoice(
  lines: ReadonlyArray<AllocLineInput>,
  settledEffective: number
): Map<string, LineAllocation> {
  const active = lines.filter((l) => !l.cancelledAt);
  const byCreated = (a: AllocLineInput, b: AllocLineInput) =>
    allocKindRank(a.kind) - allocKindRank(b.kind) ||
    a.createdAt.localeCompare(b.createdAt) ||
    a.id.localeCompare(b.id);

  const totalDue = Math.max(
    0,
    active.reduce((s, l) => s + (Number.isFinite(l.amount) ? l.amount : 0), 0)
  );
  const settled = Math.max(0, settledEffective);
  const fully = settled >= totalDue;
  // Tagihan riil = Σ baris non-kredit (dp_credit/discount). gross ≤ 0 → nota
  // kosong / harga menyusul / hanya diskon: tak ada yang tertutup (cermin
  // compute_invoice_allocation mig 110/137).
  const gross = active.reduce(
    (s, l) =>
      !isCreditLine(l.kind) && Number.isFinite(l.amount) ? s + l.amount : s,
    0
  );
  const hasCharge = gross > 0;
  // Magnitudo diskon se-nota. Nota yang totalnya jatuh ke 0 KARENA diskon tidak
  // menutup pcs apa pun — ia menunggu Tandai Lunas manual (mig 4820).
  const discountPool = active.reduce(
    (s, l) =>
      l.kind === "discount" && Number.isFinite(l.amount) ? s - l.amount : s,
    0
  );
  const zeroByDiscount = totalDue <= 0 && discountPool > 0;

  // PASS 1 — kredit DP per order.
  const pools = new Map<string, number>();
  for (const l of active) {
    if (l.kind === "dp_credit" && l.orderId) {
      pools.set(l.orderId, (pools.get(l.orderId) ?? 0) + Math.abs(l.amount));
    }
  }
  const credit = new Map<string, number>();
  for (const l of active.filter((x) => x.kind === "product_item").sort(byCreated)) {
    const pool = l.orderId ? pools.get(l.orderId) ?? 0 : 0;
    const take = Math.min(Math.max(0, l.amount), Math.max(0, pool));
    credit.set(l.id, take);
    if (l.orderId) pools.set(l.orderId, pool - take);
  }

  // PASS 1b — diskon se-nota pro-rata ke baris produk, atas SISA setelah
  // kredit DP. Pembagi = Σ sisa, jadi share ≤ sisa selalu dan tak ada diskon
  // yang bocor. `Math.min` hanya menggigit bila plafon server dilanggar data lama.
  const productLines = active.filter((x) => x.kind === "product_item");
  const remTotal = productLines.reduce(
    (s, l) => s + Math.max(0, l.amount - (credit.get(l.id) ?? 0)),
    0
  );
  const discount = new Map<string, number>();
  for (const l of productLines) {
    const rem = Math.max(0, l.amount - (credit.get(l.id) ?? 0));
    discount.set(
      l.id,
      remTotal > 0 ? Math.min(rem, (discountPool * rem) / remTotal) : 0
    );
  }

  // PASS 2 — uang riil FIFO lintas baris positif (kredit DP & diskon
  // dikecualikan: mengurangi total tapi tak menyerap uang).
  const result = new Map<string, LineAllocation>();
  let pool = settled;
  for (const l of active.filter((x) => !isCreditLine(x.kind)).sort(byCreated)) {
    const c = credit.get(l.id) ?? 0;
    const d = discount.get(l.id) ?? 0;
    const need = Math.max(0, l.amount - c - d);
    const money = Math.min(need, Math.max(0, pool));
    pool -= money;

    let coveredPcs: number | null = null;
    if (l.kind === "product_item" && l.orderItemId && l.qty != null && l.qty > 0) {
      // Pembagi = harga SETELAH diskon: baris berutang (amount − diskon), dan
      // yang membayarnya adalah kredit DP + uang. Memasukkan diskon ke
      // PEMBILANG dengan pembagi mentah akan over-cover (nota Rp 800.000 /
      // 10 pcs, bayar Rp 400.000 → 6 pcs, bukan 5).
      const due = l.amount - d;
      coveredPcs = !hasCharge
        ? 0
        : zeroByDiscount
          ? 0
          : l.amount <= 0
            ? l.qty
            : fully
              ? l.qty
              : due <= 0
                ? l.qty
                : Math.floor(((c + money) * l.qty) / due);
    }

    result.set(l.id, {
      creditAbsorbed: c,
      discountAbsorbed: d,
      moneyAbsorbed: money,
      coveredPcs
    });
  }
  return result;
}

// ─── Refund saat item dibatalkan / gagal produksi (CERMIN cancel_order_items_internal) ─
// Mig 074: saat item PO gagal produksi atau order dibatalkan, uang yang sudah
// menempel pcs itu di-REFUND OTOMATIS ke SALDO pelanggan (balance_transactions
// type='refund', append-only). balance_applied/amount_paid TIDAK ditulis-mundur;
// refund diakumulasi di invoices.refunded_amount. Fungsi di bawah adalah cermin
// TS dari aritmetika refund PER-NOTA di cancel_order_items_internal — berguna
// untuk pratinjau "akan direfund Rp X" sebelum admin mengonfirmasi pembatalan.

export type CancellationRefund = {
  /** Uang yang dikembalikan ke saldo pelanggan dari nota ini (dibulatkan, ≥ 0). */
  refund: number;
  /**
   * Nota akan di-VOID — tak ada lagi baris aktif non-kredit (product_item/
   * shipment_due/manual) yang tersisa setelah pembatalan.
   */
  willVoid: boolean;
};

/**
 * Refund sebuah nota saat sekumpulan barisnya dibatalkan (gagal produksi /
 * cancel) — CERMIN TS `cancel_order_items_internal` (mig 074).
 *
 * Aturan dari SQL:
 *   1. refund = Σ `moneyAbsorbed` baris yang dibatalkan — uang riil yang sudah
 *      terserap pcs itu, dihitung dari alokasi deterministik SEBELUM pembatalan.
 *   2. Bila SETELAH pembatalan tak ada lagi baris aktif non-kredit ("nota tak
 *      bersisa") → refund = SELURUH settled efektif (menyapu residu pembulatan /
 *      overpay) dan nota di-void.
 *   3. Selain itu refund = min(refund, settled) — refund tak pernah melebihi uang
 *      yang benar-benar masuk.
 *
 * Untuk NOTA DP (`kind='dp'`) yang order-nya batal total, seluruh barisnya ikut
 * dibatalkan → cabang (2) berlaku → DP yang sudah dibayar di-refund penuh.
 *
 * @param lines            semua baris nota (baris cancelled lama diabaikan alokasi)
 * @param settledEffective uang efektif di nota = computeSettledEffective(...)
 * @param cancelledLineIds id baris yang DIBATALKAN operasi ini
 */
export function computeCancellationRefund(
  lines: ReadonlyArray<AllocLineInput>,
  settledEffective: number,
  cancelledLineIds: ReadonlySet<string>
): CancellationRefund {
  const settled = Math.max(0, settledEffective);
  const alloc = allocateInvoice(lines, settled);

  let refund = 0;
  for (const id of cancelledLineIds) {
    refund += alloc.get(id)?.moneyAbsorbed ?? 0;
  }

  // Masih ada baris aktif non-kredit yang TIDAK ikut dibatalkan?
  const hasActiveCharge = lines.some(
    (l) =>
      !l.cancelledAt && !cancelledLineIds.has(l.id) && !isCreditLine(l.kind)
  );
  const willVoid = !hasActiveCharge;

  const finalRefund = willVoid ? settled : Math.min(refund, settled);
  return { refund: Math.round(Math.max(0, finalRefund)), willVoid };
}

// ─── Status pengiriman per nota (detail tagihan pembeli & admin) ─────────────
// Baris nota product_item menunjuk order_item ASAL; porsi yang dikirim dipecah
// jadi baris order_items BARU ber-`source_item_id` = baris asal (idiom pecah-
// baris mig 073). Karena itu "pengiriman terkait nota" = shipment dari famili
// baris (asal + pecahannya). Helper murni ini mengelompokkan famili tersebut
// jadi ringkasan per-shipment + sisa pcs yang belum masuk pengiriman.

export type InvoiceShipmentRow = {
  orderItemId: string;
  /**
   * Induk pecah-baris (`order_items.source_item_id`); null = baris ini sendiri
   * akarnya. Dipakai mengelompokkan famili ke akar yang DITUNJUK baris nota,
   * supaya jatah "belum masuk paket" dijepit per akar dan pcs yang sudah
   * berangkat lewat anak tidak dihitung dua kali terhadap induknya.
   */
  sourceItemId?: string | null;
  qty: number;
  /**
   * Pcs LUNAS baris ini (`order_items.paid_qty`). Hanya dibaca jatah "Kirim
   * Barang" (`invoiceShipReady`) — server menolak kirim melebihi angka ini.
   * null/absen = tak diketahui (payload nota publik).
   */
  paidQty?: number | null;
  keepStatus: string | null;
  /**
   * `order_items.ready_at` (mig 7040/7760) — null pada `requested_shipment` =
   * paket menunggu barang. undefined = payload tak membawanya (nota publik).
   */
  readyAt?: string | null;
  productName: string;
  variantName: string;
  /** Berat SATUAN varian (gram) — bahan hitung otomatis berat paket (mig 221). */
  weightGram?: number | null;
  shipment: {
    id: string;
    /** Kode nomor kustom pengiriman (Penomoran Dokumen, mig 198) — null = fallback #ID. */
    shipmentCode?: string | null;
    status: string;
    paymentStatus: string;
    resiNumber: string | null;
    expedition: string | null;
    /** Berat total paket (gram) yang diisi admin; null = belum diisi (mig 221). */
    weightGram?: number | null;
    /**
     * Penanda "Siap Kirim" (mig 4430) — status tetap 'paid'. Hanya dibaca
     * label sisi admin (`shipmentStatusMeta` audience admin, uji nyata S01 T3).
     */
    readyToShipAt?: string | null;
    /**
     * Tujuan paket (uji nyata S32 T4): sesudah Kirim Sebelum Lunas, kartu
     * alamat Tagihan + Kirim berpindah ke paket, jadi tagihan menyebutnya di
     * sini. Absen di payload nota publik → undefined (barisnya tak tampil).
     */
    recipientName?: string | null;
    district?: string | null;
    city?: string | null;
    isDropship?: boolean;
  } | null;
};

export type InvoiceShipmentItem = {
  productName: string;
  variantName: string;
  qty: number;
};

/** Tujuan satu paket di kartu Status Pengiriman. */
export type InvoiceShipmentDestination = {
  recipientName: string | null;
  district: string | null;
  city: string | null;
  isDropship: boolean;
};

/**
 * Tujuan dari baris paket; null bila tak ada satu pun yang bisa disebut
 * (payload nota publik tak membawanya).
 */
export function invoiceShipmentDestination(
  shipment: InvoiceShipmentRow["shipment"]
): InvoiceShipmentDestination | null {
  if (!shipment) return null;
  const clean = (v: string | null | undefined) => {
    const t = (v ?? "").trim();
    return t ? t : null;
  };
  const recipientName = clean(shipment.recipientName);
  const district = clean(shipment.district);
  const city = clean(shipment.city);
  if (!recipientName && !district && !city) return null;
  return { recipientName, district, city, isDropship: Boolean(shipment.isDropship) };
}

/**
 * Satu baris tujuan: "Mama Rania · Sagulung, Batam". Kecamatan yang sama
 * dengan kotanya tak diulang. Tanpa kata ganti: kartu ini dibaca admin dan
 * pembeli.
 */
export function shipmentDestinationText(d: InvoiceShipmentDestination | null | undefined): string {
  if (!d) return "";
  const region = [d.district, d.city]
    .filter((x): x is string => Boolean(x))
    .filter((x, i, arr) => arr.findIndex((y) => y.toLowerCase() === x.toLowerCase()) === i)
    .join(", ");
  return [d.recipientName, region].filter(Boolean).join(" · ");
}

export type InvoiceShipmentGroup = {
  id: string;
  /** Kode nomor kustom pengiriman (Penomoran Dokumen, mig 198) — null = fallback #ID. */
  shipmentCode: string | null;
  status: string;
  paymentStatus: string;
  /** Penanda "Siap Kirim" (mig 4430); absen = tak dimuat. */
  readyToShipAt?: string | null;
  resiNumber: string | null;
  expedition: string | null;
  /** Tujuan paket; null = tak dimuat / tak diketahui. */
  destination?: InvoiceShipmentDestination | null;
  /** Pcs NOTA INI di paket ini — bukan isi paket seluruhnya. */
  totalPcs: number;
  /**
   * Pcs SELURUH isi paket (Σ `packageItems`), pasangan `weight`. Paket bisa
   * memuat barang dari nota lain, jadi berat paket di samping `totalPcs`
   * terbaca "860 g · 3 pcs" untuk paket 5 pcs — selisih yang dilaporkan audit
   * berat 2026-10-22. `null`/absen = isi paket penuh tak dimuat.
   */
  packagePcs?: number | null;
  items: InvoiceShipmentItem[];
  /**
   * Berat paket siap tampil (mig 221). Sumbernya `stored` = berat yang diisi
   * admin, `auto` = hitungan dari berat produk (HANYA saat baris yang dipakai
   * memuat SELURUH isi paket — lihat `packageItems`), `unknown` = tak
   * diketahui.
   */
  weight: ResolvedShipmentWeight;
};

export type InvoiceShipmentSummary = {
  /** Pengiriman aktif yang memuat item nota ini (urut kemunculan baris). */
  shipments: InvoiceShipmentGroup[];
  /**
   * Item yang belum masuk pengiriman mana pun (digabung per varian). Dengan
   * `paidQtyByItem`, isinya HANYA pcs yang sudah DIBAYAR — pcs belum dibayar
   * menunggu pembeli, bukan toko, jadi ia bukan tunggakan kirim.
   */
  unshipped: InvoiceShipmentItem[];
  unshippedPcs: number;
};

/**
 * Peta `order_item_id baris nota → pcs LUNAS baris itu` — penjepit
 * `buildInvoiceShipmentGroups`. SATU definisi untuk ketiga pembacanya (admin,
 * pembeli, PDF nota): kalau masing-masing membangunnya sendiri, satu di antara
 * mereka pasti lupa menyaring baris yang dibatalkan lalu memajang angka yang
 * berbeda untuk nota yang sama.
 *
 * Bentuknya `Record` supaya bisa langsung jadi prop komponen; pemanggil helper
 * mengubahnya ke `Map`.
 */
export function invoicePaidQtyByItem(
  lines: ReadonlyArray<{
    kind: string;
    cancelledAt?: string | null;
    orderItemId?: string | null;
    paidQty?: number | null;
  }>
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of lines) {
    if (l.kind !== "product_item" || l.cancelledAt || !l.orderItemId) continue;
    // Satu nota bisa punya DUA baris atas order_item yang sama (pecah-baris
    // qty sebagian), jadi jatahnya dijumlah, bukan ditimpa.
    out[l.orderItemId] = (out[l.orderItemId] ?? 0) + Math.max(0, l.paidQty ?? 0);
  }
  return out;
}

function pushMergedItem(
  list: InvoiceShipmentItem[],
  row: { productName: string; variantName: string; qty: number }
) {
  const found = list.find(
    (i) =>
      i.productName === row.productName && i.variantName === row.variantName
  );
  if (found) found.qty += row.qty;
  else
    list.push({
      productName: row.productName,
      variantName: row.variantName,
      qty: row.qty
    });
}

/**
 * Paket AKTIF sebuah baris famili. Paket `cancelled` diperlakukan seperti tak
 * ber-paket (defensif — mig 117/125 sudah men-NULL-kan `shipment_request_id`
 * saat batal). Dipakai bersama kartu Status Pengiriman & `invoiceShipReady`.
 */
export function activeShipmentOf(
  row: InvoiceShipmentRow
): InvoiceShipmentRow["shipment"] {
  return row.shipment && row.shipment.status !== "cancelled"
    ? row.shipment
    : null;
}

/** Status paket yang isinya masih boleh berubah (belum dikirim). */
const EDITABLE_PACKAGE_STATUSES = new Set(["draft", "waiting_payment", "paid"]);

/**
 * Paket yang ikut berubah saat baris barang dibatalkan/ditambah lewat Edit
 * Tagihan (migrasi tagihan_kirim_tetap_utuh, `admin_edit_open_invoice`):
 * TEPAT SATU paket hidup yang dirujuk niat kirim tagihan ini dan belum
 * dikirim. Hanya bahan kalimat di editor — keputusan tetap di server.
 */
export function editFollowPackage(
  rows: ReadonlyArray<InvoiceShipmentRow>,
  intentShipmentIds: ReadonlyArray<string> | undefined
): { id: string; shipmentCode: string | null } | null {
  if (!intentShipmentIds || intentShipmentIds.length === 0) return null;
  const linked = new Set(intentShipmentIds);
  const live = new Map<string, NonNullable<InvoiceShipmentRow["shipment"]>>();
  for (const r of rows) {
    const sh = activeShipmentOf(r);
    if (sh && linked.has(sh.id)) live.set(sh.id, sh);
  }
  if (live.size !== 1) return null;
  const [sh] = [...live.values()];
  if (!EDITABLE_PACKAGE_STATUSES.has(sh.status)) return null;
  return { id: sh.id, shipmentCode: sh.shipmentCode ?? null };
}

/**
 * Akar famili sebuah baris = id yang DITAGIH baris nota (kunci peta jatah),
 * bukan selalu induk pecah-baris — lihat komentar di `buildInvoiceShipmentGroups`.
 */
export function invoiceShipmentRootOf(
  billedByItem: ReadonlyMap<string, number> | undefined
): (row: InvoiceShipmentRow) => string {
  return (row) =>
    billedByItem?.has(row.orderItemId)
      ? row.orderItemId
      : row.sourceItemId ?? row.orderItemId;
}

/** Jatah = pcs nota akar ini − pcs akar yang sudah masuk paket, lantai 0. */
export function unshippedBudget(
  billed: number | undefined,
  packaged: number | undefined
): number {
  return Math.max(0, (billed ?? 0) - (packaged ?? 0));
}

/**
 * Kelompokkan famili baris order_items sebuah nota menjadi ringkasan
 * pengiriman. Baris ber-shipment aktif → grup per shipment; baris cancelled
 * di-skip; sisanya = "belum dikirim". Shipment `cancelled` diperlakukan
 * seperti tak ber-shipment (defensif — mig 117/125 sudah men-NULL-kan
 * `shipment_request_id` saat batal).
 *
 * `packageItems` = peta `shipmentId → SELURUH isi paket itu`, dan ia
 * SATU-SATUNYA jalan mendapat berat hitungan. Bentuk itu dipilih supaya
 * kekeliruan yang berbahaya jadi sulit ditulis: `rows` sendiri TIDAK boleh
 * dipakai menghitung berat, karena baris yang dipegang pemanggil biasanya
 * hanya milik SATU NOTA sedangkan sebuah paket boleh memuat barang dari nota
 * LAIN (PROD 2026-08: 12 dari 149 paket berisi >1 nota barang) — jumlahnya
 * akan TERLALU RINGAN persis di halaman yang menagihkan ongkirnya. Pemanggil
 * yang ingin berat otomatis harus lebih dulu benar-benar mengambil isi paket
 * penuh; tanpa peta itu, yang tampil hanya berat TERSIMPAN.
 *
 * `paidQtyByItem` = peta `order_item_id baris nota → pcs LUNAS baris itu`. Ia
 * menjepit hitungan "belum masuk pengiriman" dari DUA sisi sekaligus:
 *
 *   1. barang yang nyata masih ada — baris `cancelled` tak pernah dijatah;
 *   2. pcs yang sudah dibayar dikurangi pcs akar yang sudah masuk paket.
 *
 * Kedua sisi wajib, dan itu diukur bukan dikira: simulasi atas 1.297 nota
 * produk hidup di PROD (2026-09-03) menunjukkan sisi 2 SENDIRIAN memunculkan
 * kotak baru di 37 nota — item yang sudah dibatalkan tetapi baris notanya
 * masih tercatat lunas — sementara sisi 1 sendirian membiarkan induk
 * pecah-baris dihitung ulang setelah pcs-nya berangkat lewat anaknya (11 nota,
 * kelas dobel-hitung yang sama dengan mig 4870). Berdua: 204 nota → 84, nol
 * kotak palsu baru.
 *
 * Tanpa peta, keluarannya IDENTIK dengan perilaku sebelum jepitan ini ada —
 * jalur nota ONGKIR memakai bentuk itu, karena barisnya sudah seluruh isi
 * paket dan tak punya baris nota untuk dijadikan penjepit.
 */
export function buildInvoiceShipmentGroups(
  rows: ReadonlyArray<InvoiceShipmentRow>,
  opts?: {
    packageItems?: ReadonlyMap<string, ReadonlyArray<WeightSourceItem>>;
    paidQtyByItem?: ReadonlyMap<string, number>;
  }
): InvoiceShipmentSummary {
  const packageItems = opts?.packageItems;
  const paidQtyByItem = opts?.paidQtyByItem;
  const storedWeight = new Map<string, number | null | undefined>();
  const shipments: InvoiceShipmentGroup[] = [];
  const byId = new Map<string, InvoiceShipmentGroup>();
  const unshipped: InvoiceShipmentItem[] = [];
  let unshippedPcs = 0;
  // Baris tanpa paket aktif dikumpulkan dulu (URUT BARIS), karena jatahnya
  // tak bisa dihitung sebelum seluruh baris terbaca: yang menentukan adalah
  // berapa pcs akar itu sudah masuk paket.
  const pending: InvoiceShipmentRow[] = [];
  const packagedByRoot = new Map<string, number>();
  // Akar = id yang DITAGIH baris nota, bukan selalu induk famili. Baris nota
  // boleh menunjuk ANAK pecah-baris langsung (5 baris di 2 nota PROD per
  // 2026-09-03); memaksanya ke induk yang tak ditagih nota ini akan memberi
  // jatah 0 dan MENYEMBUNYIKAN pcs lunas — fail-closed ke arah yang salah.
  const rootOf = invoiceShipmentRootOf(paidQtyByItem);

  for (const row of rows) {
    if (row.qty <= 0) continue;
    const activeShipment = activeShipmentOf(row);

    if (activeShipment) {
      let group = byId.get(activeShipment.id);
      if (!group) {
        group = {
          id: activeShipment.id,
          shipmentCode: activeShipment.shipmentCode ?? null,
          status: activeShipment.status,
          paymentStatus: activeShipment.paymentStatus,
          readyToShipAt: activeShipment.readyToShipAt ?? null,
          destination: invoiceShipmentDestination(activeShipment),
          resiNumber: activeShipment.resiNumber,
          expedition: activeShipment.expedition,
          totalPcs: 0,
          items: [],
          // Diisi setelah semua baris terkumpul — hitungan otomatis menuntut
          // seluruh isi paket, bukan baris pertama saja.
          weight: { gram: null, source: "unknown" }
        };
        byId.set(activeShipment.id, group);
        shipments.push(group);
        storedWeight.set(activeShipment.id, activeShipment.weightGram);
      }
      group.totalPcs += row.qty;
      pushMergedItem(group.items, row);
      const shippedRoot = rootOf(row);
      packagedByRoot.set(
        shippedRoot,
        (packagedByRoot.get(shippedRoot) ?? 0) + row.qty
      );
      continue;
    }

    // Baris batal tanpa shipment aktif (gagal produksi / cancel / hangus)
    // bukan "menunggu dikirim" — jangan dihitung. Ini SISI PERTAMA jepitan:
    // barang batal tak pernah masuk `pending`, jadi tak bisa dijatah.
    if (row.keepStatus === "cancelled") continue;

    pending.push(row);
  }

  // ── Jatah "belum masuk paket" per AKAR famili ────────────────────────────
  // TANPA `paidQtyByItem` jatahnya tak terbatas, sehingga keluarannya identik
  // dengan perilaku sebelum jepitan ini ada — itulah jalur nota ONGKIR, yang
  // barisnya sudah seluruh isi paket dan tak punya baris nota untuk dijadikan
  // penjepit.
  //
  // DENGAN peta: jatah = pcs LUNAS baris nota ini dikurangi pcs akar yang
  // sudah masuk paket. Pcs yang belum dibayar menunggu PEMBELI, bukan toko,
  // jadi ia bukan "belum masuk pengiriman" — dan induk yang pcs-nya sudah
  // berangkat lewat anak pecah-baris tidak dihitung ulang terhadap dirinya.
  const budgetByRoot = new Map<string, number>();
  for (const row of pending) {
    const root = rootOf(row);
    if (budgetByRoot.has(root)) continue;
    budgetByRoot.set(
      root,
      paidQtyByItem
        ? unshippedBudget(paidQtyByItem.get(root), packagedByRoot.get(root))
        : Number.POSITIVE_INFINITY
    );
  }

  for (const row of pending) {
    const root = rootOf(row);
    const budget = budgetByRoot.get(root) ?? 0;
    if (budget <= 0) continue;
    // SISI KEDUA jepitan. `take` tak pernah melebihi qty baris, sehingga
    // total per akar juga tak pernah melebihi barang yang nyata masih ada.
    const take = Math.min(row.qty, budget);
    budgetByRoot.set(root, budget - take);
    unshippedPcs += take;
    pushMergedItem(unshipped, {
      productName: row.productName,
      variantName: row.variantName,
      qty: take
    });
  }

  for (const group of shipments) {
    const pkg = packageItems?.get(group.id);
    group.weight = resolveShipmentWeight(storedWeight.get(group.id), pkg ?? []);
    if (pkg?.length) {
      group.packagePcs = pkg.reduce(
        (n, it) => n + Math.max(0, Math.trunc(Number(it.qty) || 0)),
        0
      );
    }
  }

  return { shipments, unshipped, unshippedPcs };
}
